// THE PROCEDURAL MATERIAL LIBRARY — one generator per substance, each baking a 1024x1024 tile.
//
// Split from TextureEngine.js, which is the CACHE and the DISPATCH: it decides which slot a kube wants and hands the
// baked canvas to a material. This file is the art. Keeping them apart stops a 200KB library from burying a 200-line
// cache, and lets the library be read one material at a time.
//
// EVERY generator in here obeys the same four rules, and the reason each exists is in the file it came from:
//   * 1024x1024 canvas, RepeatWrapping, and it MUST TILE — a feature crossing an edge is drawn again at every wrapped
//     offset that can reach the canvas (drawWrapped / drawWrappedBand / the per-generator wrapAt). A seam grid across
//     a voxel world's surface is the single most likely way this work fails, and doc/kube-textures.html renders every
//     tile at 2x2 repeats precisely so a seam shows as a cross through the middle of the square.
//   * DETERMINISTIC: jitter, shade and phase all come from hash01() seeded off hexSeed(), never Math.random(). Two
//     players standing on the same kube have to see the same surface.
//   * NEUTRAL unless it owns its cache slot. applyMaterial does BOTH material.color.set(hex) AND material.map = tex,
//     so the canvas is MULTIPLIED by the kube's hex on the GPU. A generator serving many kubes from one slot (the 32
//     glass tints, the 11 gases) is therefore built on white and greys and supplies only light and dark; one with its
//     own slot per kube may bake the hex in. Get this backwards and a dark kube comes out as near-black mud.
//   * Cheap: path and gradient drawing, not per-pixel loops. Each canvas is baked once and cached forever, but a
//     1024-square per-pixel loop in JS is enough to drop a frame on the bake.
import * as THREE from '/lib/three/three.module.min.js';   // REAL path, not the bare specifier — must resolve on Safari 15.6
import { blendColors, lightenColor, darkenColor, hash01, hexSeed } from './texture-helpers.js';

// Draw one feature at every wrapped position it can reach across the tile edge. A texture repeats, so a via that
// straddles x = 1024 has to reappear at x = 0 or the seam shows as a row of half-eaten dots; passing the feature's
// bounding RADIUS lets this skip the eight extra positions for the (overwhelmingly common) interior case. Used by
// every generator below that places a feature at a hashed position rather than on a lattice.
export function drawWrapped(size, x, y, reach, draw) {
    // A feature whose reach exceeds half a tile could land BOTH its -1 and +1 copy on the canvas at once and be
    // composited twice, at double density. Clamping the test only makes this conservative (it may skip a copy that
    // could not have shown anyway) rather than wrong, and it turns a silent density artefact into a no-op.
    const r = reach > size / 2 ? size / 2 : reach;
    if (x > r && x < size - r && y > r && y < size - r) { draw(x, y); return; }   // interior fast path — almost every feature
    for (let ox = -size; ox <= size; ox += size) {
        for (let oy = -size; oy <= size; oy += size) {
            const px = x + ox, py = y + oy;
            if (px + r < 0 || px - r > size || py + r < 0 || py - r > size) continue;
            draw(px, py);
        }
    }
}


// ── FROZEN VOLATILES ─────────────────────────────────────────────────────────────────────────────────────────────
// The six ices that make a comet nucleus. These are not distant crust: pickIce draws them for every comet, and the
// orbit-mining flyer puts the player's face against them at arm's length, so each one has to survive being looked at
// closely and has to be TELLABLE APART from the ice beside it — a comet's crust is two of these six, side by side.
//
// COLOUR. Every generator here is NEUTRAL: built out of white and grey, painting only light and shade, because
// applyMaterial multiplies the canvas by the kube's own hex on the GPU. The five true ices are near-white to begin
// with (#E0F7FA … #F0FFFF), so a canvas that baked those in would come out hex-squared — a dirty grey-blue instead
// of ice — and the hue that separates Methane's blue from Ammonia's green would be halved along with it. The hex
// supplies ALL of the colour; the canvas supplies all of the form. Hydrocarbon Ice follows the same rule with a warm
// bias rather than pure grey (see its note): still multiplicative, still never baking baseHex.
//
// Each ice still gets its OWN cache slot. Not for tint — a neutral canvas needs none — but because baseHex is the
// hexSeed here, so one slot per kube is what gives Water Ice and Nitrogen Ice different fracture layouts instead of
// the same canvas twice.
//
// CONTRAST. The catalogue marks all six transparent (opacity 0.8–0.9), but the voxel path draws them OPAQUE — the
// see-through rule is isGlassKubeName and no ice is in that family. So "you are looking INTO this, not at it" cannot
// come from alpha; it has to be drawn. That is what the sub-surface pools, the bubble trains and the internal
// fracture planes are for. It also means these can carry real contrast: they are lit blocks, not tinted panes.
//
// LIGHT COMES FROM THE UPPER LEFT in all six, the same convention createCraftSurfaceTexture uses, so a nucleus built
// from two of them still agrees with itself about where the sun is.
//
// SEAMS. Two rules, both stricter than the pinned-border trick in createCrystallineLattice (which works, but also
// flattens the border row into a straight line — invisible on a 64px facet mesh, fatal on a 250px fracture):
//   1. iceTilePhases() draws a feature once per tile phase whose bounding box still overlaps the canvas. An interior
//      feature draws exactly once, an edge feature twice, a corner feature four times, and every copy lands exactly
//      where the neighbouring tile will put it. Nothing is ever clipped in half at an edge.
//   2. MODULAR jitter for lattices — point (i, j) is offset by hash01(i mod n, j mod n), so the point at i = n IS the
//      point at i = 0 one tile over. The lattice is genuinely periodic rather than pinned, so its border cells are as
//      irregular as its interior ones.
// Lane and streak fields use whole-cycle sines, the same way createDamascusPattern does.
//
// Determinism is hash01/hexSeed throughout — two players mining the same comet see the same cracks.

// Phase walk for one feature. ±2 rather than ±1 so a feature up to twice the tile across is still covered on every
// side; the bounding-box test rejects the other 24 phases for anything in the middle of the canvas, which is nearly
// all of them, so the common case costs one draw and a handful of compares.
export function iceTilePhases(size, x0, y0, x1, y1, draw) {
    for (let ox = -2; ox <= 2; ox++) {
        const dx = ox * size;
        if (x1 + dx < 0 || x0 + dx > size) continue;
        for (let oy = -2; oy <= 2; oy++) {
            const dy = oy * size;
            if (y1 + dy < 0 || y0 + dy > size) continue;
            draw(dx, dy);
        }
    }
}

const iceRGBA = (r, g, b, a) => 'rgba(' + Math.round(r) + ',' + Math.round(g) + ',' + Math.round(b) + ',' + a + ')';
const iceShade = (v, a) => iceRGBA(v, v, v, a);   // the neutral workhorse: grey level v at alpha a, hue left to material.color

// A closed, SMOOTH, lobed outline — a sublimation pit, a tar patch, an exposure. The hashed points are used as
// quadratic control points and the curve runs through their midpoints, so the result is an organic blob rather than
// the polygon a lineTo chain gives (which reads as a drawn shape the moment it is bigger than about 30px).
export function iceBlobPath(ctx, seed, id, cx, cy, r, jitter, lobes, dx, dy) {
    const px = [], py = [];
    for (let k = 0; k < lobes; k++) {
        const th = (k / lobes) * Math.PI * 2;
        const rr = r * (1 - jitter + hash01(id * 31 + k + seed, id * 7 + k) * jitter * 2);
        px.push(cx + Math.cos(th) * rr + dx);
        py.push(cy + Math.sin(th) * rr + dy);
    }
    const mx = (a, b) => (px[a] + px[b]) * 0.5, my = (a, b) => (py[a] + py[b]) * 0.5;
    ctx.beginPath();
    ctx.moveTo(mx(lobes - 1, 0), my(lobes - 1, 0));
    for (let k = 0; k < lobes; k++) ctx.quadraticCurveTo(px[k], py[k], mx(k, (k + 1) % lobes), my(k, (k + 1) % lobes));
    ctx.closePath();
}

// Frost grain — the fine sparkle every one of these ices carries. Batched on purpose: a whole shade bucket goes into
// ONE path and ONE fill, because setting fillStyle per speck costs several times what drawing the speck does and
// these fields run to thousands. Grains are a few px across, so wrapping is a straight two-way shift rather than a
// phase walk. 'buckets' is an array of colour strings; each gets 'count' grains from its own hash stream.
export function iceGrainField(ctx, size, seed, salt, count, buckets, maxSize) {
    for (let b = 0; b < buckets.length; b++) {
        ctx.beginPath();
        for (let i = 0; i < count; i++) {
            const gi = salt + b * 7919 + i;
            const gx = hash01(gi + seed, 1) * size, gy = hash01(gi, seed + 2) * size;
            const w = 0.8 + hash01(gi + seed, 3) * maxSize, h = 0.8 + hash01(gi, seed + 4) * maxSize;
            ctx.rect(gx, gy, w, h);
            const wx = gx + w > size, wy = gy + h > size;
            if (wx) ctx.rect(gx - size, gy, w, h);
            if (wy) ctx.rect(gx, gy - size, w, h);
            if (wx && wy) ctx.rect(gx - size, gy - size, w, h);
        }
        ctx.fillStyle = buckets[b];
        ctx.fill();
    }
}

// Broad, very soft pools of light and shade. Under an ice this is the sub-surface — the depth you are seeing into;
// over one it is macro relief. Either way it is what stops a tile of repeated detail reading as wallpaper.
export function icePools(ctx, size, seed, salt, count, rMin, rMax, lightV, lightA, darkV, darkA) {
    for (let i = 0; i < count; i++) {
        const cx = hash01(i + salt + seed, 11) * size, cy = hash01(i + salt, seed + 12) * size;
        const r = rMin + hash01(i + salt + seed, 13) * (rMax - rMin);
        const dark = hash01(i + salt, seed + 14) > 0.48;
        iceTilePhases(size, cx - r, cy - r, cx + r, cy + r, (dx, dy) => {
            const g = ctx.createRadialGradient(cx + dx - r * 0.15, cy + dy - r * 0.15, 0, cx + dx, cy + dy, r);
            g.addColorStop(0, iceShade(dark ? darkV : lightV, dark ? darkA : lightA));
            g.addColorStop(1, iceShade(dark ? darkV : lightV, 0));
            ctx.fillStyle = g;
            ctx.fillRect(cx + dx - r, cy + dy - r, r * 2, r * 2);
        });
    }
}


// ── GLASS ────────────────────────────────────────────────────────────────────────────────────────────────────────
// Two materials that share a word and nothing else. The 32 MELT PANES ("<X> Glass", the induction-smelter melt of
// "<X> Regolith") are transparent, single-sided and seam-culled into one slab; REINFORCED GLASS is the structural
// product the machines are built from — opaque, shadow-casting, and deliberately outside that family (isGlassKubeName,
// system-engine.js). One look each.
//
// A full-width band whose height is two WHOLE-CYCLE sines of x, so it leaves the right edge at exactly the height and
// slope it entered the left. It is the only shape either glass generator draws, and it is the whole tiling argument:
//   * The polyline is sampled from -OVER to size+OVER, so both line CAPS land off-tile and the tile's left and right
//     edge columns are mid-stroke. A cap sitting ON the seam would butt against its neighbour's cap and lay down a
//     double-alpha stripe — i.e. paint the one thing a glass texture must never paint, a grid at the kube pitch.
//   * It is drawn at y0, y0-size and y0+size, so whatever the top edge clips reappears identically at the bottom.
//     That is the V half of the wrap, and it is what lets y0 be anywhere at all: no band position is forbidden.
//   * Three narrowing strokes fake a soft-edged profile. Canvas2D's own 'filter' (a real blur) is not available on the
//     oldest browser this project still supports, and a single stroke never double-blends its own overlaps, so the
//     three passes are exactly the falloff — a hard-edged band at this contrast reads as a scratch, a soft one as ream.
export function drawWrappedBand(ctx, size, y0, amp1, amp2, cycles, phase, width, colour, alpha) {
    const OVER = 64, reach = width + amp1 + amp2 + OVER;
    for (let pass = 0; pass < 3; pass++) {
        ctx.lineWidth = Math.max(0.5, width * (1 - pass * 0.34));
        ctx.strokeStyle = colour;
        ctx.globalAlpha = alpha;
        for (let rep = -1; rep <= 1; rep++) {
            const yy = y0 + rep * size;
            if (yy < -reach || yy > size + reach) continue;
            ctx.beginPath();
            for (let x = -OVER; x <= size + OVER; x += 12) {   // 12, not 32: a thin cord at 3 cycles shows its polyline corners at 32
                const t = (x / size) * Math.PI * 2;
                const y = yy + Math.sin(t * cycles + phase) * amp1 + Math.sin(t * cycles * 2 + phase * 1.7) * amp2;
                if (x === -OVER) ctx.moveTo(x, y); else ctx.lineTo(x, y);
            }
            ctx.stroke();
        }
    }
    ctx.globalAlpha = 1;
}

// FIXED, and not hexSeed(baseHex) — the one thing about this generator that must not be changed casually. All 32
// panes share a single cache slot, so the canvas is baked from whichever pane happens to render first; if any of its
// jitter came from that pane's colour, two clients that walked up to the same wall in a different order would be
// looking at two different sheets of glass. A constant seed makes the sheet the same everywhere, always.
const GLASS_SEED = hexSeed('float glass');

// FLOAT GLASS — the 32 melt panes, and every "<X> Glass Bit", from ONE canvas.
//
// Reference: a sheet of ordinary float/cast glass looked at off-axis. What actually tells you it is glass and not a
// coloured film is not sparkle — it is that the sheet is very slightly WRONG: long soft ream running in the draw
// direction, a few threads of cord where the melt was not quite homogeneous, and the odd seed bubble stretched out
// by the ribbon. That last one is the feature carrying the recognition: an elongated bubble with a bright refracted
// core and a dark rim is a thing only glass does.
//
// NEUTRAL, because all 32 tints come out of this one slot and applyMaterial multiplies the canvas by each pane's own
// hex. Anything but greys here would square the tint of every pane at once.
//
// TOWARD WHITE. The map can only ever darken — multiply has no way up — so the sheen is bought by sitting the whole
// sheet a little under white (BASE 0.95) and letting the ream crests come back to it. Read the levels as a budget:
// 208..254 over a base of 242, a ~18% swing, which through opacity ~0.5 lands near 9% on screen. That is enough to
// see the sheet move under the light and far too little to read as dirt. Contrast is not what separates glass from
// grime here — SPATIAL FREQUENCY is. Everything below is low-frequency and directional; a stipple at the same
// amplitude would look like a filthy window.
//
// NO EDGE SHEEN, though real glass has one. A bright border on this tile would draw a rectangle at every cell
// boundary — precisely the seam grid that the aFaceMask discard in system-view.js exists to remove — and it would do
// it on the INSIDE of a wall where no edge exists. The sheet's edges are geometry, not texture.
//
// ALPHA IS 1 EVERYWHERE, and must stay so: a texel's alpha multiplies the pane's own opacity, so a soft-edged sprite
// left with a transparent surround would punch a hole of extra see-through into the glass. The base fillRect is
// opaque and nothing here uses a compositing mode that can lower destination alpha.
export function createFloatGlassTexture(baseHex) {   // baseHex is accepted for signature parity and deliberately UNUSED — see GLASS_SEED
    const size = 1024;
    const canvas = document.createElement('canvas');
    canvas.width = canvas.height = size;
    const ctx = canvas.getContext('2d');
    const seed = GLASS_SEED;

    const BASE   = darkenColor('#FFFFFF', 0.05);   // rgb(242) — the sheet sits just under white so crests have somewhere to go
    const CREST  = '#FFFFFF';
    const TROUGH = darkenColor('#FFFFFF', 0.16);
    const CORD   = darkenColor('#FFFFFF', 0.22);

    ctx.fillStyle = BASE;
    ctx.fillRect(0, 0, size, size);
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';

    // 1. The slow thickness variation a ribbon carries down the line: three very wide, very faint bands. This is the
    //    only large-scale content, and it has to come from bands rather than a gradient — a linear gradient across
    //    the tile ends at a different value from where it started and seams hard.
    for (let i = 0; i < 3; i++) {
        drawWrappedBand(ctx, size, hash01(i + seed, 41) * size,
            30 + hash01(i + seed, 42) * 40, 10 + hash01(i + seed, 43) * 16,
            1, hash01(i + seed, 44) * 6.283,
            170 + hash01(i + seed, 45) * 110,
            hash01(i, seed + 46) > 0.5 ? CREST : TROUGH,
            0.05 + hash01(i, seed + 47) * 0.05);
    }
    // 2. ROLL RIPPLE — the ream. All of it parallel, because float glass is drawn in one direction; that shared
    //    grain is what makes a wall of mixed tints read as sheet stock cut from one run rather than as 40 blocks.
    for (let i = 0; i < 22; i++) {
        drawWrappedBand(ctx, size, hash01(i + seed, 51) * size,
            8 + hash01(i + seed, 52) * 30, 3 + hash01(i + seed, 53) * 11,
            1 + Math.floor(hash01(i + seed, 54) * 2), hash01(i + seed, 55) * 6.283,
            26 + hash01(i + seed, 56) * 46,
            hash01(i, seed + 57) > 0.45 ? CREST : TROUGH,
            0.10 + hash01(i, seed + 58) * 0.16);
    }
    // 3. CORD / STRIAE — thin threads of off-index melt, wandering a little harder than the ream they lie in.
    for (let i = 0; i < 28; i++) {
        drawWrappedBand(ctx, size, hash01(i + seed, 61) * size,
            12 + hash01(i + seed, 62) * 30, 4 + hash01(i + seed, 63) * 12,
            1 + Math.floor(hash01(i + seed, 64) * 3), hash01(i + seed, 65) * 6.283,
            3 + hash01(i + seed, 66) * 7,
            hash01(i, seed + 67) > 0.5 ? CREST : CORD,
            0.05 + hash01(i, seed + 68) * 0.08);
    }

    // 4. SEED BUBBLES. Gas caught in the melt and drawn out by the ribbon, so they are ELLIPSES lying along the ream,
    //    never circles — bright core, dark rim, which is what a bubble in a transparent solid actually looks like.
    //    Seven of them: a point feature repeats once per CELL, so every extra one is another dot in a lattice across
    //    a wall, and the count is a deliberate floor rather than a texturing budget. Each is drawn at every wrapped
    //    offset that can reach the tile, so one sitting on an edge arrives whole from the other side.
    ctx.globalAlpha = 1;
    for (let i = 0; i < 7; i++) {
        const bx = hash01(i + seed, 71) * size, by = hash01(i + seed, 72) * size;
        const rx = 9 + hash01(i + seed, 73) * 14;
        const ry = rx * (0.28 + hash01(i + seed, 74) * 0.18);
        const ang = (hash01(i + seed, 75) - 0.5) * 0.5;
        for (let ox = -1; ox <= 1; ox++) for (let oy = -1; oy <= 1; oy++) {
            const cx = bx + ox * size, cy = by + oy * size;
            if (cx < -rx || cx > size + rx || cy < -rx || cy > size + rx) continue;
            ctx.save();
            ctx.translate(cx, cy);
            ctx.rotate(ang);
            ctx.scale(1, ry / rx);                     // rotate THEN squash: the long axis follows the ream
            const g = ctx.createRadialGradient(0, 0, 0, 0, 0, rx);
            g.addColorStop(0.00, 'rgba(255, 255, 255, 0.95)');
            g.addColorStop(0.42, 'rgba(255, 255, 255, 0.30)');
            g.addColorStop(0.70, 'rgba(186, 186, 186, 0.55)');   // the refraction rim
            g.addColorStop(1.00, 'rgba(186, 186, 186, 0.00)');   // fades out rather than ending on an edge
            ctx.fillStyle = g;
            ctx.beginPath();
            ctx.arc(0, 0, rx, 0, Math.PI * 2);
            ctx.fill();
            ctx.restore();
        }
    }

    const texture = new THREE.CanvasTexture(canvas);
    texture.wrapS = texture.wrapT = THREE.RepeatWrapping;
    return texture;
}


// REINFORCED GLASS — wired safety glass, the other half of the family and a completely different object.
//
// Reference: Georgian wired glass — a welded steel mesh rolled into a cast plate. The recognition is carried by the
// WEAVE, not by the grid: a printed lattice reads as a decal, but a mesh where the wire alternates over and under at
// each crossing, with a small contact shadow where it dips beneath, reads as something embedded in the plate. Around
// it, the plate is cast rather than floated, so its ripple is cross-hatched — rolled from both sides — instead of
// running one way like a pane.
//
// This one is OPAQUE (isGlassKubeName excludes it, so makeVoxMaterial gives it no glassOpacity), which is why it can
// carry real contrast: the wire runs down to 0.32 where a pane never goes below 0.82.
//
// NEUTRAL, even though it owns its cache slot and could bake #A8D8E8 in the way the crafted generators bake theirs.
// Baking would square that hex — (0.66, 0.85, 0.91) becomes (0.43, 0.72, 0.83) — and turn a pale structural blue
// into a dark saturated teal. Greys keep the catalogue's colour exactly and let the wire come out as a darker shade
// of it. (Multiply cannot desaturate, so the wire is steel-BLUE rather than steel-grey; that is the honest cost.)
// hexSeed(baseHex) is safe here where it is not for the panes: the only two kubes that reach this slot, "Reinforced
// Glass" and "Reinforced Glass Bit", carry the same hex, so the seed is the same whichever renders first.
//
// TILING: the mesh pitch is size/8 and every wire is offset half a pitch, so no wire lies on the tile edge and the
// 128 px gap between the last wire of one tile and the first of the next is exactly the gap everywhere else. The
// over/under parity is (i + j) & 1 with an EVEN wire count, so the alternation also continues correctly across the
// seam rather than repeating itself. The plate ripple wraps through drawWrappedBand; the scuffs are confined well
// inside the edges instead.
export function createWiredGlassTexture(baseHex) {
    const size = 1024;
    const canvas = document.createElement('canvas');
    canvas.width = canvas.height = size;
    const ctx = canvas.getContext('2d');
    const seed = hexSeed(baseHex);

    const PLATE   = darkenColor('#FFFFFF', 0.06);
    const ROLL_HI = '#FFFFFF';
    const ROLL_LO = darkenColor('#FFFFFF', 0.20);
    const WIRE    = darkenColor('#FFFFFF', 0.60);
    const WIRE_LO = darkenColor('#FFFFFF', 0.80);          // contact shadow, under the wire and where it dips
    const WIRE_HI = blendColors(WIRE, '#FFFFFF', 0.52);    // the specular streak along a round wire

    ctx.fillStyle = PLATE;
    ctx.fillRect(0, 0, size, size);
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';

    // Cast plate: the same ream as a pane but drawn twice, the second pass through a quarter-turn that maps the tile
    // onto itself (translate to the far corner, rotate 90°), so the roll marks cross. Rolled, not floated.
    const rolled = (tag) => {
        for (let i = 0; i < 14; i++) {
            drawWrappedBand(ctx, size, hash01(i + seed, tag) * size,
                10 + hash01(i + seed, tag + 1) * 34, 4 + hash01(i + seed, tag + 2) * 13,
                1 + Math.floor(hash01(i + seed, tag + 3) * 2), hash01(i + seed, tag + 4) * 6.283,
                34 + hash01(i + seed, tag + 5) * 70,
                hash01(i, seed + tag + 6) > 0.5 ? ROLL_HI : ROLL_LO,
                0.06 + hash01(i, seed + tag + 7) * 0.10);
        }
    };
    rolled(81);
    ctx.save();
    ctx.translate(size, 0);
    ctx.rotate(Math.PI / 2);
    rolled(91);
    ctx.restore();

    // ---- The mesh. A wire is three strokes: a soft contact shadow on the plate down-right, the body, and a bright
    // core streak up-left — a round bar under one light, the same up-left light createCraftSurfaceTexture uses.
    // Bodies and cores are drawn at alpha 1 ON PURPOSE: the weave re-draws short segments over the crossings, and
    // only an opaque stroke re-draws to the same colour instead of stacking into a visible bar at every junction.
    const N = 8, pitch = size / N, off = pitch / 2;
    const BODY = 7, SHADOW = 13, CORE = 2.2, HALF = 13;   // HALF clears the widest thing a crossing can hide (the 13px shadow offset 2.5)

    const line = (ax, ay, bx, by, dx, dy, w, colour, alpha) => {
        ctx.globalAlpha = alpha; ctx.strokeStyle = colour; ctx.lineWidth = w;
        ctx.beginPath(); ctx.moveTo(ax + dx, ay + dy); ctx.lineTo(bx + dx, by + dy); ctx.stroke();
    };
    // parts is a bitmask: 1 = plate shadow, 2 = body, 4 = core. The three are drawn in separate passes so every
    // shadow lies under every wire, which is what stops a crossing from shadowing the wire that passes over it.
    const wire = (p, vert, a, b, parts) => {
        const A = vert ? [p, a] : [a, p], B = vert ? [p, b] : [b, p];
        if (parts & 1) line(A[0], A[1], B[0], B[1], 2.5, 2.5, SHADOW, WIRE_LO, 0.30);
        if (parts & 2) line(A[0], A[1], B[0], B[1], 0, 0, BODY, WIRE, 1);
        if (parts & 4) line(A[0], A[1], B[0], B[1], -1.4, -1.4, CORE, WIRE_HI, 1);
    };
    // Where a wire dips under, the one on top drops a short shadow onto it just past its own width. Butt caps, so it
    // reads as a shadow band across the wire rather than as a nub stuck on the side of it.
    const tick = (ax, ay, bx, by) => line(ax, ay, bx, by, 0, 0, BODY * 0.8, WIRE_LO, 0.42);

    for (let j = 0; j < N; j++) wire(off + j * pitch, false, -24, size + 24, 1);   // all plate shadows first
    for (let i = 0; i < N; i++) wire(off + i * pitch, true,  -24, size + 24, 1);
    for (let j = 0; j < N; j++) wire(off + j * pitch, false, -24, size + 24, 6);   // horizontals
    ctx.lineCap = 'butt';
    for (let j = 0; j < N; j++) for (let i = 0; i < N; i++) {                      // ...shadowed by the verticals about to cover them
        if (((i + j) & 1) === 0) continue;
        const cx = off + i * pitch, cy = off + j * pitch;
        tick(cx + BODY / 2 + 1, cy, cx + BODY / 2 + 7, cy);
    }
    ctx.lineCap = 'round';
    for (let i = 0; i < N; i++) wire(off + i * pitch, true, -24, size + 24, 6);    // verticals, now over everything
    ctx.lineCap = 'butt';
    for (let j = 0; j < N; j++) for (let i = 0; i < N; i++) {                      // ...shadowed at the other half of the crossings
        if (((i + j) & 1) !== 0) continue;
        const cx = off + i * pitch, cy = off + j * pitch;
        tick(cx, cy + BODY / 2 + 1, cx, cy + BODY / 2 + 7);
    }
    ctx.lineCap = 'round';
    for (let j = 0; j < N; j++) for (let i = 0; i < N; i++) {                      // ...and the horizontal comes back over the top there
        if (((i + j) & 1) !== 0) continue;
        wire(off + j * pitch, false, off + i * pitch - HALF, off + i * pitch + HALF, 6);
    }
    ctx.globalAlpha = 1;

    for (let j = 0; j < N; j++) for (let i = 0; i < N; i++) {   // the little catch of light every welded junction has
        const cx = off + i * pitch - 2, cy = off + j * pitch - 2;
        const g = ctx.createRadialGradient(cx, cy, 0, cx, cy, 7);
        g.addColorStop(0.00, 'rgba(255, 255, 255, 0.50)');
        g.addColorStop(1.00, 'rgba(255, 255, 255, 0.00)');
        ctx.fillStyle = g;
        ctx.beginPath(); ctx.arc(cx, cy, 7, 0, Math.PI * 2); ctx.fill();
    }

    for (let i = 0; i < 5; i++) {   // seeds — rounder than a pane's, because a cast plate is not drawn out
        const bx = 40 + hash01(i + seed, 101) * (size - 80), by = 40 + hash01(i + seed, 102) * (size - 80);
        const rx = 6 + hash01(i + seed, 103) * 9, ry = rx * (0.55 + hash01(i + seed, 104) * 0.4);
        ctx.save();
        ctx.translate(bx, by); ctx.scale(1, ry / rx);
        const g = ctx.createRadialGradient(0, 0, 0, 0, 0, rx);
        g.addColorStop(0.00, 'rgba(255, 255, 255, 0.75)');
        g.addColorStop(0.55, 'rgba(255, 255, 255, 0.20)');
        g.addColorStop(0.80, 'rgba(178, 178, 178, 0.50)');
        g.addColorStop(1.00, 'rgba(178, 178, 178, 0.00)');
        ctx.fillStyle = g;
        ctx.beginPath(); ctx.arc(0, 0, rx, 0, Math.PI * 2); ctx.fill();
        ctx.restore();
    }

    for (let s = 0; s < 22; s++) {   // scuffs — structural glass gets knocked. Kept well inside the edges, so no wrap needed.
        const x0 = 60 + hash01(s + seed, 111) * (size - 120), y0 = 60 + hash01(s + seed, 112) * (size - 120);
        const a = hash01(s + seed, 113) * Math.PI, len = 12 + hash01(s + seed, 114) * 40;
        ctx.globalAlpha = 0.05 + hash01(s, seed + 115) * 0.09;
        ctx.strokeStyle = hash01(s, seed + 116) > 0.5 ? ROLL_HI : ROLL_LO;
        ctx.lineWidth = 1 + hash01(s, seed + 117) * 1.6;
        ctx.beginPath(); ctx.moveTo(x0, y0); ctx.lineTo(x0 + Math.cos(a) * len, y0 + Math.sin(a) * len); ctx.stroke();
    }
    ctx.globalAlpha = 1;

    const texture = new THREE.CanvasTexture(canvas);
    texture.wrapS = texture.wrapT = THREE.RepeatWrapping;
    return texture;
}


// SILICON — a broken lump of metallurgical-grade polysilicon. Silicon is brittle and breaks CONCHOIDALLY, like flint
// and like glass, so what you are looking at is a landscape of shallow shell-shaped flake scars with ripple marks in
// them, cut apart by the straight risers where one scar ran out against the next.
// NEUTRAL, on a light ground: material.color (#5A5E6B) supplies the blue-grey and this canvas only says where the
// light is. Baking the hex in would square it to about #202127 and take the lustre with it.
export function createSiliconFractureTexture(baseHex) {
    const size = 1024;
    const canvas = document.createElement('canvas');
    canvas.width = canvas.height = size;
    const ctx = canvas.getContext('2d');
    const seed = hexSeed(baseHex);

    // Every scattered feature is drawn again shifted by a whole tile whenever it comes within `r` of an edge, which is
    // what carries a scar off the right edge and back onto the left. At most four draws, never nine.
    const wrapAt = (x, y, r, draw) => {
        const ox = x < r ? size : (x > size - r ? -size : 0);
        const oy = y < r ? size : (y > size - r ? -size : 0);
        draw(x, y);
        if (ox) draw(x + ox, y);
        if (oy) draw(x, y + oy);
        if (ox && oy) draw(x + ox, y + oy);
    };

    ctx.fillStyle = '#DCDCDC';
    ctx.fillRect(0, 0, size, size);

    // FLAKE SCARS. The surface a conchoidal break leaves is not a flat cleavage plane but a scoop, bounded by the arc
    // where the fracture ran out and by the straight platform edge it started from, and covered in ripple marks
    // concentric with that arc. Each scar here is therefore a chord-cut SEGMENT, not a disc (a field of whole discs
    // reads as fish scales), and they overlap so each one bites into the last. The ripples are the recognition:
    // nothing else in the ground makes rings.
    for (let f = 0; f < 54; f++) {
        const cx = hash01(f + seed, 41) * size, cy = hash01(f + seed, 42) * size;
        const R = 45 + hash01(f + seed, 43) * hash01(f + seed, 44) * 300;   // squared draw: many small scars, a few huge
        const g = Math.round((0.55 + hash01(f, seed + 45) * 0.45) * 255);
        const fillA = 0.24 + hash01(f, seed + 46) * 0.36;
        const rings = 3 + Math.floor(hash01(f + seed, 47) * 6);
        const a0 = hash01(f + seed, 48) * 6.2832, sweep = 2.3 + hash01(f + seed, 49) * 3.2;
        const seg = (x, y, rad) => { ctx.beginPath(); ctx.arc(x, y, rad, a0, a0 + sweep); ctx.closePath(); };
        wrapAt(cx, cy, R * 1.08 + 8, (x, y) => {
            ctx.globalAlpha = fillA;
            ctx.fillStyle = 'rgb(' + g + ',' + g + ',' + g + ')';
            seg(x, y, R); ctx.fill();
            ctx.globalAlpha = 0.34 + hash01(f, seed + 50) * 0.34;      // the step down off the scar, and the platform edge
            ctx.strokeStyle = '#3D3D3D'; ctx.lineWidth = 2.8;
            seg(x, y, R * 1.005); ctx.stroke();
            ctx.globalAlpha = 0.30 + hash01(f, seed + 51) * 0.45;      // the same rim, lit from the upper left
            ctx.strokeStyle = '#FFFFFF'; ctx.lineWidth = 2.0;
            ctx.beginPath(); ctx.arc(x - 2, y - 2, R * 0.985, a0, a0 + sweep); ctx.stroke();
            for (let r = 1; r <= rings; r++) {                          // ripple marks, concentric with the rim
                const rr = R * (1 - r * (0.09 + hash01(f * 7 + r, seed + 52) * 0.06));
                if (rr < 7) break;
                const a = 0.11 + hash01(f * 13 + r + seed, 53) * 0.24;
                ctx.globalAlpha = a;
                ctx.strokeStyle = '#3E3E3E'; ctx.lineWidth = 1.8;
                ctx.beginPath(); ctx.arc(x, y, rr, a0 + 0.06, a0 + sweep - 0.06); ctx.stroke();
                ctx.globalAlpha = a * 1.3;
                ctx.strokeStyle = '#FFFFFF'; ctx.lineWidth = 1.5;
                ctx.beginPath(); ctx.arc(x, y + 2.6, rr, a0 + 0.06, a0 + sweep - 0.06); ctx.stroke();
            }
        });
    }
    ctx.globalAlpha = 1;

    // Long straight risers where one flake ran out against the next. The slope is a WHOLE number of tiles per tile, so
    // the line closes on itself under RepeatWrapping instead of stopping dead at the seam.
    ctx.lineCap = 'butt';
    for (let s = 0; s < 7; s++) {
        const m = Math.floor(hash01(s + seed, 53) * 5) - 2;
        const c0 = hash01(s + seed, 54) * size;
        const steep = hash01(s, seed + 55) > 0.5;
        const w = 2.2 + hash01(s + seed, 56) * 3.4;
        const litA = 0.34 + hash01(s, seed + 57) * 0.32, darkA = 0.26 + hash01(s, seed + 58) * 0.28;
        for (let o = -2; o <= 2; o++) {
            const c = c0 + o * size;
            const line = (off, col, alpha, width) => {
                ctx.strokeStyle = col; ctx.globalAlpha = alpha; ctx.lineWidth = width;
                ctx.beginPath();
                if (steep) { ctx.moveTo(c + off, -size); ctx.lineTo(c + m * 3 * size + off, 2 * size); }
                else { ctx.moveTo(-size, c + off); ctx.lineTo(2 * size, c + m * 3 * size + off); }
                ctx.stroke();
            };
            line(0, '#FFFFFF', litA, w);
            line(w + 1.5, '#3D3D3D', darkA, w * 0.9);
        }
    }
    ctx.globalAlpha = 1;

    ctx.lineCap = 'round';                                             // the lustre: hard specular chips on the facets
    for (let g = 0; g < 110; g++) {
        const x = hash01(g + seed, 61) * size, y = hash01(g + seed, 62) * size;
        const l = 6 + hash01(g + seed, 63) * 26, a = hash01(g + seed, 64) * 3.1416;
        const dx = Math.cos(a) * l * 0.5, dy = Math.sin(a) * l * 0.5;
        ctx.globalAlpha = 0.25 + hash01(g, seed + 65) * 0.55;
        ctx.strokeStyle = '#FFFFFF';
        ctx.lineWidth = 1.2 + hash01(g, seed + 66) * 2.6;
        wrapAt(x, y, l + 5, (px, py) => {
            ctx.beginPath(); ctx.moveTo(px - dx, py - dy); ctx.lineTo(px + dx, py + dy); ctx.stroke();
        });
    }
    ctx.globalAlpha = 1;

    // A diagonal gradient whose stop pattern repeats every HALF of its run. A diagonal advances exactly half a run for
    // each whole tile of x or y, so a half-period pattern is what makes the wash itself tile.
    const sheen = ctx.createLinearGradient(0, 0, size, size);
    for (let k = 0; k <= 4; k++) sheen.addColorStop(k / 4, k % 2 ? 'rgba(255,255,255,0.16)' : 'rgba(105,105,105,0.12)');
    ctx.fillStyle = sheen;
    ctx.fillRect(0, 0, size, size);

    const texture = new THREE.CanvasTexture(canvas);
    texture.wrapS = texture.wrapT = THREE.RepeatWrapping;
    return texture;
}


// CARBON — graphite/anthracite. Carbon's bonding is layered, so a broken lump is a shingle of flat plates: each one
// flashes hard along its cleavage plane, the crumb between them stays sooty and matte, and fine parallel striping runs
// across every plate face where the basal planes show.
// NEUTRAL and deliberately BRIGHT. The kube hex is #333333, so the map has almost no headroom — multiplied, its very
// brightest possible pixel IS #333333 — and building on white and only ever darkening keeps the whole of that range.
export function createGraphitePlateTexture(baseHex) {
    const size = 1024;
    const canvas = document.createElement('canvas');
    canvas.width = canvas.height = size;
    const ctx = canvas.getContext('2d');
    const seed = hexSeed(baseHex);

    const wrapAt = (x, y, r, draw) => {
        const ox = x < r ? size : (x > size - r ? -size : 0);
        const oy = y < r ? size : (y > size - r ? -size : 0);
        draw(x, y);
        if (ox) draw(x + ox, y);
        if (oy) draw(x, y + oy);
        if (ox && oy) draw(x + ox, y + oy);
    };

    ctx.fillStyle = '#8F8F8F';
    ctx.fillRect(0, 0, size, size);

    for (let d = 0; d < 1100; d++) {                                    // the matte crumb between the plates
        const x = hash01(d + seed, 71) * size, y = hash01(d + seed, 72) * size;
        const w = 2 + hash01(d + seed, 73) * 5;
        ctx.globalAlpha = 0.05 + hash01(d, seed + 74) * 0.18;
        ctx.fillStyle = hash01(d, seed + 75) > 0.5 ? '#FFFFFF' : '#3F3F3F';
        wrapAt(x, y, w + 2, (px, py) => ctx.fillRect(px, py, w, w));
    }
    ctx.globalAlpha = 1;

    // Two passes of plates, big under small, so the surface reads as a SHINGLE of stacked flakes rather than as
    // scattered chips all of one size.
    const plate = (p, L, W) => {
        const x = hash01(p + seed, 81) * size, y = hash01(p + seed, 82) * size;
        const a = hash01(p + seed, 83) * 3.1416;
        const g = Math.round((0.70 + hash01(p, seed + 84) * 0.30) * 255);
        const litTop = Math.cos(a) > Math.sin(a);        // whichever long edge faces up-left is the one that takes the
                                                        // light, so every plate obeys the same lamp however it lies
        const j = (k) => (hash01(p * 7 + k + seed, 85) - 0.5) * 0.30;
        const fillA = 0.58 + hash01(p, seed + 86) * 0.40;
        const strA = 0.09 + hash01(p, seed + 87) * 0.10;
        const nst = 3 + Math.floor(hash01(p + seed, 88) * 5);
        const brightA = 0.45 + hash01(p, seed + 89) * 0.45;
        const darkA = 0.34 + hash01(p, seed + 90) * 0.32;
        wrapAt(x, y, Math.hypot(L, W) * 0.65 + 8, (px, py) => {
            ctx.save();
            ctx.translate(px, py);
            ctx.rotate(a);
            ctx.beginPath();
            ctx.moveTo(-L / 2 * (1 + j(0)), -W / 2 * (1 + j(1)));
            ctx.lineTo(L / 2 * (1 + j(2)), -W / 2 * (1 + j(3)));
            ctx.lineTo(L / 2 * (1 + j(4)), W / 2 * (1 + j(5)));
            ctx.lineTo(-L / 2 * (1 + j(6)), W / 2 * (1 + j(7)));
            ctx.closePath();
            ctx.globalAlpha = fillA;
            ctx.fillStyle = 'rgb(' + g + ',' + g + ',' + g + ')';
            ctx.fill();
            ctx.globalAlpha = strA;                       // basal planes
            ctx.strokeStyle = '#3B3B3B'; ctx.lineWidth = 1.2;
            for (let s = 1; s < nst; s++) {
                const yy = -W / 2 + (W * s) / nst;
                ctx.beginPath(); ctx.moveTo(-L / 2, yy); ctx.lineTo(L / 2, yy); ctx.stroke();
            }
            ctx.globalAlpha = brightA;                    // the cleavage flash
            ctx.strokeStyle = '#FFFFFF'; ctx.lineWidth = 2.4;
            ctx.beginPath();
            ctx.moveTo(-L / 2, litTop ? -W / 2 : W / 2); ctx.lineTo(L / 2, litTop ? -W / 2 : W / 2);
            ctx.stroke();
            ctx.globalAlpha = darkA;                      // and the shadow it throws on the plate below
            ctx.strokeStyle = '#242424'; ctx.lineWidth = 3.0;
            ctx.beginPath();
            ctx.moveTo(-L / 2, litTop ? W / 2 : -W / 2); ctx.lineTo(L / 2, litTop ? W / 2 : -W / 2);
            ctx.stroke();
            ctx.restore();
        });
    };
    for (let p = 0; p < 42; p++) plate(p, 150 + hash01(p + seed, 91) * 200, 55 + hash01(p + seed, 92) * 85);
    for (let p = 42; p < 130; p++) plate(p, 45 + hash01(p + seed, 93) * 130, 16 + hash01(p + seed, 94) * 48);
    ctx.globalAlpha = 1;

    for (let s = 0; s < 24; s++) {                                      // broad sheen, so groups of plates flash together
        const x = hash01(s + seed, 101) * size, y = hash01(s + seed, 102) * size;
        const r = 90 + hash01(s + seed, 103) * 200;
        const a = 0.06 + hash01(s, seed + 104) * 0.14;
        wrapAt(x, y, r, (px, py) => {
            const gr = ctx.createRadialGradient(px, py, 0, px, py, r);
            gr.addColorStop(0, 'rgba(255,255,255,' + a.toFixed(3) + ')');
            gr.addColorStop(1, 'rgba(255,255,255,0)');
            ctx.fillStyle = gr;
            ctx.beginPath(); ctx.arc(px, py, r, 0, 6.2832); ctx.fill();
        });
    }

    const texture = new THREE.CanvasTexture(canvas);
    texture.wrapS = texture.wrapT = THREE.RepeatWrapping;
    return texture;
}


// POTASSIUM — a soft alkali metal, drawn at the moment it is losing. Cut it and you get a mirror for about a second;
// then the surface blooms over with a dull oxide crust that creeps out of every scratch, and the metal survives only
// as shrinking bright islands. It is soft enough to cut like cheese, so its surfaces are SMEARED, never faceted —
// that, and the bloomy two-phase boundary, is what separates it from steel.
//
// NOTE ON THE DISPATCH: Potassium's metalness is exactly 0.70 and the ore gate is `metalness > 0.7`, so it has been
// falling through to a flat swatch. It would fail anyway from system-view.js's material-by-{name,hex} path, which
// hands every kube the 0.1 default — which is why this family routes by NAME instead of by metalness.
// NEUTRAL: #D6D6D6 is already a silvery grey, so the canvas supplies only value.
export function createAlkaliTarnishTexture(baseHex) {
    const size = 1024;
    const canvas = document.createElement('canvas');
    canvas.width = canvas.height = size;
    const ctx = canvas.getContext('2d');
    const seed = hexSeed(baseHex);

    const wrapAt = (x, y, r, draw) => {
        const ox = x < r ? size : (x > size - r ? -size : 0);
        const oy = y < r ? size : (y > size - r ? -size : 0);
        draw(x, y);
        if (ox) draw(x + ox, y);
        if (oy) draw(x, y + oy);
        if (ox && oy) draw(x + ox, y + oy);
    };

    ctx.fillStyle = '#EFEFEF';
    ctx.fillRect(0, 0, size, size);

    // Knife smears: soft metal does not chip, it smears, so the fresh surface is broad and waxy.
    ctx.lineCap = 'round';
    for (let s = 0; s < 70; s++) {
        const x = hash01(s + seed, 111) * size, y = hash01(s + seed, 112) * size;
        const a = (hash01(s, seed + 113) > 0.5 ? 0.55 : 2.30) + (hash01(s + seed, 114) - 0.5) * 0.5;
        const l = 90 + hash01(s + seed, 115) * 260;
        const dx = Math.cos(a) * l * 0.5, dy = Math.sin(a) * l * 0.5;
        ctx.globalAlpha = 0.04 + hash01(s, seed + 116) * 0.10;
        ctx.strokeStyle = hash01(s, seed + 117) > 0.5 ? '#FFFFFF' : '#818181';
        ctx.lineWidth = 22 + hash01(s + seed, 118) * 66;
        wrapAt(x, y, l * 0.5 + 60, (px, py) => {
            ctx.beginPath(); ctx.moveTo(px - dx, py - dy); ctx.lineTo(px + dx, py + dy); ctx.stroke();
        });
    }
    ctx.globalAlpha = 1;

    // The creeping bloom: soft-edged oxide spreading out of every scratch.
    for (let c = 0; c < 110; c++) {
        const x = hash01(c + seed, 121) * size, y = hash01(c + seed, 122) * size;
        const r = 40 + hash01(c + seed, 123) * 150;
        const a = 0.16 + hash01(c, seed + 124) * 0.30;
        wrapAt(x, y, r, (px, py) => {
            const gr = ctx.createRadialGradient(px, py, r * 0.1, px, py, r);
            gr.addColorStop(0, 'rgba(108,108,108,' + a.toFixed(3) + ')');
            gr.addColorStop(0.65, 'rgba(136,136,136,' + (a * 0.45).toFixed(3) + ')');
            gr.addColorStop(1, 'rgba(160,160,160,0)');
            ctx.fillStyle = gr;
            ctx.beginPath(); ctx.arc(px, py, r, 0, 6.2832); ctx.fill();
        });
    }

    // ...and the crust it dries into: ragged plates, lifted just enough at the edge to throw a shadow of their own.
    for (let f = 0; f < 110; f++) {
        const x = hash01(f + seed, 131) * size, y = hash01(f + seed, 132) * size;
        const r = 16 + hash01(f + seed, 133) * 66;
        const n = 7 + Math.floor(hash01(f + seed, 134) * 6);
        const g = Math.round((0.56 + hash01(f, seed + 135) * 0.22) * 255);
        const a = 0.22 + hash01(f, seed + 136) * 0.30;
        const poly = (px, py) => {
            ctx.beginPath();
            for (let k = 0; k < n; k++) {
                const ang = (k / n) * 6.2832;
                const rr = r * (0.62 + hash01(f * 17 + k + seed, 137) * 0.7);
                if (k === 0) ctx.moveTo(px + Math.cos(ang) * rr, py + Math.sin(ang) * rr);
                else ctx.lineTo(px + Math.cos(ang) * rr, py + Math.sin(ang) * rr);
            }
            ctx.closePath();
        };
        wrapAt(x, y, r * 1.6, (px, py) => {
            ctx.globalAlpha = a * 0.55;
            ctx.fillStyle = '#4E4E4E';
            poly(px + r * 0.10, py + r * 0.12); ctx.fill();
            ctx.globalAlpha = a;
            ctx.fillStyle = 'rgb(' + g + ',' + g + ',' + g + ')';
            poly(px, py); ctx.fill();
            ctx.globalAlpha = a * 1.15;
            ctx.strokeStyle = '#FFFFFF'; ctx.lineWidth = 2.0;
            ctx.stroke();
        });
    }
    ctx.globalAlpha = 1;

    // Metal that has not gone yet — bright, soft-edged islands, the thing the crust is eating.
    for (let i = 0; i < 44; i++) {
        const x = hash01(i + seed, 141) * size, y = hash01(i + seed, 142) * size;
        const r = 16 + hash01(i + seed, 143) * 50;
        const a = 0.40 + hash01(i, seed + 144) * 0.45;
        wrapAt(x, y, r * 2.2, (px, py) => {
            const gr = ctx.createRadialGradient(px - r * 0.35, py - r * 0.35, 0, px, py, r * 2.0);
            gr.addColorStop(0, 'rgba(255,255,255,' + a.toFixed(3) + ')');
            gr.addColorStop(0.40, 'rgba(255,255,255,' + (a * 0.55).toFixed(3) + ')');
            gr.addColorStop(1, 'rgba(255,255,255,0)');
            ctx.fillStyle = gr;
            ctx.beginPath(); ctx.arc(px, py, r * 2.0, 0, 6.2832); ctx.fill();
        });
    }

    for (let p = 0; p < 220; p++) {                                     // pitting, each with its own lit far wall
        const x = hash01(p + seed, 151) * size, y = hash01(p + seed, 152) * size;
        const r = 2.5 + hash01(p + seed, 153) * 8;
        ctx.globalAlpha = 0.22 + hash01(p, seed + 154) * 0.36;
        wrapAt(x, y, r * 2 + 3, (px, py) => {
            ctx.fillStyle = '#535353';
            ctx.beginPath(); ctx.arc(px, py, r, 0, 6.2832); ctx.fill();
            ctx.fillStyle = '#FFFFFF';
            ctx.beginPath(); ctx.arc(px + r * 0.55, py + r * 0.55, r * 0.72, 0, 6.2832); ctx.fill();
        });
    }
    ctx.globalAlpha = 1;

    const texture = new THREE.CanvasTexture(canvas);
    texture.wrapS = texture.wrapT = THREE.RepeatWrapping;
    return texture;
}


// HALITE — rock salt, and the most recognisable thing in this family because its geometry is unmistakable. Salt
// cleaves on three mutually perpendicular planes, so a broken face is a landscape of right-angled terraces; and a salt
// crystal grown fast is a HOPPER, its edges outrunning its faces to leave a stepped square funnel sunk into every one.
// Everything below is therefore axis-aligned rectangles: blocks stepping up, hopper pits stepping down.
// NEUTRAL: #F0F8FF is already the cold near-white, so the canvas carries only the steps and the milkiness.
export function createHaliteCleavageTexture(baseHex) {
    const size = 1024;
    const canvas = document.createElement('canvas');
    canvas.width = canvas.height = size;
    const ctx = canvas.getContext('2d');
    const seed = hexSeed(baseHex);

    const wrapAt = (x, y, r, draw) => {
        const ox = x < r ? size : (x > size - r ? -size : 0);
        const oy = y < r ? size : (y > size - r ? -size : 0);
        draw(x, y);
        if (ox) draw(x + ox, y);
        if (oy) draw(x, y + oy);
        if (ox && oy) draw(x + ox, y + oy);
    };

    ctx.fillStyle = '#EFF3F6';
    ctx.fillRect(0, 0, size, size);

    for (let m = 0; m < 40; m++) {                                     // impurity mottle in the body of the salt
        const x = hash01(m + seed, 161) * size, y = hash01(m + seed, 162) * size;
        const r = 70 + hash01(m + seed, 163) * 200;
        const a = 0.04 + hash01(m, seed + 164) * 0.08;
        const dark = hash01(m, seed + 165) > 0.5;
        wrapAt(x, y, r, (px, py) => {
            const gr = ctx.createRadialGradient(px, py, 0, px, py, r);
            gr.addColorStop(0, (dark ? 'rgba(84,90,98,' : 'rgba(255,255,255,') + a.toFixed(3) + ')');
            gr.addColorStop(1, dark ? 'rgba(84,90,98,0)' : 'rgba(255,255,255,0)');
            ctx.fillStyle = gr;
            ctx.beginPath(); ctx.arc(px, py, r, 0, 6.2832); ctx.fill();
        });
    }

    // ONE bevel, used RAISED for the blocks and inverted (sunk) for the hopper pits, so the two read as opposite
    // depths against each other; the light is from the upper left throughout. The face fill is OPAQUE on purpose: an
    // earlier translucent version let every block show through every other, so only the bevel EDGES were visible and
    // the whole tile read as line-work — a maze. Opaque, a block hides what it lands on and the surface reads as
    // stacked slabs.
    const bevel = (x, y, w, h, g, lip, sunk) => {
        if (!sunk) {                                              // contact shadow, down-right — RAISED blocks only:
            ctx.globalAlpha = 0.22;                               // a pit casts no shadow onto the surface around it,
            ctx.fillStyle = '#2F343A';                            // and drawing one gave every hopper a raised cue
            ctx.fillRect(x + lip, y + h, w, lip * 2.4);           // fighting its sunk cue, so the treads read as
            ctx.fillRect(x + w, y + lip, lip * 2.4, h);           // stacked picture frames instead of funnels.
            ctx.globalAlpha = 1;
        }
        const face = ctx.createLinearGradient(x, y, x + w, y + h);
        const hi = Math.min(255, Math.round(g * 1.06)), lo = Math.round(g * 0.88);
        face.addColorStop(0, 'rgb(' + hi + ',' + hi + ',' + hi + ')');
        face.addColorStop(1, 'rgb(' + lo + ',' + lo + ',' + lo + ')');
        ctx.fillStyle = face;
        ctx.fillRect(x, y, w, h);
        ctx.globalAlpha = sunk ? 0.30 : 0.38;
        ctx.fillStyle = sunk ? '#2F343A' : '#FFFFFF';
        ctx.fillRect(x, y, w, lip);
        ctx.fillRect(x, y, lip, h);
        ctx.globalAlpha = sunk ? 0.34 : 0.28;
        ctx.fillStyle = sunk ? '#FFFFFF' : '#2F343A';
        ctx.fillRect(x, y + h - lip, w, lip);
        ctx.fillRect(x + w - lip, y, lip, h);
        ctx.globalAlpha = 1;
    };

    // The terraces. Big, few and chunky — they are the SUBSTANCE of the texture, and everything else is an accent on
    // top of them, so they want to read as broad flat cleaved faces separated by tone.
    for (let t = 0; t < 13; t++) {
        const x = hash01(t + seed, 171) * size, y = hash01(t + seed, 172) * size;
        const w = 190 + hash01(t + seed, 173) * 290, h = 190 + hash01(t + seed, 174) * 290;
        const g = Math.round((0.70 + hash01(t, seed + 175) * 0.30) * 255);   // wider spread: blocks read apart by TONE, not by outline
        const lip = 9 + Math.floor(hash01(t + seed, 176) * 7);
        wrapAt(x, y, Math.max(w, h) + 44, (px, py) => {
            bevel(px, py, w, h, g, lip, false);
            if (hash01(t, seed + 177) < 0.14) {                   // ONE further step down, RARELY — a bevel inside a bevel is a nested frame, and at 0.45 that was the whole tile
                const dx = 60 + hash01(t + seed, 178) * 90, dy = 60 + hash01(t + seed, 179) * 90;
                const iw = w - dx - 30, ih = h - dy - 30;
                if (iw > 70 && ih > 70) bevel(px + dx, py + dy, iw, ih, Math.round(g * 0.93), Math.max(5, lip - 3), false);
            }
        });
    }
    for (let t = 0; t < 13; t++) {                                // loose cleaved chips, off the nesting rhythm — half as many, so the face between the blocks stays visible as FACE
        const x = hash01(t + seed, 231) * size, y = hash01(t + seed, 232) * size;
        const w = 40 + hash01(t + seed, 233) * 150, h = 40 + hash01(t + seed, 234) * 150;
        const g = Math.round((0.78 + hash01(t, seed + 235) * 0.22) * 255);
        wrapAt(x, y, Math.max(w, h) + 26, (px, py) => bevel(px, py, w, h, g, 5 + Math.floor(hash01(t + seed, 236) * 4), false));
    }

    // HOPPER FACES — the signature. Nested squares, each tread lower than the last, and the treads are uneven because
    // a real hopper's are.
    // TWO of them, small — not six large ones. A hopper is an ACCENT on a salt face, and at six per tile (each up to
    // 340px with five treads, each tread a bevelled square) they covered the surface in concentric squares and the
    // whole texture read as a 3D-printer test pattern rather than as rock. Rock salt is mostly plain cleaved cube
    // faces; the hopper is the thing you notice once, not the thing you see everywhere.
    for (let hc = 0; hc < 2; hc++) {
        const x = hash01(hc + seed, 181) * size, y = hash01(hc + seed, 182) * size;
        const outer = 130 + hash01(hc + seed, 183) * 110;
        const levels = 2 + Math.floor(hash01(hc + seed, 184) * 2);
        wrapAt(x, y, outer + 26, (px, py) => {
            let s = outer;
            for (let k = 0; k < levels && s > 34; k++) {
                bevel(px - s / 2, py - s / 2, s, s, Math.round((0.99 - k * 0.05) * 255), 8, true);
                s -= 2 * (outer / (2.4 * levels)) * (0.65 + hash01(hc * 9 + k + seed, 185) * 0.8);
            }
        });
    }

    // Right-angle cleavage cracks, kept SHORT and held off the tile edge on purpose. A full-length rule tiles perfectly
    // well but lands in the same place on every cell, so it draws a line along every kube boundary in the world and
    // reads as grout rather than as cleavage in the salt.
    for (let c = 0; c < 34; c++) {
        const x = 40 + hash01(c + seed, 191) * (size - 80), y = 40 + hash01(c + seed, 192) * (size - 80);
        const l1 = 24 + hash01(c + seed, 193) * 130, l2 = 24 + hash01(c + seed, 194) * 130;
        const w = 1.5 + hash01(c + seed, 195) * 2.5;
        const sx = hash01(c, seed + 196) > 0.5 ? 1 : -1, sy = hash01(c, seed + 197) > 0.5 ? 1 : -1;
        const a = 0.10 + hash01(c, seed + 198) * 0.22;
        const arm = (col, alpha, o) => {
            ctx.globalAlpha = alpha; ctx.fillStyle = col;
            ctx.fillRect(Math.min(x, x + sx * l1), y + o, l1, w);
            ctx.fillRect(x + o, Math.min(y, y + sy * l2), w, l2);
        };
        arm('#FFFFFF', a * 1.3, -w);
        arm('#2F343A', a, 0);
    }
    ctx.globalAlpha = 1;

    for (let s = 0; s < 46; s++) {                                 // glassy specular chips on the cleavage faces
        const x = hash01(s + seed, 201) * size, y = hash01(s + seed, 202) * size;
        const w = 10 + hash01(s + seed, 203) * 34, h = w * (0.3 + hash01(s + seed, 204) * 1.0);
        ctx.globalAlpha = 0.30 + hash01(s, seed + 205) * 0.45;
        ctx.fillStyle = '#FFFFFF';
        wrapAt(x, y, Math.max(w, h) + 6, (px, py) => ctx.fillRect(px, py, w, h));
    }
    ctx.globalAlpha = 1;

    // A milky wash over the top. Salt is translucent, so light travels a little way INTO it before it comes back out.
    // Without this the blocks read as opaque masonry; with it the tile reads as one crystal mass that happens to be
    // stepped, rather than as a wall of separate bricks.
    for (let s = 0; s < 26; s++) {
        const x = hash01(s + seed, 211) * size, y = hash01(s + seed, 212) * size;
        const r = 90 + hash01(s + seed, 213) * 230;
        const a = 0.05 + hash01(s, seed + 214) * 0.11;
        const light = hash01(s, seed + 215) > 0.42;
        wrapAt(x, y, r, (px, py) => {
            const gr = ctx.createRadialGradient(px, py, 0, px, py, r);
            gr.addColorStop(0, (light ? 'rgba(255,255,255,' : 'rgba(120,128,138,') + a.toFixed(3) + ')');
            gr.addColorStop(1, light ? 'rgba(255,255,255,0)' : 'rgba(120,128,138,0)');
            ctx.fillStyle = gr;
            ctx.beginPath(); ctx.arc(px, py, r, 0, 6.2832); ctx.fill();
        });
    }

    const texture = new THREE.CanvasTexture(canvas);
    texture.wrapS = texture.wrapT = THREE.RepeatWrapping;
    return texture;
}


// BAUXITE — pisolitic bauxite, and it is the most distinctive rock in the catalogue: a clay matrix packed solid with
// pea-sized spheroids, each of which grew in concentric shells around a nucleus the way a hailstone does. Packing and
// rings are the recognition, and the shading is the deliberate inverse of createVesicularSlagTexture's holes — these
// are convex and lit on the upper left, those are concave and lit on the lower right, under the same lamp.
// NEUTRAL: #A9603C carries all the red-brown; the canvas would only muddy it.
export function createPisoliticTexture(baseHex) {
    const size = 1024;
    const canvas = document.createElement('canvas');
    canvas.width = canvas.height = size;
    const ctx = canvas.getContext('2d');
    const seed = hexSeed(baseHex);

    const wrapAt = (x, y, r, draw) => {
        const ox = x < r ? size : (x > size - r ? -size : 0);
        const oy = y < r ? size : (y > size - r ? -size : 0);
        draw(x, y);
        if (ox) draw(x + ox, y);
        if (oy) draw(x, y + oy);
        if (ox && oy) draw(x + ox, y + oy);
    };

    ctx.fillStyle = '#D5D5D5';
    ctx.fillRect(0, 0, size, size);

    for (let m = 0; m < 60; m++) {                                      // the clay between the ooids
        const x = hash01(m + seed, 211) * size, y = hash01(m + seed, 212) * size;
        const r = 70 + hash01(m + seed, 213) * 200;
        const a = 0.04 + hash01(m, seed + 214) * 0.10;
        const dark = hash01(m, seed + 215) > 0.5;
        wrapAt(x, y, r, (px, py) => {
            const gr = ctx.createRadialGradient(px, py, 0, px, py, r);
            gr.addColorStop(0, (dark ? 'rgba(70,70,70,' : 'rgba(255,255,255,') + a.toFixed(3) + ')');
            gr.addColorStop(1, dark ? 'rgba(70,70,70,0)' : 'rgba(255,255,255,0)');
            ctx.fillStyle = gr;
            ctx.beginPath(); ctx.arc(px, py, r, 0, 6.2832); ctx.fill();
        });
    }

    const ooid = (px, py, r, id) => {
        ctx.globalAlpha = 0.20;
        ctx.fillStyle = '#3C3C3C';
        ctx.beginPath(); ctx.arc(px + r * 0.20, py + r * 0.22, r * 1.02, 0, 6.2832); ctx.fill();   // contact shadow
        ctx.globalAlpha = 1;
        const hi = Math.round((0.90 + hash01(id, seed + 221) * 0.10) * 255);
        const lo = Math.round((0.52 + hash01(id, seed + 222) * 0.22) * 255);
        const mid = Math.round((hi + lo) / 2);
        const gr = ctx.createRadialGradient(px - r * 0.38, py - r * 0.40, r * 0.06, px, py, r);
        gr.addColorStop(0, 'rgb(' + hi + ',' + hi + ',' + hi + ')');
        gr.addColorStop(0.62, 'rgb(' + mid + ',' + mid + ',' + mid + ')');
        gr.addColorStop(1, 'rgb(' + lo + ',' + lo + ',' + lo + ')');
        ctx.fillStyle = gr;
        ctx.beginPath(); ctx.arc(px, py, r, 0, 6.2832); ctx.fill();
        ctx.globalAlpha = 0.34 + hash01(id, seed + 223) * 0.30;
        ctx.strokeStyle = '#4A4A4A';
        ctx.lineWidth = 1.8 + hash01(id, seed + 224) * 2.2;
        ctx.beginPath(); ctx.arc(px, py, r * 0.96, 0, 6.2832); ctx.stroke();
        const rings = Math.floor(hash01(id, seed + 225) * 4);      // the concentric cortex — a pisolith grew in shells
        for (let k = 1; k <= rings; k++) {
            const rr = r * (0.80 - k * 0.17);
            if (rr < 3) break;
            ctx.globalAlpha = 0.10 + hash01(id * 5 + k, seed + 226) * 0.16;
            ctx.strokeStyle = k % 2 ? '#FFFFFF' : '#5A5A5A';
            ctx.lineWidth = 1.4;
            ctx.beginPath(); ctx.arc(px, py, rr, 0, 6.2832); ctx.stroke();
        }
        ctx.globalAlpha = 0.40 + hash01(id, seed + 227) * 0.35;
        ctx.fillStyle = '#FFFFFF';
        ctx.beginPath(); ctx.arc(px - r * 0.40, py - r * 0.42, r * 0.20, 0, 6.2832); ctx.fill();
        ctx.globalAlpha = 1;
    };

    for (let b = 0; b < 20; b++) {                                      // a few oversized ones, laid down first
        const x = hash01(b + seed, 231) * size, y = hash01(b + seed, 232) * size;
        const r = 40 + hash01(b + seed, 233) * 26;
        wrapAt(x, y, r * 1.3, (px, py) => ooid(px, py, r, b * 3 + 1));
    }

    // A jittered GRID rather than free scatter: pisolitic bauxite is packed — the ooids support each other and the clay
    // is only what fills between them. Free placement leaves holes and reads as gravel in mud instead.
    const cell = 64, span = size / cell;
    for (let j = 0; j < span; j++) {
        for (let i = 0; i < span; i++) {
            if (hash01(i + seed, j * 7 + 241) < 0.18) continue;   // more gaps: a full grid reads as a lattice however well it is jittered
            const cx = i * cell + cell / 2 + (hash01(i + seed, j + 242) - 0.5) * cell * 0.92;
            const cy = j * cell + cell / 2 + (hash01(i, j + seed + 243) - 0.5) * cell * 0.92;
            const r = 15 + hash01(i * 31 + j + seed, 244) * 21;   // 30-72px: a wide size range is most of what breaks the rows
            wrapAt(cx, cy, r * 1.3, (px, py) => ooid(px, py, r, i * 37 + j * 101 + 7));
        }
    }
    ctx.globalAlpha = 1;

    const texture = new THREE.CanvasTexture(canvas);
    texture.wrapS = texture.wrapT = THREE.RepeatWrapping;
    return texture;
}


// TITANIUM ORE — ilmenite and rutile in a silicate host. The point is that this is DISSEMINATED ore, not a metal: dark
// blocky grains scattered through a duller granular rock, each throwing one hard specular flash off whichever facet
// faces the light. That flash on an otherwise matte matrix is what says "there is metal in this" — without it the same
// grains read as gravel. The thin radiating sprays are rutile's needle habit.
// NEUTRAL: #4E5158 gives the cold near-black; the canvas gives the grain distribution and the flash.
export function createDisseminatedOreTexture(baseHex) {
    const size = 1024;
    const canvas = document.createElement('canvas');
    canvas.width = canvas.height = size;
    const ctx = canvas.getContext('2d');
    const seed = hexSeed(baseHex);

    const wrapAt = (x, y, r, draw) => {
        const ox = x < r ? size : (x > size - r ? -size : 0);
        const oy = y < r ? size : (y > size - r ? -size : 0);
        draw(x, y);
        if (ox) draw(x + ox, y);
        if (oy) draw(x, y + oy);
        if (ox && oy) draw(x + ox, y + oy);
    };

    ctx.fillStyle = '#E9E9E9';
    ctx.fillRect(0, 0, size, size);

    for (let d = 0; d < 1400; d++) {                               // the dull granular silicate host
        const x = hash01(d + seed, 251) * size, y = hash01(d + seed, 252) * size;
        const r = 2 + hash01(d + seed, 253) * 6;
        const g = Math.round((0.64 + hash01(d, seed + 254) * 0.36) * 255);
        ctx.globalAlpha = 0.10 + hash01(d, seed + 255) * 0.26;
        ctx.fillStyle = 'rgb(' + g + ',' + g + ',' + g + ')';
        wrapAt(x, y, r + 2, (px, py) => { ctx.beginPath(); ctx.arc(px, py, r, 0, 6.2832); ctx.fill(); });
    }
    ctx.globalAlpha = 1;

    ctx.lineCap = 'round';
    for (let v = 0; v < 26; v++) {                                 // pale hairline veining in the host
        const x = hash01(v + seed, 261) * size, y = hash01(v + seed, 262) * size;
        const a = hash01(v + seed, 263) * 3.1416;
        const l = 120 + hash01(v + seed, 264) * 300;
        const dx = Math.cos(a) * l * 0.5, dy = Math.sin(a) * l * 0.5;
        ctx.globalAlpha = 0.06 + hash01(v, seed + 265) * 0.10;
        ctx.strokeStyle = '#FFFFFF';
        ctx.lineWidth = 3 + hash01(v + seed, 266) * 9;
        wrapAt(x, y, l * 0.5 + 12, (px, py) => {
            ctx.beginPath(); ctx.moveTo(px - dx, py - dy); ctx.lineTo(px + dx, py + dy); ctx.stroke();
        });
    }
    ctx.globalAlpha = 1;

    // Rutile grows as needles, often as a radiating spray out of one point — the habit that gives sagenite its name.
    for (let s = 0; s < 24; s++) {
        const x = hash01(s + seed, 271) * size, y = hash01(s + seed, 272) * size;
        const n = 3 + Math.floor(hash01(s + seed, 273) * 4);
        const base = hash01(s + seed, 274) * 6.2832;
        wrapAt(x, y, 150, (px, py) => {
            for (let k = 0; k < n; k++) {
                const a = base + (k / n) * 3.1416 + (hash01(s * 9 + k + seed, 275) - 0.5) * 0.6;
                const l = 40 + hash01(s * 9 + k + seed, 276) * 90;
                const g = Math.round((0.40 + hash01(s * 9 + k, seed + 277) * 0.20) * 255);
                ctx.globalAlpha = 0.55 + hash01(s * 9 + k, seed + 278) * 0.35;
                ctx.strokeStyle = 'rgb(' + g + ',' + g + ',' + g + ')';
                ctx.lineWidth = 2.5 + hash01(s * 9 + k + seed, 279) * 3.5;
                ctx.beginPath(); ctx.moveTo(px, py); ctx.lineTo(px + Math.cos(a) * l, py + Math.sin(a) * l); ctx.stroke();
                ctx.globalAlpha = 0.30;
                ctx.strokeStyle = '#FFFFFF';
                ctx.lineWidth = 1;
                ctx.beginPath(); ctx.moveTo(px, py - 0.9); ctx.lineTo(px + Math.cos(a) * l, py + Math.sin(a) * l - 0.9); ctx.stroke();
            }
        });
    }
    ctx.globalAlpha = 1;

    // The ilmenite: blocky (4-6 sides, never round), nearly black, and throwing ONE hard specular flash off the facet
    // that faces the light.
    for (let gI = 0; gI < 210; gI++) {
        const x = hash01(gI + seed, 281) * size, y = hash01(gI + seed, 282) * size;
        const r = 10 + hash01(gI + seed, 283) * 30;
        const n = 4 + Math.floor(hash01(gI + seed, 284) * 3);
        const rot = hash01(gI + seed, 285) * 6.2832;
        const g = Math.round((0.26 + hash01(gI, seed + 286) * 0.22) * 255);
        const poly = (px, py, scale) => {
            ctx.beginPath();
            for (let k = 0; k < n; k++) {
                const a = rot + (k / n) * 6.2832;
                const rr = r * scale * (0.72 + hash01(gI * 13 + k + seed, 287) * 0.42);
                if (k === 0) ctx.moveTo(px + Math.cos(a) * rr, py + Math.sin(a) * rr);
                else ctx.lineTo(px + Math.cos(a) * rr, py + Math.sin(a) * rr);
            }
            ctx.closePath();
        };
        wrapAt(x, y, r * 1.8, (px, py) => {
            ctx.globalAlpha = 0.20;
            ctx.fillStyle = '#3A3A3A';
            poly(px + r * 0.20, py + r * 0.22, 1.0); ctx.fill();
            ctx.globalAlpha = 0.84 + hash01(gI, seed + 288) * 0.16;
            ctx.fillStyle = 'rgb(' + g + ',' + g + ',' + g + ')';
            poly(px, py, 1.0); ctx.fill();
            ctx.globalAlpha = 0.30 + hash01(gI, seed + 289) * 0.40;
            ctx.fillStyle = '#FFFFFF';
            poly(px - r * 0.30, py - r * 0.32, 0.40); ctx.fill();
            ctx.globalAlpha = 0.22 + hash01(gI, seed + 290) * 0.25;
            ctx.strokeStyle = '#FFFFFF'; ctx.lineWidth = 1.4;
            poly(px, py, 1.0); ctx.stroke();
        });
    }
    ctx.globalAlpha = 1;

    const texture = new THREE.CanvasTexture(canvas);
    texture.wrapS = texture.wrapT = THREE.RepeatWrapping;
    return texture;
}


// SLAG — vitrified smelter waste. It cools as a GLASS with the gas still in it, so it is a solid full of frozen
// bubbles: vesicles of wildly assorted size, some whole and some burst open, with smooth glassy shell in between.
// NEUTRAL in value, with ONE deliberate exception: the thin-film sheen. Fresh slag glass carries an oxide skin that
// interferes like oil on water, and that shifting pale rainbow is half of what makes a slag heap recognisable. It is
// held at 7% alpha over a near-white ground, so the multiply by #4F4A46 still lands on a desaturated warm grey rather
// than tinting the kube some new colour.
export function createVesicularSlagTexture(baseHex) {
    const size = 1024;
    const canvas = document.createElement('canvas');
    canvas.width = canvas.height = size;
    const ctx = canvas.getContext('2d');
    const seed = hexSeed(baseHex);

    const wrapAt = (x, y, r, draw) => {
        const ox = x < r ? size : (x > size - r ? -size : 0);
        const oy = y < r ? size : (y > size - r ? -size : 0);
        draw(x, y);
        if (ox) draw(x + ox, y);
        if (oy) draw(x, y + oy);
        if (ox && oy) draw(x + ox, y + oy);
    };

    ctx.fillStyle = '#E4E4E4';
    ctx.fillRect(0, 0, size, size);

    for (let s = 0; s < 30; s++) {                                  // the glass itself: broad, smooth, uneven
        const x = hash01(s + seed, 301) * size, y = hash01(s + seed, 302) * size;
        const r = 90 + hash01(s + seed, 303) * 230;
        const a = 0.05 + hash01(s, seed + 304) * 0.12;
        const light = hash01(s, seed + 305) > 0.5;
        wrapAt(x, y, r, (px, py) => {
            const gr = ctx.createRadialGradient(px, py, 0, px, py, r);
            gr.addColorStop(0, (light ? 'rgba(255,255,255,' : 'rgba(70,70,70,') + a.toFixed(3) + ')');
            gr.addColorStop(1, light ? 'rgba(255,255,255,0)' : 'rgba(70,70,70,0)');
            ctx.fillStyle = gr;
            ctx.beginPath(); ctx.arc(px, py, r, 0, 6.2832); ctx.fill();
        });
    }

    // THIN-FILM SHEEN. Each band's stop pattern repeats a whole number of times across its run, and the first and last
    // stop are the same colour, so the wash tiles with everything else: the two DIAGONAL bands need an EVEN count,
    // because a diagonal gradient advances half a run for each whole tile of x or y; the axis-aligned one does not.
    const FILM = ['rgba(150,215,235,', 'rgba(235,190,225,', 'rgba(240,225,180,', 'rgba(190,235,205,'];
    const BANDS = [[0, 0, size, size, 2], [size, 0, 0, size, 2], [0, 0, size, 0, 3]];
    for (let b = 0; b < BANDS.length; b++) {
        const q = BANDS[b], cyc = q[4];
        const g = ctx.createLinearGradient(q[0], q[1], q[2], q[3]);
        for (let k = 0; k <= cyc * 4; k++) g.addColorStop(k / (cyc * 4), FILM[(k + b) % 4] + '0.07)');
        ctx.fillStyle = g;
        ctx.fillRect(0, 0, size, size);
    }

    for (let d = 0; d < 620; d++) {                                // micro-froth between the big holes
        const x = hash01(d + seed, 311) * size, y = hash01(d + seed, 312) * size;
        const r = 1.5 + hash01(d + seed, 313) * 4;
        ctx.globalAlpha = 0.14 + hash01(d, seed + 314) * 0.26;
        ctx.fillStyle = '#565656';
        wrapAt(x, y, r + 2, (px, py) => { ctx.beginPath(); ctx.arc(px, py, r, 0, 6.2832); ctx.fill(); });
    }
    ctx.globalAlpha = 1;

    // A VESICLE IS A HOLE, so it is shaded as the inverse of a ball — this is the whole difference between reading as
    // bubbles and reading as packed marbles. Light from the upper left falls on the FAR inner wall (down-right) while
    // the near wall and the throat stay in shadow, and the raised rim above the hole takes a thin lit lip. Canvas
    // angles run clockwise from +x with y downward, so 0.35..2.35 is that lower-right wall and 3.45..5.95 the rim.
    const vesicle = (px, py, r, id, ragged) => {
        const path = (rad) => {
            if (!ragged) { ctx.beginPath(); ctx.arc(px, py, rad, 0, 6.2832); return; }
            const n = 8 + Math.floor(hash01(id, seed + 321) * 6);
            ctx.beginPath();
            for (let k = 0; k < n; k++) {
                const a = (k / n) * 6.2832;
                const rr = rad * (0.70 + hash01(id * 19 + k + seed, 322) * 0.58);
                if (k === 0) ctx.moveTo(px + Math.cos(a) * rr, py + Math.sin(a) * rr);
                else ctx.lineTo(px + Math.cos(a) * rr, py + Math.sin(a) * rr);
            }
            ctx.closePath();
        };
        ctx.globalAlpha = 0.34 + hash01(id, seed + 323) * 0.24;     // lit rim, up-left, sitting proud of the hole
        ctx.strokeStyle = '#FFFFFF'; ctx.lineWidth = 2 + r * 0.06;
        ctx.beginPath(); ctx.arc(px, py, r * 1.02, 3.45, 5.95); ctx.stroke();
        const gr = ctx.createRadialGradient(px + r * 0.34, py + r * 0.36, r * 0.05, px, py, r * 1.05);
        gr.addColorStop(0, 'rgba(128,128,128,0.62)');              // the far wall, catching the light
        gr.addColorStop(0.45, 'rgba(56,56,56,0.80)');
        gr.addColorStop(1, 'rgba(24,24,24,0.92)');                 // the near wall and the throat, in shadow
        ctx.globalAlpha = 1;
        ctx.fillStyle = gr;
        path(r); ctx.fill();
        ctx.globalAlpha = 0.30 + hash01(id, seed + 324) * 0.28;    // a sliver of glass catching light deep in the pit
        ctx.strokeStyle = '#FFFFFF'; ctx.lineWidth = 1 + r * 0.05;
        ctx.beginPath(); ctx.arc(px, py, r * 0.74, 0.35, 2.35); ctx.stroke();
        ctx.globalAlpha = 0.45 + hash01(id, seed + 325) * 0.30;    // the sharp broken edge of the hole
        ctx.strokeStyle = '#232323'; ctx.lineWidth = 1.4 + r * 0.03;
        path(r); ctx.stroke();
        ctx.globalAlpha = 1;
    };

    for (let v = 0; v < 120; v++) {
        const x = hash01(v + seed, 331) * size, y = hash01(v + seed, 332) * size;
        const t = hash01(v + seed, 333);
        const r = 5 + t * t * t * 76;                              // a few big holes, a great many small ones
        const ragged = hash01(v, seed + 334) > 0.72;               // ...and about a quarter of them burst open
        wrapAt(x, y, r * 1.7, (px, py) => vesicle(px, py, r, v, ragged));
    }

    ctx.lineCap = 'round';
    for (let c = 0; c < 70; c++) {                                 // conchoidal chips on the glass between the holes
        const x = hash01(c + seed, 341) * size, y = hash01(c + seed, 342) * size;
        const a = hash01(c + seed, 343) * 3.1416, l = 10 + hash01(c + seed, 344) * 44;
        const dx = Math.cos(a) * l * 0.5, dy = Math.sin(a) * l * 0.5;
        ctx.globalAlpha = 0.18 + hash01(c, seed + 345) * 0.42;
        ctx.strokeStyle = '#FFFFFF';
        ctx.lineWidth = 1 + hash01(c, seed + 346) * 2;
        wrapAt(x, y, l * 0.5 + 4, (px, py) => {
            ctx.beginPath(); ctx.moveTo(px - dx, py - dy); ctx.lineTo(px + dx, py + dy); ctx.stroke();
        });
    }
    ctx.globalAlpha = 1;

    const texture = new THREE.CanvasTexture(canvas);
    texture.wrapS = texture.wrapT = THREE.RepeatWrapping;
    return texture;
}


// AEROGEL — "frozen smoke": 99.8% air held in a silica nanofoam. There is no grain, no facet and no edge to draw, only
// a milky internal haze that thickens and thins, wisps of denser foam curling through it, and the shell marks of the
// internal fractures a cast monolith always carries. The contrast is deliberately tiny: a strongly-featured aerogel
// looks like marble. NEUTRAL — the famous Rayleigh blue is already the kube's own #DCEBF5.
//
// OPACITY. kubes.json declares opacity 0.15 / transparent:true, but NEITHER live call site applies them: on-world the
// crust material is makeVoxMaterial's MeshBasicMaterial, whose transparency comes only from isGlassKubeName (which
// excludes Aerogel by design — system-engine.js names it as the reason glassness is not keyed off the transparent
// flag), and the hotbar builds its material from colour/metalness/roughness alone. So the kube renders OPAQUE and its
// translucency has to be PAINTED, in luminance. The canvas is likewise filled fully opaque, for the same reason a
// glass pane wears no crust texture at all: a map's alpha multiplies the material's.
export function createNanofoamTexture(baseHex) {
    const size = 1024;
    const canvas = document.createElement('canvas');
    canvas.width = canvas.height = size;
    const ctx = canvas.getContext('2d');
    const seed = hexSeed(baseHex);

    const wrapAt = (x, y, r, draw) => {
        const ox = x < r ? size : (x > size - r ? -size : 0);
        const oy = y < r ? size : (y > size - r ? -size : 0);
        draw(x, y);
        if (ox) draw(x + ox, y);
        if (oy) draw(x, y + oy);
        if (ox && oy) draw(x + ox, y + oy);
    };

    ctx.fillStyle = '#FFFFFF';
    ctx.fillRect(0, 0, size, size);

    for (let h = 0; h < 46; h++) {                                 // denser pockets of foam
        const x = hash01(h + seed, 351) * size, y = hash01(h + seed, 352) * size;
        const r = 110 + hash01(h + seed, 353) * 220;
        const g = Math.round((0.58 + hash01(h, seed + 354) * 0.22) * 255);
        const a = 0.10 + hash01(h, seed + 355) * 0.22;
        wrapAt(x, y, r, (px, py) => {
            const gr = ctx.createRadialGradient(px, py, 0, px, py, r);
            gr.addColorStop(0, 'rgba(' + g + ',' + g + ',' + g + ',' + a.toFixed(3) + ')');
            gr.addColorStop(0.55, 'rgba(' + g + ',' + g + ',' + g + ',' + (a * 0.45).toFixed(3) + ')');
            gr.addColorStop(1, 'rgba(' + g + ',' + g + ',' + g + ',0)');
            ctx.fillStyle = gr;
            ctx.beginPath(); ctx.arc(px, py, r, 0, 6.2832); ctx.fill();
        });
    }

    for (let h = 0; h < 70; h++) {                                 // ...and the thin places you can see further into
        const x = hash01(h + seed, 361) * size, y = hash01(h + seed, 362) * size;
        const r = 35 + hash01(h + seed, 363) * 210;
        const a = 0.16 + hash01(h, seed + 364) * 0.24;
        wrapAt(x, y, r, (px, py) => {
            const gr = ctx.createRadialGradient(px, py, 0, px, py, r);
            gr.addColorStop(0, 'rgba(255,255,255,' + a.toFixed(3) + ')');
            gr.addColorStop(1, 'rgba(255,255,255,0)');
            ctx.fillStyle = gr;
            ctx.beginPath(); ctx.arc(px, py, r, 0, 6.2832); ctx.fill();
        });
    }

    // The smoke. Streamlines through a curl field made entirely of WHOLE-CYCLE sines, so the field is exactly periodic
    // across the tile and a wisp leaving the right edge arrives at the left already pointing the right way. The walk is
    // done in UNWRAPPED coordinates and the finished polyline is then drawn at every tile offset it can still reach, so
    // the curve crosses the seam as one continuous stroke — the usual modulo-and-moveTo trick leaves a round cap on
    // each side of the seam, which on a texture this smooth was the largest step in the whole image.
    const TAU = 6.283185307179586;
    const p1 = hash01(seed, 371) * TAU, p2 = hash01(seed, 372) * TAU, p3 = hash01(seed, 373) * TAU;
    const field = (x, y) => {
        const u = (x / size) * TAU, v = (y / size) * TAU;
        return Math.sin(u + p1) * Math.cos(v + p2) * 1.9 + Math.sin(2 * v + p3) * 0.8 + Math.cos(u - v + p1) * 0.5
            + Math.sin(3 * u - v + p2) * 0.45 + Math.cos(2 * u + 3 * v + p3) * 0.35;
    };
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    for (let w = 0; w < 130; w++) {
        let x = hash01(w + seed, 381) * size, y = hash01(w + seed, 382) * size;
        // A constant per-wisp turn away from the field. Without it every wisp follows the same few streamlines and the
        // result is brushed satin; with it they cross each other and the haze billows. It is a constant, so the field
        // stays exactly tile-periodic.
        const bias = (hash01(w + seed, 386) - 0.5) * 2.6;
        const steps = 12 + Math.floor(hash01(w + seed, 387) * 22);
        const pts = [x, y];
        let x0 = x, x1 = x, y0 = y, y1 = y;
        for (let s = 0; s < steps; s++) {
            const a = field(x, y) + bias;
            x += Math.cos(a) * 12; y += Math.sin(a) * 12;
            pts.push(x, y);
            if (x < x0) x0 = x; if (x > x1) x1 = x;
            if (y < y0) y0 = y; if (y > y1) y1 = y;
        }
        const lw = 8 + hash01(w, seed + 385) * 34;
        ctx.strokeStyle = hash01(w, seed + 383) > 0.42 ? '#FFFFFF' : '#ACACAC';
        ctx.globalAlpha = 0.030 + hash01(w, seed + 384) * 0.075;
        ctx.lineWidth = lw;
        for (let oy = -1; oy <= 1; oy++) {
            if (y1 + oy * size < -lw || y0 + oy * size > size + lw) continue;
            for (let ox = -1; ox <= 1; ox++) {
                if (x1 + ox * size < -lw || x0 + ox * size > size + lw) continue;
                ctx.beginPath();
                ctx.moveTo(pts[0] + ox * size, pts[1] + oy * size);
                for (let k = 2; k < pts.length; k += 2) ctx.lineTo(pts[k] + ox * size, pts[k + 1] + oy * size);
                ctx.stroke();
            }
        }
    }
    ctx.globalAlpha = 1;

    // Internal shell marks. A cast monolith always carries a few conchoidal fractures inside it, and they are the only
    // hard edges in here — without them the haze reads as cloth rather than as something SOLID you are seeing into.
    for (let c = 0; c < 12; c++) {
        const cx = hash01(c + seed, 391) * size, cy = hash01(c + seed, 392) * size;
        const rings = 4 + Math.floor(hash01(c + seed, 393) * 5);
        const stepR = 24 + hash01(c + seed, 394) * 36;
        const a0 = hash01(c + seed, 395) * TAU, sweep = 0.9 + hash01(c + seed, 396) * 1.7;
        wrapAt(cx, cy, stepR * rings + 10, (px, py) => {
            for (let r = 1; r <= rings; r++) {
                ctx.lineWidth = 2.0;
                ctx.globalAlpha = 0.08 + hash01(c * 11 + r + seed, 397) * 0.13;
                ctx.strokeStyle = '#FFFFFF';
                ctx.beginPath(); ctx.arc(px, py, r * stepR, a0, a0 + sweep); ctx.stroke();
                ctx.globalAlpha = 0.05 + hash01(c * 11 + r + seed, 398) * 0.09;
                ctx.strokeStyle = '#8C8C8C';
                ctx.beginPath(); ctx.arc(px, py + 3.5, r * stepR, a0, a0 + sweep); ctx.stroke();
            }
        });
    }
    ctx.globalAlpha = 1;

    for (let d = 0; d < 1100; d++) {                               // light scattering off the nanostructure
        const x = hash01(d + seed, 401) * size, y = hash01(d + seed, 402) * size;
        const r = 0.8 + hash01(d + seed, 403) * 2;
        ctx.globalAlpha = 0.10 + hash01(d, seed + 404) * 0.32;
        ctx.fillStyle = hash01(d, seed + 405) > 0.35 ? '#FFFFFF' : '#A0A0A0';
        wrapAt(x, y, r + 2, (px, py) => { ctx.beginPath(); ctx.arc(px, py, r, 0, 6.2832); ctx.fill(); });
    }
    ctx.globalAlpha = 1;

    const texture = new THREE.CanvasTexture(canvas);
    texture.wrapS = texture.wrapT = THREE.RepeatWrapping;
    return texture;
}


// COR — the game's own material: the kube at the centre of every differentiated world (_coreKube in system-view's
// compositionFor) and the only thing in the 5.00+ density band, so it is the deepest thing a player ever reaches.
// It has no real referent, so it is built from two that do: DENDRITIC native metal — the branching growth you find as
// native silver in a rock face, or as manganese dendrites on limestone — threaded through a body carrying OPAL's play
// of colour, a pale spectral sheen that shifts rather than glows.
//
// Deliberately NOT the Superconductor/Hyper-Cor look. createEmissivePulseLattice is fabricated: dark shell, straight
// orthogonal conduits on an exact 8-lane grid, hot saturated colour, big blooming junctions. Cor is the opposite on
// every one of those axes — pale body, branching curves that never repeat a spacing, low-saturation spectral wash, and
// motes small enough to read as something living in the stone. Side by side they should say BUILT versus FOUND.
//
// BAKED-PER-KUBE, and the only one of the family that can be: Cor's hex is a near-white, so material.color barely
// tints it and this canvas is shown almost exactly as drawn. That is why the spectral wash is safe here and would not
// be anywhere else — and also why it has to stay pale, because at near-white the map IS the appearance. The hex was
// #FFFFFF until CON-07 moved it to #FFF8E7, so that Cor stopped being the same white swatch as the Lumina rocks, and
// that faint warmth costs the wash at most a fifth of its blue.
export function createCorTexture(baseHex) {
    const size = 1024;
    const canvas = document.createElement('canvas');
    canvas.width = canvas.height = size;
    const ctx = canvas.getContext('2d');
    const seed = hexSeed(baseHex);

    const wrapAt = (x, y, r, draw) => {
        const ox = x < r ? size : (x > size - r ? -size : 0);
        const oy = y < r ? size : (y > size - r ? -size : 0);
        draw(x, y);
        if (ox) draw(x + ox, y);
        if (oy) draw(x, y + oy);
        if (ox && oy) draw(x + ox, y + oy);
    };

    ctx.fillStyle = '#EFF0F5';
    ctx.fillRect(0, 0, size, size);

    const BODY = ['rgba(255,238,220,', 'rgba(222,238,255,', 'rgba(240,230,250,', 'rgba(226,246,238,'];
    for (let m = 0; m < 30; m++) {
        const x = hash01(m + seed, 411) * size, y = hash01(m + seed, 412) * size;
        const r = 120 + hash01(m + seed, 413) * 250;
        const c = BODY[Math.floor(hash01(m + seed, 414) * 4) % 4];
        const a = 0.10 + hash01(m, seed + 415) * 0.18;
        wrapAt(x, y, r, (px, py) => {
            const gr = ctx.createRadialGradient(px, py, 0, px, py, r);
            gr.addColorStop(0, c + a.toFixed(3) + ')');
            gr.addColorStop(1, c + '0)');
            ctx.fillStyle = gr;
            ctx.beginPath(); ctx.arc(px, py, r, 0, 6.2832); ctx.fill();
        });
    }

    // PLAY OF COLOUR. Both bands run on a DIAGONAL and repeat an EVEN number of times, which is what makes them tile —
    // a diagonal gradient advances half a run for each whole tile of x or y, so an odd count would not close. A third,
    // axis-aligned band was built and dropped: it passes the seam test but reads as stripes once the tile repeats.
    const SPECTRUM = ['rgba(255,214,224,', 'rgba(255,240,205,', 'rgba(214,246,226,', 'rgba(216,226,255,'];
    const BANDS = [[0, 0, size, size, 2], [size, 0, 0, size, 4]];
    for (let b = 0; b < BANDS.length; b++) {
        const q = BANDS[b], cyc = q[4];
        const g = ctx.createLinearGradient(q[0], q[1], q[2], q[3]);
        for (let k = 0; k <= cyc * 4; k++) g.addColorStop(k / (cyc * 4), SPECTRUM[(k + b) % 4] + '0.12)');
        ctx.fillStyle = g;
        ctx.fillRect(0, 0, size, size);
    }

    for (let s = 0; s < 14; s++) {                                 // pearl sheen: broad, soft, colourless
        const x = hash01(s + seed, 421) * size, y = hash01(s + seed, 422) * size;
        const r = 130 + hash01(s + seed, 423) * 220;
        const a = 0.10 + hash01(s, seed + 424) * 0.16;
        wrapAt(x, y, r, (px, py) => {
            const gr = ctx.createRadialGradient(px - r * 0.3, py - r * 0.3, 0, px, py, r);
            gr.addColorStop(0, 'rgba(255,255,255,' + a.toFixed(3) + ')');
            gr.addColorStop(1, 'rgba(255,255,255,0)');
            ctx.fillStyle = gr;
            ctx.beginPath(); ctx.arc(px, py, r, 0, 6.2832); ctx.fill();
        });
    }

    // The dendrites. A stack-driven branching walk, three generations deep, thinning as it goes. Each segment is
    // stroked three times — a wide faint halo, a warm middle, a near-white core — which is cheaper and steadier than
    // shadowBlur, and it is drawn through wrapAt while the walk position is kept modulo the tile, so a branch that
    // leaves one edge continues out of the other with its glow intact.
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    const motes = [];
    for (let t = 0; t < 15; t++) {
        const stack = [{ x: hash01(t + seed, 431) * size, y: hash01(t + seed, 432) * size, h: hash01(t + seed, 433) * 6.2832, d: 0, n: 0 }];
        let guard = 0;
        while (stack.length && guard < 120) {
            const b = stack.pop();
            let x = b.x, y = b.y, h = b.h;
            const steps = 5 + Math.floor(hash01(t * 41 + b.n + seed, 434) * 7);
            const w = 6.8 - b.d * 1.35;
            for (let s = 0; s < steps && guard < 120; s++, guard++) {
                h += (hash01(t * 61 + guard + seed, 435) - 0.5) * 0.55;
                const len = 13 + hash01(t * 71 + guard + seed, 436) * 14;
                const dx = Math.cos(h) * len, dy = Math.sin(h) * len;
                wrapAt(x + dx * 0.5, y + dy * 0.5, len * 0.5 + w * 2.4 + 4, (px, py) => {
                    const seg = (col, alpha, width) => {
                        ctx.globalAlpha = alpha; ctx.strokeStyle = col; ctx.lineWidth = width;
                        ctx.beginPath();
                        ctx.moveTo(px - dx * 0.5, py - dy * 0.5);
                        ctx.lineTo(px + dx * 0.5, py + dy * 0.5);
                        ctx.stroke();
                    };
                    seg('#FFE0AE', 0.13, w * 3.8);
                    seg('#CDB48E', 0.28, w * 1.5);
                    seg('#FFFCF4', 0.90, Math.max(1, w * 0.55));
                });
                x = (x + dx + size) % size; y = (y + dy + size) % size;
                if (b.d < 3 && hash01(t * 83 + guard + seed, 437) > 0.66) {
                    const side = hash01(t * 91 + guard + seed, 438) > 0.5 ? 1 : -1;
                    stack.push({ x, y, h: h + side * (0.55 + hash01(t * 97 + guard + seed, 439) * 0.55), d: b.d + 1, n: b.n + 1 });
                }
            }
            if (b.d >= 1 && motes.length < 110) motes.push([x, y]);
        }
    }
    ctx.globalAlpha = 1;

    for (let i = 0; i < motes.length; i++) {                       // a light at the tips — small enough to read as
        const mx = motes[i][0], my = motes[i][1];                  // something alive in the stone, not as a machine
        const r = 8 + hash01(i + seed, 441) * 15;
        const a = 0.42 + hash01(i, seed + 442) * 0.48;
        wrapAt(mx, my, r * 2.2, (px, py) => {
            const gr = ctx.createRadialGradient(px, py, 0, px, py, r * 2.0);
            gr.addColorStop(0, 'rgba(255,250,234,' + a.toFixed(3) + ')');
            gr.addColorStop(0.35, 'rgba(255,231,182,' + (a * 0.45).toFixed(3) + ')');
            gr.addColorStop(1, 'rgba(255,224,168,0)');
            ctx.fillStyle = gr;
            ctx.beginPath(); ctx.arc(px, py, r * 2.0, 0, 6.2832); ctx.fill();
            ctx.globalAlpha = 0.95;
            ctx.fillStyle = '#FFFFFF';
            ctx.beginPath(); ctx.arc(px, py, Math.max(1.2, r * 0.18), 0, 6.2832); ctx.fill();
            ctx.globalAlpha = 1;
        });
    }

    for (let c = 0; c < 150; c++) {                                // craquelure — keeps it mineral rather than fluid
        const x = hash01(c + seed, 451) * size, y = hash01(c + seed, 452) * size;
        const a = hash01(c + seed, 453) * 3.1416, l = 14 + hash01(c + seed, 454) * 54;
        const dx = Math.cos(a) * l * 0.5, dy = Math.sin(a) * l * 0.5;
        ctx.globalAlpha = 0.05 + hash01(c, seed + 455) * 0.10;
        ctx.strokeStyle = hash01(c, seed + 456) > 0.5 ? '#FFFFFF' : '#8A90A4';
        ctx.lineWidth = 0.8 + hash01(c, seed + 457) * 1.4;
        wrapAt(x, y, l * 0.5 + 3, (px, py) => {
            ctx.beginPath(); ctx.moveTo(px - dx, py - dy); ctx.lineTo(px + dx, py + dy); ctx.stroke();
        });
    }
    ctx.globalAlpha = 1;

    const texture = new THREE.CanvasTexture(canvas);
    texture.wrapS = texture.wrapT = THREE.RepeatWrapping;
    return texture;
}


// WATER ICE — a glacier's calving face, or a lake-ice core held up to the light. Two features carry the recognition
// and neither is the colour. The CONCHOIDAL FRACTURE PLANES: a crack in clear ice runs nearly straight, kinks, and
// dies away at both ends — and it is never a plain dark line, it is a dark cleft with a bright mirror sliver beside
// it where the plane itself catches the light. And the TRAINS OF TRAPPED BUBBLES, which run in strings because they
// were frozen in as the front advanced, unevenly spaced and wildly uneven in size. Under both, broad soft pools do
// the work of the 20 cm of translucent ice you are actually looking into.
//
// A crack is FILLED as a tapered ribbon, not stroked segment by segment. Stroking short segments to taper them
// double-composites at every join and beads the crack like a dotted line; one filled polygon whose half-width is a
// function of arc length tapers exactly and composites once.
export function createGlacialIce(baseHex) {
    const size = 1024;
    const canvas = document.createElement('canvas');
    canvas.width = canvas.height = size;
    const ctx = canvas.getContext('2d');
    const seed = hexSeed(baseHex);

    ctx.fillStyle = iceShade(240, 1);
    ctx.fillRect(0, 0, size, size);
    icePools(ctx, size, seed, 0, 11, 220, 430, 255, 0.60, 176, 0.44);    // deep — the body of the ice
    icePools(ctx, size, seed, 60, 20, 70, 180, 255, 0.34, 198, 0.26);    // shallow — cloudier lenses nearer the face

    for (let c = 0; c < 15; c++) {
        const segs = 3 + Math.floor(hash01(c + seed, 51) * 2);
        let x = hash01(c + seed, 52) * size, y = hash01(c, seed + 53) * size;
        let a = hash01(c + seed, 54) * Math.PI * 2;
        const corner = [x, y];
        let x0 = x, y0 = y, x1 = x, y1 = y;
        for (let s = 0; s < segs; s++) {
            a += (hash01(c + seed, 60 + s) - 0.5) * 1.05;               // straight run, then a KINK — conchoidal, not wiggly
            const len = 90 + hash01(c + seed, 70 + s) * 150;
            x += Math.cos(a) * len; y += Math.sin(a) * len;
            corner.push(x, y);
            if (x < x0) x0 = x; if (x > x1) x1 = x;
            if (y < y0) y0 = y; if (y > y1) y1 = y;
        }
        const SUB = 8, dense = [];
        for (let s = 0; s + 3 < corner.length; s += 2)
            for (let u = 0; u < SUB; u++) {
                const t = u / SUB;
                dense.push(corner[s] + (corner[s + 2] - corner[s]) * t, corner[s + 1] + (corner[s + 3] - corner[s + 1]) * t);
            }
        dense.push(corner[corner.length - 2], corner[corner.length - 1]);
        const np = dense.length / 2;
        const nxs = [], nys = [];                                        // per-point normal, from the local heading
        for (let i = 0; i < np; i++) {
            const ia = Math.max(0, i - 1), ib = Math.min(np - 1, i + 1);
            const ddx = dense[ib * 2] - dense[ia * 2], ddy = dense[ib * 2 + 1] - dense[ia * 2 + 1];
            const L = Math.hypot(ddx, ddy) || 1;
            nxs.push(-ddy / L); nys.push(ddx / L);
        }
        const w = 2.6 + hash01(c, seed + 55) * 7.0;
        const aCleft = 0.34 + hash01(c, seed + 56) * 0.30;
        const aPlane = 0.50 + hash01(c, seed + 57) * 0.38;
        // Half-width tapers to nothing at both tips, and a small hashed wobble keeps the edge from reading as drawn.
        const halfAt = (i) => {
            const t = i / (np - 1);
            return 0.3 + w * 0.5 * Math.pow(Math.sin(Math.PI * t), 0.55) * (0.75 + hash01(c + seed, 300 + i) * 0.5);
        };
        const ribbon = (dx, dy, off, scale) => {
            ctx.beginPath();
            for (let i = 0; i < np; i++) {
                const h = halfAt(i) * scale + off;
                const px = dense[i * 2] + dx + nxs[i] * h, py = dense[i * 2 + 1] + dy + nys[i] * h;
                if (i === 0) ctx.moveTo(px, py); else ctx.lineTo(px, py);
            }
            for (let i = np - 1; i >= 0; i--) {
                const h = -halfAt(i) * scale + off;
                ctx.lineTo(dense[i * 2] + dx + nxs[i] * h, dense[i * 2 + 1] + dy + nys[i] * h);
            }
            ctx.closePath();
        };
        const pad = w * 4;
        iceTilePhases(size, x0 - pad, y0 - pad, x1 + pad, y1 + pad, (dx, dy) => {
            ctx.fillStyle = iceShade(86, aCleft);
            ribbon(dx, dy, 0, 1); ctx.fill();
            ctx.fillStyle = iceShade(255, aPlane);                       // the plane itself, reflecting alongside the cleft
            ribbon(dx, dy, w * 0.95, 0.42); ctx.fill();
        });
    }

    // Bubble trains. Spacing, size and sideways wander are all rolled per bubble, and a quarter of the trains collapse
    // into a tight cluster — a bubble train records how fast the front was moving, so it is never a bead chain.
    for (let t = 0; t < 80; t++) {
        const bx = hash01(t + seed, 81) * size, by = hash01(t, seed + 82) * size;
        const a = hash01(t + seed, 83) * Math.PI * 2;
        const n = 4 + Math.floor(hash01(t + seed, 84) * 9);
        const tight = hash01(t, seed + 86) > 0.75;
        const span = tight ? 3 + hash01(t + seed, 87) * 5 : 9 + hash01(t, seed + 85) * 16;
        const ca = Math.cos(a), sa = Math.sin(a), pxn = -sa, pyn = ca;
        const reach = span * n * 1.9 + 16;   // the per-bubble step can reach 1.85x 'span'; a box built on the MEAN
                                            // step lets a long train run outside it and lose its wrapped copy
        iceTilePhases(size, bx - reach, by - reach, bx + reach, by + reach, (dx, dy) => {
            let d = 0;
            for (let k = 0; k < n; k++) {
                d += span * (0.35 + hash01(t + seed, 100 + k) * 1.5);
                const j = (hash01(t, seed + 120 + k) - 0.5) * span * 1.4;
                const px = bx + dx + ca * d + pxn * j, py = by + dy + sa * d + pyn * j;
                const r = 1.0 + Math.pow(hash01(t + seed, 90 + k), 2.4) * 6.0;   // mostly tiny, occasionally one big one
                ctx.fillStyle = iceShade(112, 0.32);
                ctx.beginPath(); ctx.arc(px + r * 0.30, py + r * 0.36, r, 0, Math.PI * 2); ctx.fill();
                ctx.fillStyle = iceShade(255, 0.80);
                ctx.beginPath(); ctx.arc(px - r * 0.20, py - r * 0.24, r * 0.72, 0, Math.PI * 2); ctx.fill();
            }
        });
    }

    iceGrainField(ctx, size, seed, 400, 1100, [iceShade(255, 0.24), iceShade(200, 0.16)], 2.2);

    const texture = new THREE.CanvasTexture(canvas);
    texture.wrapS = texture.wrapT = THREE.RepeatWrapping;
    return texture;
}


// CO2 ICE — the Swiss-cheese terrain of Mars' south polar residual cap, which is dry ice and nothing else: flat-
// floored, STEEP-WALLED, round-lobed sublimation pits a few metres deep, growing a few metres a year, cut into a
// bright granular frost. The recognition is the pit shading and it depends on two things.
// First the sign: a HOLE lit from the upper left is dark on the upper-left inner wall and bright on the lower-right
// one — the opposite of a dome, and the reason a flipped crater photograph looks like a hill.
// Second the shape of the falloff. Each wall is an OFFSET RADIAL gradient clipped to the rim, so it is hard against
// the scarp (the clip) and fades inward (the gradient). The two versions that do not work: a linear gradient across
// the whole pit gives a soft dimple, and a dimple in frost reads as a water droplet on glass; and a constant-width
// band around the rim gives an annulus, and an annulus inside an outline reads as a droplet too.
// Pits sit on a JITTERED GRID rather than free hashed positions: free positions clump, and clumped pits read as
// spilled gravel instead of as a sublimating cap.
export function createDryIceFrost(baseHex) {
    const size = 1024;
    const canvas = document.createElement('canvas');
    canvas.width = canvas.height = size;
    const ctx = canvas.getContext('2d');
    const seed = hexSeed(baseHex);

    ctx.fillStyle = iceShade(246, 1);
    ctx.fillRect(0, 0, size, size);
    icePools(ctx, size, seed, 0, 9, 160, 320, 255, 0.40, 212, 0.28);
    iceGrainField(ctx, size, seed, 100, 1700,
        [iceShade(255, 0.55), iceShade(220, 0.40), iceShade(190, 0.28), iceShade(255, 0.30)], 3.0);

    const pit = (id, cx, cy, r) => {
        const pad = r * 1.5;
        const rim = (ox, oy, dx, dy) => iceBlobPath(ctx, seed, id, cx + ox, cy + oy, r, 0.30, 11, dx, dy);
        iceTilePhases(size, cx - pad, cy - pad, cx + pad, cy + pad, (dx, dy) => {
            ctx.save();
            rim(0, 0, dx, dy);
            ctx.clip();
            ctx.fillStyle = iceShade(234, 0.95);                          // flat floor, barely darker than the plain
            ctx.fillRect(cx + dx - pad, cy + dy - pad, pad * 2, pad * 2);
            // The walls are OFFSET RADIAL gradients clipped to the rim: sharp where they meet the scarp (the clip
            // does that) and fading inward (the gradient does that). A constant-width band would draw an annulus,
            // and an annulus inside an outline is a water droplet sitting on the frost, not a hole in it.
            const gs = ctx.createRadialGradient(cx + dx + r * 0.55, cy + dy + r * 0.55, r * 0.10,
                                                cx + dx + r * 0.55, cy + dy + r * 0.55, r * 1.65);
            gs.addColorStop(0, iceShade(118, 0)); gs.addColorStop(0.55, iceShade(118, 0.12)); gs.addColorStop(1, iceShade(118, 0.72));
            ctx.fillStyle = gs;                                           // shadowed wall under the UPPER-LEFT scarp
            ctx.fillRect(cx + dx - pad, cy + dy - pad, pad * 2, pad * 2);
            const gl = ctx.createRadialGradient(cx + dx - r * 0.55, cy + dy - r * 0.55, r * 0.10,
                                                cx + dx - r * 0.55, cy + dy - r * 0.55, r * 1.65);
            gl.addColorStop(0, iceShade(255, 0)); gl.addColorStop(0.60, iceShade(255, 0.10)); gl.addColorStop(1, iceShade(255, 0.62));
            ctx.fillStyle = gl;                                           // sunlit far wall
            ctx.fillRect(cx + dx - pad, cy + dy - pad, pad * 2, pad * 2);
            ctx.lineWidth = r * 0.13;                                     // the wall itself is near-vertical all the way
            ctx.strokeStyle = iceShade(150, 0.20);                        // round, so a narrow even band belongs here —
            rim(0, 0, dx, dy); ctx.stroke();                              // it is the crescent above that says which side
            ctx.restore();                                                // the sun is on
            ctx.lineWidth = 1.4;                                          // and one thin dark edge for the scarp, no
            ctx.strokeStyle = iceShade(150, 0.22);                        // bright ring: a lit ring all the way round
            rim(0, 0, dx, dy); ctx.stroke();                              // reads as a droplet sitting ON the frost
        });
    };

    // 8x8 jittered grid, ~62% occupied → an even scatter of large pits, none piled on another.
    const G = 8, gs = size / G;
    for (let j = 0; j < G; j++) for (let i = 0; i < G; i++) {
        const id = j * G + i;
        if (hash01(id + seed, 141) > 0.62) continue;
        pit(id, (i + 0.15 + hash01(id + seed, 142) * 0.7) * gs, (j + 0.15 + hash01(id, seed + 143) * 0.7) * gs,
            24 + hash01(id + seed, 144) * 36);
    }
    // 20x20 jittered grid for the young pits peppering the ground between the old ones.
    const S = 20, ss = size / S;
    for (let j = 0; j < S; j++) for (let i = 0; i < S; i++) {
        const id = 400 + j * S + i;
        if (hash01(id + seed, 151) > 0.36) continue;
        pit(id, (i + 0.1 + hash01(id + seed, 152) * 0.8) * ss, (j + 0.1 + hash01(id, seed + 153) * 0.8) * ss,
            4 + hash01(id + seed, 154) * 9);
    }

    iceGrainField(ctx, size, seed, 900, 1300, [iceShade(255, 0.34), iceShade(206, 0.22)], 2.4);

    const texture = new THREE.CanvasTexture(canvas);
    texture.wrapS = texture.wrapT = THREE.RepeatWrapping;
    return texture;
}


// METHANE ICE — Pluto's bladed terrain (Tartarus Dorsa), which is methane and which is the strangest surface anyone
// has photographed: fields of parallel knife-edged ridges hundreds of metres tall, all aligned, standing out of deep
// shadowed troughs. They are penitentes — what a high-altitude snowfield grows — at absurd scale.
// Three decisions carry the recognition, and all three were arrived at by discarding the obvious version:
//   * THE GROUND IS DARK and the blades are bright things standing on it. A bright ground with dark lines drawn on
//     it is corduroy, whichever way the shading runs.
//   * A blade is a FILLED SHAPE with a gradient across it — lit flank, crest, then a STEP down to the shaded flank —
//     plus a shadow cast onto the trough to its right. A stroked line has no thickness to be lit on one side of, and
//     a smooth ramp across the blade instead of a step gives satin.
//   * Blades are DISCRETE SEGMENTS with pointed tips, not continuous stripes running the height of the tile. This is
//     the one that matters most: continuous parallel bands read as wood grain no matter how they are shaded, because
//     nothing in them ever ends. A blade that stops, showing the trough floor behind its tip, reads as an object.
// Segments are placed along whole-tile lanes and drawn through iceTilePhases, so one crossing the top edge arrives at
// the bottom; the lane spacing divides 1024 exactly, so the field also wraps left to right.
export function createBladedFrost(baseHex) {
    const size = 1024;
    const canvas = document.createElement('canvas');
    canvas.width = canvas.height = size;
    const ctx = canvas.getContext('2d');
    const seed = hexSeed(baseHex);
    // The trough floor, and the one number to be careful with. It has to sit clearly below the crest (255) or the
    // blades stop reading as objects standing on something — but it is also the tile's dominant area, so it sets the
    // kube's average brightness. At 150 the field looked right and Methane Ice rendered at 59% of its catalogue blue,
    // which is not what the catalogue asked for; 184 keeps the full crest-to-trough range and lands the mean at 69%.
    const GROUND = 184;

    ctx.fillStyle = iceShade(GROUND, 1);
    ctx.fillRect(0, 0, size, size);
    icePools(ctx, size, seed, 500, 10, 120, 280, 255, 0.22, 132, 0.28);   // uneven trough floor, before the blades

    const lanes = 16, gap = size / lanes, STEP = 18;
    // One blade SEGMENT: from y0, 'len' long, on the lane centred at 'nom'. Half-width tapers to a point at both
    // ends, so the blade has tips you can see past. Drawn through iceTilePhases on its own bounding box, so a blade
    // that runs off the top of the tile arrives at the bottom whole rather than being cut.
    const blade = (key, nom, y0, len, half, prom) => {
        const cyc = 1 + Math.floor(hash01(key + seed, 161) * 2);
        const amp = 2 + hash01(key + seed, 162) * 6;              // BARELY wander: a penitente is a straight knife, and
        const ph = hash01(key + seed, 163) * Math.PI * 2;         // a wandering one reads as a fold of cloth
        const xAt = (y) => nom + Math.sin((y / size) * Math.PI * 2 * cyc + ph) * amp;
        const halfAt = (t) => half * Math.pow(Math.sin(Math.PI * Math.min(1, Math.max(0, t))), 0.28);
        const N = Math.max(6, Math.round(len / STEP));   // a fixed COUNT, not a fixed increment: both edges then
        const shape = (dx, dy, inner, outer, grow) => {  // land exactly on the tips whatever the blade's length is
            ctx.beginPath();
            for (let k = 0; k <= N; k++) {
                const t = k / N, y = y0 + len * t, h = halfAt(t) * grow;
                const x = xAt(y) + h * inner + dx;
                if (k === 0) ctx.moveTo(x, y + dy); else ctx.lineTo(x, y + dy);
            }
            for (let k = N; k >= 0; k--) {
                const t = k / N, y = y0 + len * t;
                ctx.lineTo(xAt(y) + halfAt(t) * grow * outer + dx, y + dy);
            }
            ctx.closePath();
        };
        const bb = [nom - half - amp - 4, y0 - 4, nom + half * 1.1 + gap * 0.4 + amp + 4, y0 + len + 4];
        iceTilePhases(size, bb[0], bb[1], bb[2], bb[3], (dx, dy) => {
            shape(dx, dy, 0.5, 1 + gap * 0.4 / half, 1);          // shadow cast right, onto the trough floor
            const gsh = ctx.createLinearGradient(nom + half * 0.5 + dx, 0, nom + half + gap * 0.4 + dx, 0);
            gsh.addColorStop(0, iceShade(74, 0.58 * prom));
            gsh.addColorStop(1, iceShade(74, 0));
            ctx.fillStyle = gsh;
            ctx.fill();

            shape(dx, dy, -1, 1, 1);                              // the blade
            const gx = ctx.createLinearGradient(nom - half + dx, 0, nom + half + dx, 0);
            gx.addColorStop(0.00, iceShade(190, 0.55 * prom));    // foot of the lit flank, rising out of the trough
            gx.addColorStop(0.30, iceShade(250, 0.92 * prom));
            gx.addColorStop(0.42, iceShade(255, 1.00 * prom));    // the crest, edge-on to the sun
            gx.addColorStop(0.47, iceShade(86, 0.88 * prom));     // THE KNIFE EDGE — a step, not a ramp
            gx.addColorStop(0.80, iceShade(80, 0.70 * prom));
            gx.addColorStop(1.00, iceShade(104, 0.44 * prom));
            ctx.fillStyle = gx;
            ctx.fill();

            ctx.fillStyle = iceShade(255, 0.88 * prom);           // hard crest highlight, tapering with the blade
            shape(dx, dy, -0.12, 0.02, 1);
            ctx.fill();
        });
    };
    // Lanes hold two to four separate blades with gaps between them, and every third lane also carries a thin infill
    // blade off-centre. A full second set at half spacing would be 32 equal lanes, i.e. pinstripes.
    for (let l = 0; l < lanes; l++) {
        const nom = l * gap + gap * 0.5 + (hash01(l + seed, 166) - 0.5) * gap * 0.5;
        const n = 2 + Math.floor(hash01(l + seed, 172) * 3);
        for (let s = 0; s < n; s++) {
            const key = l * 7 + s;
            const y0 = (s / n + hash01(key + seed, 173) * 0.6 / n) * size;
            const len = size / n * (0.42 + hash01(key + seed, 174) * 0.44);
            blade(key, nom, y0, len, gap * (0.22 + hash01(key, seed + 170) * 0.13),
                  0.66 + hash01(key, seed + 169) * 0.34);
        }
        if (l % 3 === 0) {
            const key = l * 7 + 400;
            blade(key, nom + gap * (0.42 + hash01(key + seed, 175) * 0.3),
                  hash01(key + seed, 176) * size * 0.7, size * (0.16 + hash01(key + seed, 177) * 0.24),
                  gap * 0.10, 0.42);
        }
    }

    // Transverse notches — the cross-fractures that chop a penitente field into individual blades.
    ctx.lineCap = 'round';
    for (let i = 0; i < 210; i++) {
        const nx = hash01(i + seed, 181) * size, ny = hash01(i, seed + 182) * size;
        const len = 8 + hash01(i + seed, 183) * 26;
        const a = (hash01(i + seed, 184) - 0.5) * 0.7 + Math.PI / 2;
        const ex = Math.cos(a) * len, ey = Math.sin(a) * len;
        iceTilePhases(size, Math.min(nx, nx + ex) - 4, Math.min(ny, ny + ey) - 4,
                            Math.max(nx, nx + ex) + 4, Math.max(ny, ny + ey) + 4, (dx, dy) => {
            ctx.strokeStyle = hash01(i, seed + 187) > 0.5 ? iceShade(112, 0.26 + hash01(i, seed + 185) * 0.26)
                                                          : iceShade(255, 0.28 + hash01(i, seed + 185) * 0.32);
            ctx.lineWidth = 1.4 + hash01(i, seed + 186) * 2.6;
            ctx.beginPath(); ctx.moveTo(nx + dx, ny + dy); ctx.lineTo(nx + dx + ex, ny + dy + ey); ctx.stroke();
        });
    }

    icePools(ctx, size, seed, 200, 7, 190, 330, 255, 0.22, 162, 0.22);   // after the blades, so it shades the field
    iceGrainField(ctx, size, seed, 700, 1300, [iceShade(255, 0.26), iceShade(168, 0.18)], 2.4);

    const texture = new THREE.CanvasTexture(canvas);
    texture.wrapS = texture.wrapT = THREE.RepeatWrapping;
    return texture;
}


// AMMONIA ICE — the fresh ammonia-hydrate frost around Charon's young craters and along Enceladus' tiger stripes: a
// bright, MATTE, sugar-crystal blanket. It is the only one of the five that is not glassy (roughness 0.30, the
// highest here), so it gets no mirror slivers, no bubbles and no cracks; what it gets is CRUMB, and crumb has to be
// PACKED — hoar frost grows crystal against crystal, so an even scatter of specks on a clean ground reads as paper.
// Crystals are therefore placed in CLUSTERS: a hashed cluster centre, then a handful of crystals around it, so the
// surface has aggregates and bare patches the way a real frost blanket does. Each crystal is a small irregular facet
// with a contact shadow down-right of it; that pairing is what makes a speckle read as three-dimensional. A scatter
// of hard white pinpoints on top is the glint off individual crystal faces.
export function createSugarFrost(baseHex) {
    const size = 1024;
    const canvas = document.createElement('canvas');
    canvas.width = canvas.height = size;
    const ctx = canvas.getContext('2d');
    const seed = hexSeed(baseHex);

    ctx.fillStyle = iceShade(236, 1);
    ctx.fillRect(0, 0, size, size);
    icePools(ctx, size, seed, 0, 10, 160, 340, 255, 0.42, 186, 0.36);    // the blanket is drifted, not poured
    icePools(ctx, size, seed, 70, 12, 40, 120, 255, 0.30, 198, 0.26);

    // Fine sugar packed BETWEEN the crystals. Dense on purpose: leave the ground smooth and the crystals above read
    // as gravel scattered on paper rather than as the top of a frost blanket.
    iceGrainField(ctx, size, seed, 100, 1900,
        [iceShade(255, 0.55), iceShade(255, 0.36), iceShade(158, 0.38), iceShade(128, 0.32), iceShade(202, 0.32)], 2.4);

    // Clustered crystals. Shadow layer then facet layer per pass, so a crystal never shadows itself but later passes
    // pile over earlier ones the way drifted frost does. Batched: two fills per pass for ~1200 crystals.
    const CLUSTERS = 250, PER = 3;
    for (let p = 0; p < 2; p++) {
        for (let layer = 0; layer < 2; layer++) {
            ctx.beginPath();
            for (let ci = 0; ci < CLUSTERS; ci++) {
                const cid = p * 3313 + ci;
                const ccx = hash01(cid + seed, 231) * size, ccy = hash01(cid, seed + 232) * size;
                const spread = 7 + hash01(cid + seed, 233) * 20;
                for (let q = 0; q < PER; q++) {
                    const gi = cid * 11 + q;
                    const gx = ccx + (hash01(gi + seed, 241) - 0.5) * spread * 2;
                    const gy = ccy + (hash01(gi, seed + 242) - 0.5) * spread * 2;
                    const r = 3.4 + hash01(gi + seed, 243) * 7.6;
                    const sx = layer ? 0 : r * 0.5, sy = layer ? 0 : r * 0.58;
                    const put = (ox2, oy2) => {
                        for (let k = 0; k < 5; k++) {
                            const th = k * Math.PI * 0.4 + hash01(gi + seed, 250 + k) * 0.8;
                            const rr = r * (0.5 + hash01(gi, seed + 260 + k) * 0.75);
                            const x = gx + sx + ox2 + Math.cos(th) * rr, y = gy + sy + oy2 + Math.sin(th) * rr;
                            if (k === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y);
                        }
                        ctx.closePath();
                    };
                    put(0, 0);
                    const wx = gx + r * 2 > size, wy = gy + r * 2 > size, lx = gx - r * 2 < 0, ly = gy - r * 2 < 0;
                    if (wx) put(-size, 0);
                    if (lx) put(size, 0);
                    if (wy) put(0, -size);
                    if (ly) put(0, size);
                    if (wx && wy) put(-size, -size);
                    if (lx && ly) put(size, size);
                    if (wx && ly) put(-size, size);
                    if (lx && wy) put(size, -size);
                }
            }
            ctx.fillStyle = layer ? iceShade(255, 0.62) : iceShade(108, 0.42);
            ctx.fill();
        }
    }

    // Crystal glints — hard, tiny, pure white. Matte overall, but a facet that happens to face the sun still catches
    // it, and this is the only thing in the six that says "individual crystals" at a glance.
    iceGrainField(ctx, size, seed, 800, 420, [iceShade(255, 0.95)], 1.0);

    const texture = new THREE.CanvasTexture(canvas);
    texture.wrapS = texture.wrapT = THREE.RepeatWrapping;
    return texture;
}


// NITROGEN ICE — Sputnik Planitia. Pluto's heart is a sheet of nitrogen ice warm enough to CONVECT, and its surface
// is the only place anyone has photographed convection cells directly: 20–40 km polygons, gently domed so their
// middles are pale, separated by narrow sunken troughs that collect dark debris and rafted water-ice hills.
// That mosaic is the recognition, which is why this one is a lattice and not a noise field. The lattice is periodic
// by MODULAR jitter rather than by pinning (see the family note): the point at i = n is literally the point at i = 0
// one tile over, so the border cells are as irregular as the middle ones instead of flattened along the seam. Cell
// edges bow through a control point keyed on the EDGE, so the two cells sharing one bow together and never gap.
// The domes are kept SHALLOW and the trough is a single narrow valley: a strong dome plus a doubled bright rim turns
// the mosaic into quilting, which is the failure mode this shape falls into if you let it.
export function createConvectionIce(baseHex) {
    const size = 1024;
    const canvas = document.createElement('canvas');
    canvas.width = canvas.height = size;
    const ctx = canvas.getContext('2d');
    const seed = hexSeed(baseHex);

    ctx.fillStyle = iceShade(242, 1);
    ctx.fillRect(0, 0, size, size);

    const n = 4, cell = size / n;                     // four cells across a kube face — the scale Sputnik reads at
    const m = (i) => ((i % n) + n) % n;                // modular index: this is what makes the lattice repeat
    const pX = (i, j) => i * cell + (hash01(m(i) + seed, m(j)) - 0.5) * cell * 0.62;
    const pY = (i, j) => j * cell + (hash01(m(i), m(j) + seed) - 0.5) * cell * 0.62;
    const ctrl = (i, j, i2, j2, out) => {
        const ax = pX(i, j), ay = pY(i, j), bx = pX(i2, j2), by = pY(i2, j2);
        const dx = bx - ax, dy = by - ay, L = Math.hypot(dx, dy) || 1;
        const k = (hash01(m(i) * 5 + m(i2) + seed, m(j) * 5 + m(j2)) - 0.5) * cell * 0.34;
        out[0] = (ax + bx) * 0.5 - dy / L * k;
        out[1] = (ay + by) * 0.5 + dx / L * k;
    };
    const cp = [0, 0];
    const cellPath = (i, j) => {
        ctx.beginPath();
        ctx.moveTo(pX(i, j), pY(i, j));
        ctrl(i, j, i + 1, j, cp);         ctx.quadraticCurveTo(cp[0], cp[1], pX(i + 1, j), pY(i + 1, j));
        ctrl(i + 1, j, i + 1, j + 1, cp); ctx.quadraticCurveTo(cp[0], cp[1], pX(i + 1, j + 1), pY(i + 1, j + 1));
        ctrl(i + 1, j + 1, i, j + 1, cp); ctx.quadraticCurveTo(cp[0], cp[1], pX(i, j + 1), pY(i, j + 1));
        ctrl(i, j + 1, i, j, cp);         ctx.quadraticCurveTo(cp[0], cp[1], pX(i, j), pY(i, j));
        ctx.closePath();
    };

    // -1 … n INCLUSIVE, both axes. The obvious range (0 … n-1) leaves the tile's right and bottom margins bare, and
    // -1 … n-1 still does: cell n starts at n*cell PLUS its jitter, and when that jitter is negative the cell pokes
    // back into the canvas by up to a third of a cell. The strip it should have covered falls through to the flat
    // base fill, which is a hard-edged band along the seam — the one thing this whole family is built to avoid.
    // The extra ring costs 11 of 36 cells and is entirely clipped away where it is not needed.
    for (let j = -1; j <= n; j++) {
        for (let i = -1; i <= n; i++) {
            const cx = (pX(i, j) + pX(i + 1, j + 1)) * 0.5, cy = (pY(i, j) + pY(i + 1, j + 1)) * 0.5;
            const dome = 0.6 + hash01(m(i) + seed, m(j) + seed) * 0.4;
            ctx.save();
            cellPath(i, j);
            ctx.clip();
            const g = ctx.createRadialGradient(cx - cell * 0.14, cy - cell * 0.14, 0, cx, cy, cell * 0.80);
            g.addColorStop(0.00, iceShade(255, 0.55 * dome));   // the middle is rising, and is the palest part of the cell
            g.addColorStop(0.60, iceShade(255, 0.20 * dome));
            g.addColorStop(1.00, iceShade(186, 0.34));          // sinking edge
            ctx.fillStyle = g;
            // The whole canvas, not a box around the cell centre: a jittered cell can reach further from that centre
            // than any fixed box, and the uncovered corner shows up as a hard rectangle that looks exactly like a
            // tile seam. The clip is what bounds this fill; the rect only has to be at least as big as the clip.
            ctx.fillRect(0, 0, size, size);
            // Mottling inside the cell — the uneven surface of one convecting slab. Soft pools, deliberately NOT
            // concentric rings and not arcs about the dome: a ring reads as a target and an arc reads as a brush
            // stroke, and either one in the middle of every cell makes the tile look printed rather than convected.
            for (let k = 0; k < 14; k++) {
                const ox = (hash01(m(i) * 7 + k + seed, m(j) * 7 + k) - 0.5) * cell * 1.25;
                const oy = (hash01(m(i) * 7 + k, m(j) * 7 + k + seed) - 0.5) * cell * 1.25;
                const rr = cell * (0.10 + hash01(m(i) * 11 + k + seed, m(j) * 11 + k) * 0.34);
                const up = hash01(m(i) + k, m(j) + k + seed) > 0.5;
                const g2 = ctx.createRadialGradient(cx + ox, cy + oy, 0, cx + ox, cy + oy, rr);
                g2.addColorStop(0, iceShade(up ? 255 : 196, up ? 0.30 : 0.24));
                g2.addColorStop(1, iceShade(up ? 255 : 196, 0));
                ctx.fillStyle = g2;
                ctx.fillRect(cx + ox - rr, cy + oy - rr, rr * 2, rr * 2);
            }
            iceGrainField(ctx, size, seed, 1000 + (m(i) * 4 + m(j)) * 137, 220,
                          [iceShade(200, 0.22), iceShade(255, 0.28)], 3.0);
            ctx.restore();
        }
    }

    // Troughs, drawn after every cell so no dome paints over one. A wide soft valley and a narrow dark floor — and
    // no offset bright rim, which is what turned this into stitching on the previous pass.
    ctx.lineJoin = 'round'; ctx.lineCap = 'round';
    for (let j = -1; j <= n; j++) {
        for (let i = -1; i <= n; i++) {
            ctx.strokeStyle = iceShade(196, 0.24); ctx.lineWidth = 22;
            cellPath(i, j); ctx.stroke();
            ctx.strokeStyle = iceShade(140, 0.24); ctx.lineWidth = 7;
            cellPath(i, j); ctx.stroke();
            ctx.strokeStyle = iceShade(96, 0.34); ctx.lineWidth = 2.2;
            cellPath(i, j); ctx.stroke();
        }
    }

    // Rafted hills — the dark blocks that collect in the troughs. Walked along each cell's corner-to-corner chords
    // with heavy jitter, so they follow the mosaic without lining up like a dotted rule.
    for (let j = -1; j <= n; j++) {
        for (let i = -1; i <= n; i++) {
            const ax = pX(i, j), ay = pY(i, j), bx = pX(i + 1, j), by = pY(i + 1, j);
            for (let k = 0; k < 10; k++) {
                if (hash01(m(i) * 23 + k + seed, m(j) * 23 + k) > 0.5) continue;
                const t = (k + 0.5) / 10 + (hash01(m(i) * 29 + k, m(j) * 29 + k + seed) - 0.5) * 0.09;
                const hx = ax + (bx - ax) * t + (hash01(m(i) * 13 + k + seed, m(j) * 13 + k) - 0.5) * 34;
                const hy = ay + (by - ay) * t + (hash01(m(i) * 13 + k, m(j) * 13 + k + seed) - 0.5) * 34;
                const r = 1.4 + hash01(m(i) + k + seed, m(j) + k) * 4.0;
                ctx.fillStyle = iceShade(104, 0.42);
                ctx.beginPath(); ctx.ellipse(hx, hy, r, r * 0.7, hash01(k + seed, m(i)) * 3.14, 0, Math.PI * 2); ctx.fill();
                ctx.fillStyle = iceShade(255, 0.40);
                ctx.beginPath(); ctx.arc(hx - r * 0.36, hy - r * 0.42, r * 0.4, 0, Math.PI * 2); ctx.fill();
            }
        }
    }

    // Nitrogen ice is the glassiest of the six (roughness 0.18): a sparse hard sparkle, not a frost blanket.
    iceGrainField(ctx, size, seed, 300, 700, [iceShade(255, 0.40), iceShade(198, 0.18)], 2.0);

    const texture = new THREE.CanvasTexture(canvas);
    texture.wrapS = texture.wrapT = THREE.RepeatWrapping;
    return texture;
}


// HYDROCARBON ICE — tholins. Not a clear ice at all: the dark organic mantle comet 67P/Churyumov–Gerasimenko wears
// over its ice, and the same stuff that darkens Titan. What Rosetta's OSIRIS frames show from a few kilometres up is
// a surface that is DARK, GRITTY AND STREWN: a fine sooty mantle, thick in places and stripped in others, with the
// stripped ground standing a little brighter, and the whole thing littered with dark pebbles and boulders.
// The recognition is that debris field plus the patchiness, and it sits between two failure modes. All-soft edges
// give watercolour: overlapping washes with round blobs floating in them. All-hard edges give desert camouflage.
// So a mantle unit is a soft apron with ONE hard-edged core inside it, and the debris is small, numerous and
// angular rather than large, round and dark.
//
// Tinting: still multiplicative, still never bakes baseHex — a canvas built AROUND #7A6A55 would come out hex-
// squared at roughly #3A2F22 and lose the substance entirely. What it does instead of pure grey is BIAS: highlights
// pass through a warm near-white (255,242,224) and the tar through a warm dark (70,58,48), so the multiply shifts
// hue toward ruddy in the light and toward black-brown in the tar without ever replacing the catalogue colour.
// Nothing here is above white, so the brightest thing on the kube is exactly its own hex.
export function createTholinCrust(baseHex) {
    const size = 1024;
    const canvas = document.createElement('canvas');
    canvas.width = canvas.height = size;
    const ctx = canvas.getContext('2d');
    const seed = hexSeed(baseHex);

    ctx.fillStyle = iceRGBA(243, 236, 227, 1);
    ctx.fillRect(0, 0, size, size);

    // Mantle units. A soft body with ONE hard-edged core rather than a hard silhouette: an outer wash for the thin
    // apron of dust that feathers away, and a smaller sharp lobe inside it for the part that is thick enough to have
    // a boundary. A hard edge all the way round gives desert camouflage; no edge at all gives watercolour.
    const patch = (id, cx, cy, r, jitter, lobes, body, core, scarp) => {
        const pad = r * 2.5;      // MUST exceed the outer blob's max reach, r*1.45*(1+jitter*1.2) — a fill that stops
                                  // short inside a clip leaves a hard-edged rectangle that reads as a tile seam
        iceTilePhases(size, cx - pad, cy - pad, cx + pad, cy + pad, (dx, dy) => {
            ctx.save();
            iceBlobPath(ctx, seed, id, cx, cy, r * 1.45, jitter * 1.2, lobes, dx, dy);
            ctx.clip();
            const g = ctx.createRadialGradient(cx + dx, cy + dy, r * 0.25, cx + dx, cy + dy, r * 1.45);
            g.addColorStop(0, body); g.addColorStop(1, iceRGBA(0, 0, 0, 0));
            ctx.fillStyle = g;
            ctx.fillRect(cx + dx - pad, cy + dy - pad, pad * 2, pad * 2);
            ctx.restore();
            ctx.save();
            iceBlobPath(ctx, seed, id + 900, cx, cy, r * 0.66, jitter, lobes, dx, dy);
            ctx.clip();
            ctx.fillStyle = core;
            ctx.fillRect(cx + dx - pad, cy + dy - pad, pad * 2, pad * 2);
            if (scarp) {                                           // the up-sun lip where the mantle has pulled back;
                ctx.strokeStyle = iceRGBA(255, 248, 236, 0.30);    // faint, or it reads as a snail trail
                ctx.lineWidth = 4;
                iceBlobPath(ctx, seed, id + 900, cx + 4, cy + 4, r * 0.66, jitter, lobes, dx, dy);
                ctx.stroke();
            }
            ctx.restore();
        });
    };

    for (let i = 0; i < 11; i++)                                   // thick mantle
        patch(i + 40, hash01(i + seed, 301) * size, hash01(i, seed + 302) * size, 100 + hash01(i + seed, 303) * 140,
              0.30, 20, iceRGBA(104, 87, 72, 0.40), iceRGBA(92, 77, 64, 0.26), false);
    for (let i = 0; i < 22; i++)                                   // the darkest pockets inside it
        patch(i + 70, hash01(i + seed, 306) * size, hash01(i, seed + 307) * size, 30 + hash01(i + seed, 308) * 62,
              0.36, 16, iceRGBA(64, 52, 42, 0.34), iceRGBA(52, 43, 36, 0.28), false);
    for (let i = 0; i < 15; i++)                                   // stripped ground, brighter and ruddier
        patch(i + 120, hash01(i + seed, 311) * size, hash01(i, seed + 312) * size, 34 + hash01(i + seed, 313) * 78,
              0.30, 18, iceRGBA(255, 242, 224, 0.26), iceRGBA(255, 246, 232, 0.22), true);

    // Dust lineation. Whole-cycle sines across the tile — the createDamascusPattern rule — so a streak leaves the
    // right edge exactly where it entered the left. Few and WIDE: a comb of thin lines reads as wood grain, and this
    // is gas-drag lineation across a dark blanket, not a plank.
    for (let s = 0; s < 12; s++) {
        const y0 = hash01(s + seed, 321) * size;
        const cyc = 1 + Math.floor(hash01(s + seed, 322) * 2);
        const a1 = 26 + hash01(s + seed, 323) * 70, p1 = hash01(s + seed, 324) * Math.PI * 2;
        const a2 = 8 + hash01(s + seed, 325) * 22, p2 = hash01(s + seed, 326) * Math.PI * 2;
        const w = 26 + hash01(s, seed + 327) * 58;
        ctx.strokeStyle = hash01(s, seed + 330) > 0.45 ? iceRGBA(74, 62, 52, 0.16 + hash01(s, seed + 328) * 0.12)
                                                       : iceRGBA(255, 244, 228, 0.12 + hash01(s, seed + 329) * 0.10);
        ctx.lineWidth = w;
        for (let ph = -1; ph <= 1; ph++) {              // the streak wanders in y, so it needs the vertical wrap
            ctx.beginPath();
            // Sampled from BEYOND both edges, not from 0 to size. Two reasons, and the second is the subtle one:
            // the step must divide 1024 or the last sample lands short of the right edge and the streak simply
            // stops; and a wide stroke's end cap is square to the LAST CHORD, which is not the chord the next
            // tile's first cap is square to, so two abutting caps leave a wedge-shaped notch on the seam. Running
            // the polyline past both edges puts every cap off-canvas, where it cannot be seen. y is periodic, so
            // the overhang is exactly what the neighbouring tile draws there anyway.
            for (let x = -64; x <= size + 64; x += 32) {
                const t = (x / size) * Math.PI * 2;
                const y = y0 + Math.sin(t * cyc + p1) * a1 + Math.sin(t * cyc * 2 + p2) * a2 + ph * size;
                if (x === -64) ctx.moveTo(x, y); else ctx.lineTo(x, y);
            }
            ctx.stroke();
        }
    }

    // The debris field: dark angular pebbles, lit on the upper-left shoulder and shadowed on the ground down-right.
    // 67P's surface is covered in them, and they are what make this read as a comet crust rather than as mud.
    for (let c = 0; c < 620; c++) {
        const cx = hash01(c + seed, 341) * size, cy = hash01(c, seed + 342) * size;
        const R = 2.0 + Math.pow(hash01(c + seed, 343), 2.0) * 11;
        const rock = (dx, dy, ox2, oy2, scale) => {
            ctx.beginPath();
            for (let k = 0; k < 6; k++) {
                const th = k * Math.PI / 3 + hash01(c + seed, 350 + k) * 0.8;
                const rr = R * scale * (0.6 + hash01(c, seed + 360 + k) * 0.7);
                const x = cx + dx + ox2 + Math.cos(th) * rr, y = cy + dy + oy2 + Math.sin(th) * rr;
                if (k === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y);
            }
            ctx.closePath();
        };
        iceTilePhases(size, cx - R * 2, cy - R * 2, cx + R * 2, cy + R * 2, (dx, dy) => {
            ctx.fillStyle = iceRGBA(40, 33, 28, 0.34);
            rock(dx, dy, R * 0.35, R * 0.40, 1.0); ctx.fill();       // contact shadow on the ground
            ctx.fillStyle = iceRGBA(72, 60, 50, 0.62);
            rock(dx, dy, 0, 0, 1.0); ctx.fill();                     // the pebble
            ctx.fillStyle = iceRGBA(206, 192, 174, 0.26);
            rock(dx, dy, -R * 0.22, -R * 0.26, 0.46); ctx.fill();    // dust-free top face catching the sun
        });
    }

    // Soot. Fine, dark and dense — this is what stops the patches reading as camouflage and makes the surface grit.
    iceGrainField(ctx, size, seed, 500, 2200,
        [iceRGBA(48, 40, 34, 0.26), iceRGBA(88, 73, 60, 0.20), iceRGBA(30, 25, 21, 0.18),
         iceRGBA(255, 244, 228, 0.22), iceRGBA(126, 106, 86, 0.18)], 2.4);

    const texture = new THREE.CanvasTexture(canvas);
    texture.wrapS = texture.wrapT = THREE.RepeatWrapping;
    return texture;
}


// A two-layer FR-4 board with a green LPI solder mask and a HASL finish — the reference is an Arduino-class board
// seen face-on: ground pour over most of the substrate, Manhattan-plus-45-degree routing with a clearance moat
// round every net, plated through-hole vias, 0805 discrete footprints, and a fine-pitch QFN with a thermal pad.
// The routing geometry is what carries the recognition, so it is drawn at board scale, not as ornament.
//
// NEUTRAL by construction, even though it has its own cache slot. applyMaterial multiplies this canvas by the
// kube hex on the GPU and a multiply can only DARKEN: with #2E7D4F's red channel at 46/255 there is NO canvas
// value that yields a warm copper trace. So the canvas is a light map, not paint — every scrap of green comes
// from the hex, and the substrate/pour/trace/silkscreen separate by VALUE, which is exactly how a real masked
// board separates them (the copper is under the mask; only the pads are bare). The one hue move a multiply
// allows is pulling a channel DOWN, so exposed metal is drawn as a warm white with the blue removed, landing it
// on the yellow side of the hex — bare tinned copper.
export function createCircuitBoardTexture(baseHex) {
    const size = 1024;
    const canvas = document.createElement('canvas');
    canvas.width = canvas.height = size;
    const ctx = canvas.getContext('2d');
    const seed = hexSeed(baseHex);

    const FR4      = 'rgb(110, 116, 104)';   // bare substrate — what shows in the clearance moats
    const POUR     = 'rgb(158, 164, 150)';   // copper ground pour, under the mask
    const POUR_HI  = 'rgb(180, 186, 170)';   // the second plane of a split pour
    const TRACE    = 'rgb(198, 204, 184)';   // a routed net, under the mask
    const TRACE_HI = 'rgb(228, 234, 210)';
    const TRACE_LO = 'rgb(86, 90, 80)';
    const PAD      = 'rgb(255, 238, 176)';   // exposed metal: blue pulled down, so it lands warm of the hex
    const PAD_HI   = 'rgb(255, 251, 216)';
    const HOLE     = 'rgb(22, 24, 20)';
    const SILK     = 'rgb(255, 255, 255)';   // white legend — the brightest thing on the board, i.e. the hex itself
    const SILK_LO  = 'rgb(84, 88, 80)';

    // Safari 15.6 has no ctx.roundRect (16.4+), and this file has to run there — see the import note at the top.
    const rrect = (x, y, w, h, r) => {
        ctx.beginPath();
        ctx.moveTo(x + r, y);
        ctx.lineTo(x + w - r, y); ctx.quadraticCurveTo(x + w, y, x + w, y + r);
        ctx.lineTo(x + w, y + h - r); ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
        ctx.lineTo(x + r, y + h); ctx.quadraticCurveTo(x, y + h, x, y + h - r);
        ctx.lineTo(x, y + r); ctx.quadraticCurveTo(x, y, x + r, y);
        ctx.closePath();
    };

    ctx.fillStyle = POUR;
    ctx.fillRect(0, 0, size, size);

    // Glass-cloth weave. On a real board the FR-4 basket prints faintly through the mask; at 16px pitch it is the
    // sub-detail you only notice up close. Whole-tile pitch divides 1024, so it repeats without a step.
    ctx.globalAlpha = 0.055;
    for (let i = 0; i < 64; i++) {
        ctx.fillStyle = (i & 1) ? POUR_HI : FR4;
        ctx.fillRect(i * 16, 0, 8, size);
        ctx.fillRect(0, i * 16 + 8, size, 8);
    }
    ctx.globalAlpha = 1;

    // Split planes: a second pour at a slightly different potential, fenced off by a moat of bare substrate.
    for (let p = 0; p < 3; p++) {
        const w = 240 + hash01(p + seed, 51) * 380, h = 200 + hash01(p + seed, 52) * 340;
        const x = hash01(p + seed, 53) * size, y = hash01(p + seed, 54) * size;
        drawWrapped(size, x, y, Math.max(w, h) * 0.5 + 20, (px, py) => {
            ctx.fillStyle = FR4;    rrect(px - w / 2 - 9, py - h / 2 - 9, w + 18, h + 18, 26); ctx.fill();
            ctx.fillStyle = POUR_HI; rrect(px - w / 2, py - h / 2, w, h, 20); ctx.fill();
        });
    }

    // ── Routing ─────────────────────────────────────────────────────────────────────────────────────────────
    // Every full-length net enters and leaves the tile at the SAME coordinate on opposite edges: the lateral
    // offsets of its runs start at 0, wander, and are forced back to 0, so a trace that walks off the right edge
    // walks back on at the left one exactly where it left. Runs are joined by 45-degree diagonals, never by a
    // curve, which is what makes it read as routed copper rather than as wire. Both ends overhang the edge by
    // 24px so the offset shadow/highlight passes (drawn 2.5px down-right, 1.5px up-left) still cover the seam.
    const RUNS = 5, run = size / RUNS;
    const lanePts = (c, k, vertical) => {
        const off = [0];
        for (let i = 1; i < RUNS; i++) off.push(Math.round((hash01(k + seed, 40 + i) - 0.5) * 18) * 8);   // ±72, so a diagonal is at most 144 of a 204.8 run
        off.push(0);
        const pts = [];
        const P = (a, b) => pts.push(vertical ? [c + b, a] : [a, c + b]);
        P(-24, 0);
        for (let i = 0; i < RUNS; i++) {
            const d = Math.abs(off[i + 1] - off[i]);
            P((i + 1) * run - d, off[i]);
            P((i + 1) * run, off[i + 1]);
        }
        P(size + 24, 0);
        return pts;
    };
    const strokePts = (pts, w, style, dx, dy) => {
        ctx.strokeStyle = style; ctx.lineWidth = w;
        ctx.beginPath();
        ctx.moveTo(pts[0][0] + dx, pts[0][1] + dy);
        for (let i = 1; i < pts.length; i++) ctx.lineTo(pts[i][0] + dx, pts[i][1] + dy);
        ctx.stroke();
    };

    ctx.lineCap = 'butt';           // a rounded cap would bulge past the seam and break the join
    ctx.lineJoin = 'round';
    const nets = [];
    for (let k = 0; k < 19; k++) {
        const vertical = k >= 11;
        const idx = vertical ? k - 11 : k, n = vertical ? 8 : 11;
        const c = (idx + 0.5) * (size / n) + (hash01(k + seed, 7) - 0.5) * (size / n) * 0.5;
        const h = hash01(k + seed, 8);
        nets.push({ pts: lanePts(c, k, vertical), w: h > 0.86 ? 26 + h * 12 : 7 + h * 9 });   // a couple of fat power rails among the signal traces
    }

    // The QFN and its fanout. Central by construction, so nothing here can reach a seam.
    const bx = size / 2, by = size / 2, qs = 152, PIN = 11, pitch = (2 * qs) / PIN;
    const padPos = [];
    for (let s = 0; s < 4; s++) {
        for (let i = 0; i < PIN; i++) {
            const t = -qs + (i + 0.5) * pitch;
            const p = s === 0 ? [bx + t, by - qs, 0, -1] : s === 1 ? [bx + qs, by + t, 1, 0]
                    : s === 2 ? [bx + t, by + qs, 0, 1] : [bx - qs, by + t, -1, 0];
            padPos.push(p);
            if (hash01(s * 32 + i + seed, 61) < 0.42) continue;                      // not every pin is routed out
            const len = 46 + hash01(s * 32 + i + seed, 62) * 120, sw = 34;
            const ax = p[0] + p[2] * 22, ay = p[1] + p[3] * 22;
            const ex = ax + p[2] * len, ey = ay + p[3] * len;
            const sgn = hash01(s * 32 + i + seed, 63) > 0.5 ? 1 : -1;                // then a 45-degree break into the fabric
            const fin = p[2] ? [ex + p[2] * sw, ey + sgn * sw] : [ex + sgn * sw, ey + p[3] * sw];
            nets.push({ pts: [[ax, ay], [ex, ey], fin], w: 6 + hash01(s * 32 + i + seed, 64) * 3 });
        }
    }

    for (const L of nets) strokePts(L.pts, L.w + 15, FR4, 0, 0);              // clearance moat cut out of the pour
    ctx.fillStyle = FR4;                                                       // keepout under the chip body
    ctx.fillRect(bx - qs - 8, by - qs - 8, (qs + 8) * 2, (qs + 8) * 2);
    const vias = [];
    for (let v = 0; v < 40; v++) {                                             // vias sit ON a net, so they land on its centreline
        const L = nets[Math.floor(hash01(v + seed, 81) * nets.length)];
        const p = L.pts[1 + Math.floor(hash01(v + seed, 82) * (L.pts.length - 2))];
        const r = 11 + hash01(v + seed, 83) * 8;
        vias.push([p[0], p[1], r]);
        drawWrapped(size, p[0], p[1], r + 9, (px, py) => {
            ctx.fillStyle = FR4; ctx.beginPath(); ctx.arc(px, py, r + 8, 0, Math.PI * 2); ctx.fill();
        });
    }
    for (const L of nets) strokePts(L.pts, L.w + 3, TRACE_LO, 2.5, 2.5);      // the copper stands proud of the substrate:
    for (const L of nets) strokePts(L.pts, L.w, TRACE, 0, 0);                 // shadow down-right, lit edge up-left,
    for (const L of nets) strokePts(L.pts, Math.max(1.6, L.w * 0.22), TRACE_HI, -1.6, -1.6);   // same light as every other generator here
    for (const V of vias) {
        drawWrapped(size, V[0], V[1], V[2] + 2, (px, py) => {
            ctx.fillStyle = PAD;    ctx.beginPath(); ctx.arc(px, py, V[2], 0, Math.PI * 2); ctx.fill();
            ctx.fillStyle = PAD_HI; ctx.beginPath(); ctx.arc(px - V[2] * 0.18, py - V[2] * 0.18, V[2] * 0.66, 0, Math.PI * 2); ctx.fill();
            ctx.fillStyle = HOLE;   ctx.beginPath(); ctx.arc(px, py, V[2] * 0.42, 0, Math.PI * 2); ctx.fill();
        });
    }

    // QFN pads + the exposed thermal pad with its stitching vias — the detail that says "this is a footprint".
    for (const p of padPos) {
        const w = p[2] ? 46 : 15, h = p[2] ? 15 : 46;
        ctx.fillStyle = PAD; rrect(p[0] - w / 2, p[1] - h / 2, w, h, 4); ctx.fill();
        ctx.fillStyle = PAD_HI; rrect(p[0] - w / 2 + 1.5, p[1] - h / 2 + 1.5, w - 5, h - 5, 3); ctx.fill();
    }
    const tp = qs * 0.56;
    ctx.fillStyle = PAD;    ctx.fillRect(bx - tp, by - tp, tp * 2, tp * 2);
    ctx.fillStyle = PAD_HI; ctx.fillRect(bx - tp + 3, by - tp + 3, tp * 2 - 9, tp * 2 - 9);
    ctx.strokeStyle = FR4; ctx.lineWidth = 4;                                  // mask webbing splits a big thermal pad
    for (let i = 1; i < 3; i++) {
        ctx.beginPath(); ctx.moveTo(bx - tp + (i / 3) * tp * 2, by - tp); ctx.lineTo(bx - tp + (i / 3) * tp * 2, by + tp); ctx.stroke();
        ctx.beginPath(); ctx.moveTo(bx - tp, by - tp + (i / 3) * tp * 2); ctx.lineTo(bx + tp, by - tp + (i / 3) * tp * 2); ctx.stroke();
    }
    ctx.fillStyle = HOLE;
    for (let j = 0; j < 3; j++) for (let i = 0; i < 3; i++) {
        ctx.beginPath(); ctx.arc(bx + (i - 1) * tp * 0.66, by + (j - 1) * tp * 0.66, 6, 0, Math.PI * 2); ctx.fill();
    }

    // Discrete footprints. An 0805 pad pair inside its silkscreen bracket is what makes a board look populated.
    for (let d = 0; d < 16; d++) {
        const dx = hash01(d + seed, 71) * size, dy = hash01(d + seed, 72) * size;
        if (Math.abs(dx - bx) < qs + 100 && Math.abs(dy - by) < qs + 100) continue;
        const vert = hash01(d + seed, 73) > 0.5;
        const pw = vert ? 34 : 26, ph = vert ? 26 : 34, gap = 26;
        drawWrapped(size, dx, dy, 62, (px, py) => {
            const ox = vert ? 0 : (pw + gap) / 2, oy = vert ? (ph + gap) / 2 : 0;
            ctx.fillStyle = FR4; rrect(px - pw / 2 - ox - 8, py - ph / 2 - oy - 8, pw + 2 * ox + 16, ph + 2 * oy + 16, 10); ctx.fill();
            for (const s of [-1, 1]) {
                ctx.fillStyle = PAD;    rrect(px - pw / 2 + s * ox, py - ph / 2 + s * oy, pw, ph, 4); ctx.fill();
                ctx.fillStyle = PAD_HI; rrect(px - pw / 2 + s * ox + 2, py - ph / 2 + s * oy + 2, pw - 6, ph - 6, 3); ctx.fill();
            }
            ctx.lineWidth = 5; ctx.strokeStyle = SILK_LO;                      // a dark halo doubles the legend's local contrast
            ctx.beginPath();
            if (vert) { ctx.moveTo(px - pw / 2 - 12, py - oy - ph / 2 - 4); ctx.lineTo(px - pw / 2 - 12, py + oy + ph / 2 + 4);
                        ctx.moveTo(px + pw / 2 + 12, py - oy - ph / 2 - 4); ctx.lineTo(px + pw / 2 + 12, py + oy + ph / 2 + 4); }
            else      { ctx.moveTo(px - ox - pw / 2 - 4, py - ph / 2 - 12); ctx.lineTo(px + ox + pw / 2 + 4, py - ph / 2 - 12);
                        ctx.moveTo(px - ox - pw / 2 - 4, py + ph / 2 + 12); ctx.lineTo(px + ox + pw / 2 + 4, py + ph / 2 + 12); }
            ctx.stroke();
            ctx.lineWidth = 3; ctx.strokeStyle = SILK; ctx.stroke();
        });
    }

    // Silkscreen: the chip outline with its pin-1 dot, and a couple of fiducial rings.
    ctx.lineWidth = 7; ctx.strokeStyle = SILK_LO; ctx.strokeRect(bx - qs - 34, by - qs - 34, (qs + 34) * 2, (qs + 34) * 2);
    ctx.lineWidth = 4; ctx.strokeStyle = SILK;    ctx.strokeRect(bx - qs - 34, by - qs - 34, (qs + 34) * 2, (qs + 34) * 2);
    ctx.fillStyle = SILK_LO; ctx.beginPath(); ctx.arc(bx - qs - 52, by - qs - 52, 15, 0, Math.PI * 2); ctx.fill();
    ctx.fillStyle = SILK;    ctx.beginPath(); ctx.arc(bx - qs - 52, by - qs - 52, 11, 0, Math.PI * 2); ctx.fill();
    for (let f = 0; f < 3; f++) {
        const fx = hash01(f + seed, 91) * size, fy = hash01(f + seed, 92) * size;
        if (Math.abs(fx - bx) < qs + 90 && Math.abs(fy - by) < qs + 90) continue;
        drawWrapped(size, fx, fy, 24, (px, py) => {
            ctx.strokeStyle = SILK; ctx.lineWidth = 4;
            ctx.beginPath(); ctx.arc(px, py, 17, 0, Math.PI * 2); ctx.stroke();
            ctx.fillStyle = PAD; ctx.beginPath(); ctx.arc(px, py, 9, 0, Math.PI * 2); ctx.fill();
        });
    }

    // Semi-gloss mask sheen: broad, soft, low-contrast. Wrapped, so it never parks a bright blob on the seam.
    for (let g = 0; g < 4; g++) {
        const gx = hash01(g + seed, 95) * size, gy = hash01(g + seed, 96) * size, gr = 260 + hash01(g + seed, 97) * 220;
        drawWrapped(size, gx, gy, gr, (px, py) => {
            const rg = ctx.createRadialGradient(px, py, 0, px, py, gr);
            rg.addColorStop(0, 'rgba(255,255,255,0.075)');
            rg.addColorStop(1, 'rgba(255,255,255,0)');
            ctx.fillStyle = rg; ctx.beginPath(); ctx.arc(px, py, gr, 0, Math.PI * 2); ctx.fill();
        });
    }

    const texture = new THREE.CanvasTexture(canvas);
    texture.wrapS = texture.wrapT = THREE.RepeatWrapping;
    return texture;
}


// A monocrystalline PV laminate — the reference is a 5-busbar M2 cell in a module: near-black silicon-nitride
// anti-reflective coating, ~50 fine silver fingers gridded across five printed busbars, tinned interconnect
// ribbons running out of the cell and across the gap to its neighbour, and the chamfered PSEUDO-SQUARE outline
// left behind when a square wafer is sawn from a round ingot. The chamfer is the feature that carries the
// recognition — nothing else in the game is that shape — so it is built properly, as the intersection of the
// square with the ingot circle, not as a straight corner cut.
//
// NEUTRAL. #1B3A6B has 27/255 of red, so no canvas value can produce a silver finger; the fingers separate from
// the coating by VALUE (they are ~4x brighter) with the blue channel pulled down, which desaturates them toward
// grey — the closest a multiply can come to metal on a deep blue, and close to what a real cell looks like in
// shade anyway. The dark coating comes from writing the cell field at a quarter brightness, not from paint.
export function createPhotovoltaicTexture(baseHex) {
    const size = 1024;
    const canvas = document.createElement('canvas');
    canvas.width = canvas.height = size;
    const ctx = canvas.getContext('2d');
    const seed = hexSeed(baseHex);

    const BACK    = 'rgb(236, 238, 233)';    // white backsheet between the cells
    const BACK_LO = 'rgb(176, 180, 174)';
    const AR      = 'rgb(60, 62, 72)';       // silicon-nitride anti-reflective coating: nearly black
    const AR_HI   = 'rgb(96, 99, 112)';
    const AR_EDGE = 'rgb(38, 40, 47)';
    const FINGER  = 'rgb(252, 246, 214)';    // screen-printed silver: blue pulled down so it greys off the hex
    const BUS     = 'rgb(255, 252, 236)';
    const BUS_LO  = 'rgb(120, 118, 104)';
    const RIBBON  = 'rgb(226, 220, 196)';

    ctx.fillStyle = BACK;
    ctx.fillRect(0, 0, size, size);
    ctx.globalAlpha = 0.5;                                                    // laminate weave in the backsheet
    ctx.fillStyle = BACK_LO;
    for (let i = 0; i < 128; i++) ctx.fillRect(0, i * 8 + 4, size, 1.4);
    ctx.globalAlpha = 1;

    // TILING: two cells across, pitch 512, so the tile edge falls exactly down the middle of an inter-cell gap —
    // the seam becomes the module's own cell spacing and the chamfers meet across it as the usual white diamond.
    // R/s is the ingot-diameter-to-wafer-side ratio, and it is what sets how much corner comes off: a real M2 cell
    // is a 156mm square out of a 210mm boule, so only the last fifth or so of each edge is cut away. Too small a
    // ratio and the cell turns into an octagon; this is close to the real proportion.
    const GAP = 26, s = 256 - GAP / 2, R = s * 1.26, t = Math.sqrt(R * R - s * s), th = Math.atan2(t, s);
    const BB = 5, bbAt = (i) => ((i + 0.5) / BB * 2 - 1) * s;

    // Interconnect ribbons, drawn full height UNDER the cells: they show only where the cells do not cover them,
    // i.e. in the gaps — including the gap the tile edge runs through, which is why this wraps for free.
    for (const cx of [256, 768]) {
        for (let i = 0; i < BB; i++) {
            const x = cx + bbAt(i);
            ctx.fillStyle = BUS_LO; ctx.fillRect(x - 7, 0, 14, size);
            ctx.fillStyle = RIBBON; ctx.fillRect(x - 5.5, 0, 11, size);
            ctx.fillStyle = BUS;    ctx.fillRect(x - 5.5, 0, 3, size);        // the solder fillet catches the light on one side
        }
    }

    const cellPath = (cx, cy) => {
        ctx.beginPath();
        for (let q = 0; q < 4; q++) ctx.arc(cx, cy, R, q * Math.PI / 2 + th, q * Math.PI / 2 + (Math.PI / 2 - th));
        ctx.closePath();
    };

    for (const cy of [256, 768]) for (const cx of [256, 768]) {
        const ck = (cx + cy * 3) + seed;
        ctx.save();
        cellPath(cx, cy); ctx.clip();

        ctx.fillStyle = AR; ctx.fillRect(cx - s - 4, cy - s - 4, s * 2 + 8, s * 2 + 8);

        // Alkaline-textured wafer: a dense diagonal micro-hatch standing in for the pyramid etch that makes a
        // mono cell glitter. Both diagonals, very low contrast, so it reads as sparkle rather than as lines.
        ctx.globalAlpha = 0.09; ctx.strokeStyle = AR_HI; ctx.lineWidth = 1.2;
        for (let d = -s * 2; d < s * 2; d += 7) {
            ctx.beginPath(); ctx.moveTo(cx - s + d, cy - s); ctx.lineTo(cx + s + d, cy + s); ctx.stroke();
            ctx.beginPath(); ctx.moveTo(cx - s + d, cy + s); ctx.lineTo(cx + s + d, cy - s); ctx.stroke();
        }
        ctx.globalAlpha = 1;

        // Specular sheen off the AR coating, thrown from the same up-left light every other generator uses.
        const sg = ctx.createRadialGradient(cx - s * 0.45, cy - s * 0.5, 0, cx - s * 0.45, cy - s * 0.5, s * 1.7);
        sg.addColorStop(0, 'rgba(255,255,255,0.15)');
        sg.addColorStop(0.55, 'rgba(255,255,255,0.045)');
        sg.addColorStop(1, 'rgba(255,255,255,0)');
        ctx.fillStyle = sg; ctx.fillRect(cx - s, cy - s, s * 2, s * 2);

        // Fingers: 54 of them, perpendicular to the busbars, at the pitch a real cell prints them.
        const FN = 54;
        ctx.strokeStyle = FINGER; ctx.lineWidth = 2.4; ctx.globalAlpha = 0.9;
        for (let f = 0; f < FN; f++) {
            const y = cy - s + (f + 0.5) * (2 * s / FN);
            ctx.beginPath(); ctx.moveTo(cx - s - 4, y); ctx.lineTo(cx + s + 4, y); ctx.stroke();
        }
        ctx.globalAlpha = 0.28; ctx.strokeStyle = BUS_LO; ctx.lineWidth = 1.2;   // each printed line casts a hairline shadow
        for (let f = 0; f < FN; f++) {
            const y = cy - s + (f + 0.5) * (2 * s / FN) + 2.1;
            ctx.beginPath(); ctx.moveTo(cx - s - 4, y); ctx.lineTo(cx + s + 4, y); ctx.stroke();
        }
        ctx.globalAlpha = 1;

        // Busbars, with the periodic solder pads a segmented busbar is printed with.
        for (let i = 0; i < BB; i++) {
            const x = cx + bbAt(i);
            ctx.fillStyle = BUS_LO; ctx.fillRect(x - 6.5, cy - s - 4, 13, s * 2 + 8);
            ctx.fillStyle = BUS;    ctx.fillRect(x - 5, cy - s - 4, 10, s * 2 + 8);
            ctx.fillStyle = FINGER;
            for (let p = 0; p < 13; p++) {
                const y = cy - s + (p + 0.5) * (2 * s / 13);
                ctx.fillRect(x - 10, y - 9, 20, 18);
            }
        }

        ctx.restore();

        // Cell edge: the sawn rim is darker than the field, with a thin lit lip up-left.
        ctx.lineWidth = 7; ctx.strokeStyle = AR_EDGE; cellPath(cx, cy); ctx.stroke();
        ctx.save(); cellPath(cx, cy); ctx.clip();
        ctx.lineWidth = 3; ctx.globalAlpha = 0.35; ctx.strokeStyle = BACK;
        ctx.beginPath(); ctx.arc(cx, cy, R - 2, Math.PI * 1.02, Math.PI * 1.72); ctx.stroke();
        ctx.globalAlpha = 1; ctx.restore();

        // Micro-cracks: two or three hairlines, the thing every fielded module actually has.
        const nc = Math.floor(hash01(ck, 21) * 3);
        ctx.strokeStyle = 'rgba(255,255,255,0.10)'; ctx.lineWidth = 1.6;
        ctx.save(); cellPath(cx, cy); ctx.clip();
        for (let c = 0; c < nc; c++) {
            const a = hash01(ck + c, 22) * Math.PI * 2, r0 = hash01(ck + c, 23) * s;
            ctx.beginPath();
            ctx.moveTo(cx + Math.cos(a) * r0, cy + Math.sin(a) * r0);
            let ax = cx + Math.cos(a) * r0, ay = cy + Math.sin(a) * r0, ah = hash01(ck + c, 24) * Math.PI * 2;
            for (let st = 0; st < 8; st++) {
                ah += (hash01(ck + c * 7 + st, 25) - 0.5) * 0.8;
                ax += Math.cos(ah) * 26; ay += Math.sin(ah) * 26;
                ctx.lineTo(ax, ay);
            }
            ctx.stroke();
        }
        ctx.restore();
    }

    const texture = new THREE.CanvasTexture(canvas);
    texture.wrapS = texture.wrapT = THREE.RepeatWrapping;
    return texture;
}


// An energy-storage module — the reference is an EV/aerospace battery pack module or a power-tool battery: a
// machined dark housing bolted into a rack, a recessed window onto three wound cells whose cores are lit, and a
// segmented state-of-charge gauge with a high-voltage bolt marked beside it. Deliberately NOT
// createEmissivePulseLattice: that one is light in TRANSIT (orthogonal conduits crossing a dark shell, blooming
// at the junctions). This one is light HELD — everything glowing is inside something, behind a bezel, wound in
// coils, and metered. If the two ever end up side by side the difference should be obvious: conduits versus a
// container.
//
// BAKED-PER-KUBE, and in an unusual direction: it does not bake the hex IN, it divides the hex OUT. applyMaterial
// multiplies this canvas by #7FE0FF, and that hex is bright in every channel (127/224/255), which means a texel
// written as target/base lands the shaded pixel on `target` EXACTLY — so this generator can have a neutral dark
// gunmetal housing on a cyan kube, which no straight-neutral canvas could (a neutral canvas on a cyan hex is
// cyan all the way down, and the block reads as jelly rather than as hardware). Anything the hex cannot reach
// clamps at 255, i.e. at the hex itself, which is the brightest cyan available and is what the cell cores use.
// The consequence is that the canvas is tied to this hex and this cache slot; it must not be shared.
export function createPowerCoreTexture(baseHex) {
    const size = 1024;
    const canvas = document.createElement('canvas');
    canvas.width = canvas.height = size;
    const ctx = canvas.getContext('2d');
    const seed = hexSeed(baseHex);

    const n = parseInt(baseHex.replace('#', ''), 16);
    const bR = Math.max(12, (n >> 16) & 255), bG = Math.max(12, (n >> 8) & 255), bB = Math.max(12, n & 255);
    const tgt = (r, g, b) => 'rgb(' + Math.min(255, Math.round(r * 255 / bR)) + ', '
                                   + Math.min(255, Math.round(g * 255 / bG)) + ', '
                                   + Math.min(255, Math.round(b * 255 / bB)) + ')';

    const HOUSING = tgt(74, 78, 84);
    const H_LIT   = tgt(126, 132, 140);
    const H_MID   = tgt(96, 100, 107);
    const H_DEEP  = tgt(40, 43, 47);
    const WELL    = tgt(15, 17, 21);
    const COIL    = tgt(118, 110, 94);       // dull wound copper — as warm as a cyan multiply can reach
    const COIL_HI = tgt(170, 160, 138);
    const CORE    = 'rgb(255, 255, 255)';    // = the hex, the brightest cyan on the block
    const CORE_MID = tgt(78, 186, 222);
    const CORE_LO = tgt(30, 74, 96);
    const OFF     = tgt(46, 50, 55);

    const rrect = (x, y, w, h, r) => {       // Safari 15.6 has no ctx.roundRect
        ctx.beginPath();
        ctx.moveTo(x + r, y);
        ctx.lineTo(x + w - r, y); ctx.quadraticCurveTo(x + w, y, x + w, y + r);
        ctx.lineTo(x + w, y + h - r); ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
        ctx.lineTo(x + r, y + h); ctx.quadraticCurveTo(x, y + h, x, y + h - r);
        ctx.lineTo(x, y + r); ctx.quadraticCurveTo(x, y, x + r, y);
        ctx.closePath();
    };

    ctx.fillStyle = HOUSING;
    ctx.fillRect(0, 0, size, size);

    // Machined finish on the housing: fine horizontal streaks, whole cycles only so the left edge meets the right.
    for (let i = 0; i < 260; i++) {
        const y = (i / 260) * size, cyc = 1 + Math.floor(hash01(i + seed, 1) * 3);
        ctx.globalAlpha = 0.05 + hash01(i, seed + 2) * 0.06;
        ctx.strokeStyle = hash01(i, seed + 3) > 0.5 ? H_LIT : H_DEEP;
        ctx.lineWidth = 0.8 + hash01(i, seed + 4) * 1.6;
        ctx.beginPath();
        for (let x = 0; x <= size; x += 32) {
            const yy = y + Math.sin((x / size) * Math.PI * 2 * cyc + hash01(i + seed, 5) * 6.283) * (0.7 + hash01(i + seed, 6) * 2);
            if (x === 0) ctx.moveTo(x, yy); else ctx.lineTo(x, yy);
        }
        ctx.stroke();
    }
    ctx.globalAlpha = 1;

    // MOUNTING RAIL ON THE SEAM. The tile edge is drawn as a raised rack rail, so a stack of these reads as
    // modules bolted into a frame rather than as a texture that happens to repeat. It is ONE rail 2*RW wide
    // straddling the join, drawn as its two halves — which is why the lit lip sits at size-RW and the shadowed
    // lip at RW, a rail's width apart ACROSS the seam. Bevelling each half on its own instead (lit at its own
    // top, dark at its own bottom) puts a dark line at 1023 hard against a bright line at 0, and a wall of them
    // shows a hairline stripe at every cell boundary — measured at 71/255 mean channel step before this fix.
    const RW = 34;
    ctx.fillStyle = H_MID;
    ctx.fillRect(0, 0, size, RW); ctx.fillRect(0, size - RW, size, RW);
    ctx.fillRect(0, 0, RW, size); ctx.fillRect(size - RW, 0, RW, size);
    ctx.fillStyle = H_LIT; ctx.globalAlpha = 0.55;
    ctx.fillRect(0, size - RW, size, 4); ctx.fillRect(size - RW, 0, 4, size);
    ctx.fillStyle = H_DEEP; ctx.globalAlpha = 0.6;
    ctx.fillRect(0, RW - 4, size, 4); ctx.fillRect(RW - 4, 0, 4, size);
    ctx.globalAlpha = 1;

    const bolt = (bx, by, r) => drawWrapped(size, bx, by, r + 5, (x, y) => {
        ctx.fillStyle = H_DEEP; ctx.globalAlpha = 0.6;
        ctx.beginPath(); ctx.arc(x + r * 0.24, y + r * 0.24, r * 1.05, 0, Math.PI * 2); ctx.fill();
        ctx.globalAlpha = 1;
        const g = ctx.createRadialGradient(x - r * 0.4, y - r * 0.45, r * 0.1, x, y, r);
        g.addColorStop(0, H_LIT); g.addColorStop(0.55, H_MID); g.addColorStop(1, H_DEEP);
        ctx.fillStyle = g; ctx.beginPath(); ctx.arc(x, y, r, 0, Math.PI * 2); ctx.fill();
        ctx.fillStyle = WELL; ctx.beginPath(); ctx.arc(x, y, r * 0.38, 0, Math.PI * 2); ctx.fill();
    });
    for (let i = 0; i < 8; i++) {            // pitch 128 divides 1024, so the bolt row runs unbroken across a wall
        const p = i * 128 + 64;
        bolt(p, RW / 2, 11); bolt(p, size - RW / 2, 11);
        bolt(RW / 2, p, 11); bolt(size - RW / 2, p, 11);
    }

    // ── The containment window: recessed, so its bevel is the rail's bevel inverted. ────────────────────────
    const wi = 128, ww = size - 2 * wi;
    ctx.fillStyle = H_DEEP; ctx.globalAlpha = 0.5; ctx.fillRect(wi - 14, wi - 14, ww + 28, ww + 28); ctx.globalAlpha = 1;
    ctx.fillStyle = WELL; ctx.fillRect(wi, wi, ww, ww);
    ctx.fillStyle = H_DEEP; ctx.globalAlpha = 0.9;
    ctx.fillRect(wi, wi, ww, 9); ctx.fillRect(wi, wi, 9, ww);
    ctx.fillStyle = H_LIT; ctx.globalAlpha = 0.45;
    ctx.fillRect(wi, wi + ww - 8, ww, 8); ctx.fillRect(wi + ww - 8, wi, 8, ww);
    ctx.globalAlpha = 1;

    // Three wound cells. Each is a glowing rod inside a coil: the cylinder is shaded across its width, the
    // windings ride over it, and the light leaks out BETWEEN the turns — which is what makes it read as
    // contained rather than as an emitting surface.
    const CN = 3, cellW = (ww - 60) / CN - 26, top = wi + 62, bot = wi + ww - 128;
    for (let c = 0; c < CN; c++) {
        const cx = wi + 30 + 13 + c * ((ww - 60) / CN) + cellW / 2;
        const x0 = cx - cellW / 2, h = bot - top;

        ctx.fillStyle = H_DEEP; rrect(x0 - 7, top - 7, cellW + 14, h + 14, 20); ctx.fill();      // cell can
        const cg = ctx.createLinearGradient(x0, 0, x0 + cellW, 0);
        cg.addColorStop(0, CORE_LO); cg.addColorStop(0.34, CORE_MID); cg.addColorStop(0.5, CORE);
        cg.addColorStop(0.68, CORE_MID); cg.addColorStop(1, CORE_LO);
        ctx.fillStyle = cg; rrect(x0, top, cellW, h, 14); ctx.fill();

        ctx.save(); rrect(x0, top, cellW, h, 14); ctx.clip();
        const TURNS = 11, tp = h / TURNS, tilt = 0.06;                                           // the winding, tilted,
        for (let w = 0; w < TURNS; w++) {                                                         // because a solenoid is
            const y = top + (w + 0.5) * tp;                                                       // a helix, not a stack
            ctx.fillStyle = 'rgba(0,0,0,0.42)';                                                    // of separate rings
            ctx.beginPath(); ctx.ellipse(cx, y + tp * 0.20, cellW * 0.54, tp * 0.24, tilt, 0, Math.PI * 2); ctx.fill();
            ctx.fillStyle = COIL;    ctx.beginPath(); ctx.ellipse(cx, y, cellW * 0.54, tp * 0.24, tilt, 0, Math.PI * 2); ctx.fill();
            ctx.fillStyle = COIL_HI; ctx.beginPath(); ctx.ellipse(cx - cellW * 0.14, y - tp * 0.07, cellW * 0.33, tp * 0.10, tilt, 0, Math.PI * 2); ctx.fill();
        }
        ctx.fillStyle = CORE; ctx.globalAlpha = 0.5;                                              // light leaking out
        ctx.shadowColor = CORE; ctx.shadowBlur = 30;                                              // BETWEEN the turns is
        for (let w = 0; w < TURNS; w++) {                                                         // the whole read: the
            const y = top + (w + 1) * tp;                                                         // energy is inside the
            ctx.fillRect(cx - cellW * 0.30, y - tp * 0.14, cellW * 0.60, tp * 0.28);              // winding, not on it
        }
        ctx.shadowBlur = 0; ctx.globalAlpha = 1;
        ctx.restore();

        ctx.fillStyle = H_MID; rrect(x0 - 4, top - 20, cellW + 8, 26, 8); ctx.fill();            // terminals
        ctx.fillStyle = H_MID; rrect(x0 - 4, bot - 6, cellW + 8, 26, 8); ctx.fill();
        ctx.fillStyle = H_LIT; ctx.fillRect(x0 - 2, top - 18, cellW + 4, 4);
        ctx.fillStyle = CORE; ctx.globalAlpha = 0.85;                                            // charge showing at the post
        ctx.fillRect(x0 + cellW * 0.3, top - 14, cellW * 0.4, 6);
        ctx.globalAlpha = 1;
    }

    // Bus bar across the terminals — the thing that ties the cells into one pack.
    ctx.fillStyle = H_MID; ctx.fillRect(wi + 30, wi + 26, ww - 60, 22);
    ctx.fillStyle = H_LIT; ctx.fillRect(wi + 30, wi + 26, ww - 60, 4);
    ctx.fillStyle = H_DEEP; ctx.fillRect(wi + 30, wi + 44, ww - 60, 4);

    // State-of-charge gauge: eight segments, a hashed number of them lit. Reads as "stored, and this much left".
    const gy = wi + ww - 92, gx = wi + 46, gw = ww - 152, SEG = 8, sw = (gw - (SEG - 1) * 9) / SEG;
    const lit = 5 + Math.floor(hash01(seed, 77) * 3);
    ctx.fillStyle = H_DEEP; rrect(gx - 12, gy - 12, gw + 24, 58, 10); ctx.fill();
    for (let i = 0; i < SEG; i++) {
        const x = gx + i * (sw + 9);
        if (i < lit) {
            ctx.shadowColor = CORE; ctx.shadowBlur = 26;
            ctx.fillStyle = CORE; ctx.fillRect(x, gy, sw, 34);
            ctx.shadowBlur = 0;
        } else {
            ctx.fillStyle = OFF; ctx.fillRect(x, gy, sw, 34);
            ctx.fillStyle = H_DEEP; ctx.fillRect(x + 2, gy + 2, sw - 4, 30);
        }
    }

    // High-voltage bolt beside the gauge — pure geometry, no glyph, so it is bit-identical on every client.
    const lx = wi + ww - 86, ly = gy + 17, ls = 30;
    ctx.fillStyle = CORE; ctx.shadowColor = CORE; ctx.shadowBlur = 22;
    ctx.beginPath();
    ctx.moveTo(lx + ls * 0.18, ly - ls * 0.62);
    ctx.lineTo(lx - ls * 0.30, ly + ls * 0.08);
    ctx.lineTo(lx - ls * 0.02, ly + ls * 0.08);
    ctx.lineTo(lx - ls * 0.20, ly + ls * 0.62);
    ctx.lineTo(lx + ls * 0.32, ly - ls * 0.06);
    ctx.lineTo(lx + ls * 0.02, ly - ls * 0.06);
    ctx.closePath(); ctx.fill();
    ctx.shadowBlur = 0;

    // Glow spilling out of the well onto the bezel — the last thing that says the light is coming from INSIDE.
    const bg = ctx.createRadialGradient(size / 2, size / 2, ww * 0.34, size / 2, size / 2, ww * 0.86);
    bg.addColorStop(0, 'rgba(255,255,255,0.16)');
    bg.addColorStop(1, 'rgba(255,255,255,0)');
    ctx.fillStyle = bg; ctx.fillRect(wi - 40, wi - 40, ww + 80, ww + 80);

    const texture = new THREE.CanvasTexture(canvas);
    texture.wrapS = texture.wrapT = THREE.RepeatWrapping;
    return texture;
}

// A BATTERY IS A RACK OF CELLS, not a glowing lump. The Power Core's look — one bright well with a coil round it —
// said "reactor", and a battery borrowed it because it was the nearest thing on the shelf. What reads as a battery
// is the repetition: a row of upright cells behind a bezel, each divided into segments, sitting in a recess deep
// enough that the light is clearly coming from inside the box.
//
// IT HAS TO TILE, on both axes, because this wraps a cube and the seams meet. Horizontally that is free — the cells
// sit on an even pitch that divides the canvas — and vertically it comes from the rails being symmetric, so the top
// of one face continues the bottom of the one above it.
//
// AND IT CANNOT BE BRIGHTER THAN THE KUBE'S HEX, because the map is MULTIPLIED by it. That is why every colour here
// goes through tgt(): it divides by the base so the number written is what comes OUT the other side, and why the
// cell highlight is plain white — white times the hex IS the hex, which makes the hex the brightest thing on the
// block. It follows that the hex has to BE the cell colour rather than the housing colour, and the housing is then
// darkened down from it. A dark-slate hex with bright blue cells is not a thing this pipeline can express.
// THE BATTERY WEARS ITS OWN GAUGE.
//
// It used to be a rack of eight cells at invented charges — decorative, and a lie: the numbers on it had nothing to
// do with the number the HUD was reporting. A battery is now one per inventory slot (products.json `stackLimit`),
// which is what makes this possible at all: with a slot to itself, a face can show THIS battery's charge rather than
// an average of several.
//
// A RING, NOT A BAR, and that choice is about size rather than taste. The same texture is read at three scales — a
// one-metre block, a 34px hotbar icon, and an 18px inventory thumbnail — and an arc degrades the best of the ten
// options considered: a bar's length becomes a few pixels of difference, where an arc's ANGLE is still legible when
// the whole gauge is nine pixels across. The sweep starts at twelve o'clock and runs clockwise, which is the one
// convention nobody has to be taught.
//
// `fill` is 0..1 and is QUANTISED BY THE CALLER (TextureEngine bakes one texture per step), so this function is a
// pure function of (hex, fill) and its result is cacheable.
export function createBatteryTexture(baseHex, fill = 1) {
    const size = 1024;
    const canvas = document.createElement('canvas');
    canvas.width = canvas.height = size;
    const ctx = canvas.getContext('2d');
    const seed = hexSeed(baseHex);
    const f = Math.max(0, Math.min(1, Number(fill)));

    // The map is MULTIPLIED by the kube's hex, so nothing here can be brighter than it: tgt divides by the base so a
    // colour written as (96,190,236) arrives as (96,190,236), and pure white arrives as the hex exactly.
    const n = parseInt(baseHex.replace('#', ''), 16);
    const bR = Math.max(12, (n >> 16) & 255), bG = Math.max(12, (n >> 8) & 255), bB = Math.max(12, n & 255);
    const tgt = (r, g, b) => 'rgb(' + Math.min(255, Math.round(r * 255 / bR)) + ', '
                                   + Math.min(255, Math.round(g * 255 / bG)) + ', '
                                   + Math.min(255, Math.round(b * 255 / bB)) + ')';

    const HOUSING = tgt(34, 38, 48);
    const H_LIT   = tgt(74, 80, 92);
    const H_DEEP  = tgt(14, 16, 21);
    const PLATE   = tgt(26, 30, 39);
    const EDGE    = tgt(92, 101, 116);
    const BEZEL   = tgt(60, 67, 80);
    const WELL    = tgt(5, 6, 9);
    const TRACK   = tgt(10, 13, 20);   // a RECESS, not a tint: it has to be clearly darker than PLATE or an empty gauge is a blank plate
    const BOLT    = tgt(122, 128, 138);
    const BOLT_LO = tgt(38, 41, 46);
    const ARC_HI  = 'rgb(255, 255, 255)';     // = the hex exactly: the brightest thing on the block
    const ARC_MID = tgt(96, 190, 236);
    const ARC_LO  = tgt(26, 62, 86);

    const rrect = (x, y, w, h, r) => {        // Safari 15.6 has no ctx.roundRect
        ctx.beginPath();
        ctx.moveTo(x + r, y);
        ctx.lineTo(x + w - r, y); ctx.quadraticCurveTo(x + w, y, x + w, y + r);
        ctx.lineTo(x + w, y + h - r); ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
        ctx.lineTo(x + r, y + h); ctx.quadraticCurveTo(x, y + h, x, y + h - r);
        ctx.lineTo(x, y + r); ctx.quadraticCurveTo(x, y, x + r, y);
        ctx.closePath();
    };

    // ---- the case. Machined in whole cycles only, so the left edge meets the right and the face still tiles.
    ctx.fillStyle = HOUSING;
    ctx.fillRect(0, 0, size, size);
    for (let i = 0; i < 240; i++) {
        const y = (i / 240) * size, cyc = 1 + Math.floor(hash01(i + seed, 1) * 3);
        ctx.globalAlpha = 0.05 + hash01(i, seed + 2) * 0.06;
        ctx.strokeStyle = hash01(i, seed + 3) > 0.5 ? H_LIT : H_DEEP;
        ctx.lineWidth = 0.8 + hash01(i, seed + 4) * 1.5;
        ctx.beginPath();
        for (let x = 0; x <= size; x += 32) {
            const yy = y + Math.sin((x / size) * Math.PI * 2 * cyc + hash01(i, seed + 5) * 6.283) * 1.6;
            if (x === 0) ctx.moveTo(x, yy); else ctx.lineTo(x, yy);
        }
        ctx.stroke();
    }
    ctx.globalAlpha = 1;

    // ---- the instrument plate the gauge is set into. Inset far enough that the ring never reaches a tile edge:
    // a face is ONE gauge, and an arc clipped by the seam would read as two broken ones on a wall of them.
    const M = size * 0.085;
    ctx.fillStyle = PLATE; rrect(M, M, size - M * 2, size - M * 2, size * 0.05); ctx.fill();
    ctx.strokeStyle = EDGE; ctx.lineWidth = size * 0.007;
    rrect(M, M, size - M * 2, size - M * 2, size * 0.05); ctx.stroke();
    // …and a shadow under the lip, so the plate sits IN the case rather than on it.
    const lip = ctx.createLinearGradient(0, M, 0, M + size * 0.06);
    lip.addColorStop(0, 'rgba(0,0,0,0.75)'); lip.addColorStop(1, 'rgba(0,0,0,0)');
    ctx.save(); rrect(M, M, size - M * 2, size - M * 2, size * 0.05); ctx.clip();
    ctx.fillStyle = lip; ctx.fillRect(M, M, size - M * 2, size * 0.10); ctx.restore();

    const cx = size / 2, cy = size / 2, R = size * 0.29, LW = size * 0.095;

    // ---- the bezel: twelve ticks, the quarters longer. They are what makes a partial arc MEASURABLE rather than
    // merely present — without them 40% and 55% look the same at icon size.
    for (let i = 0; i < 12; i++) {
        const a = (i / 12) * Math.PI * 2 - Math.PI / 2, quarter = i % 3 === 0;
        const r0 = R + LW * 0.64, r1 = R + LW * (quarter ? 1.05 : 0.88);
        ctx.strokeStyle = quarter ? EDGE : BEZEL;
        ctx.lineWidth = size * (quarter ? 0.020 : 0.013);
        ctx.beginPath();
        ctx.moveTo(cx + Math.cos(a) * r0, cy + Math.sin(a) * r0);
        ctx.lineTo(cx + Math.cos(a) * r1, cy + Math.sin(a) * r1);
        ctx.stroke();
    }

    // ---- the dead track. Always drawn whole, so an empty battery still reads as a gauge at zero rather than as a
    // blank plate — "flat" and "broken" must not look the same.
    ctx.lineCap = 'butt';
    ctx.strokeStyle = TRACK; ctx.lineWidth = LW;
    ctx.beginPath(); ctx.arc(cx, cy, R, 0, Math.PI * 2); ctx.stroke();
    // The groove's two edges, lit the way a groove is: a shadow on the inner wall and a catch of light on the outer
    // rim. Without them an empty cell was a flat plate with nothing on it — and "flat" must not look like "broken".
    ctx.strokeStyle = 'rgba(0,0,0,0.70)'; ctx.lineWidth = size * 0.009;
    ctx.beginPath(); ctx.arc(cx, cy, R - LW / 2, 0, Math.PI * 2); ctx.stroke();
    ctx.strokeStyle = 'rgba(255,255,255,0.13)'; ctx.lineWidth = size * 0.007;
    ctx.beginPath(); ctx.arc(cx, cy, R + LW / 2, 0, Math.PI * 2); ctx.stroke();

    // ---- the charge. Twelve o'clock, clockwise.
    if (f > 0.004) {
        const a0 = -Math.PI / 2, a1 = a0 + Math.PI * 2 * f;
        const g = ctx.createLinearGradient(cx - R, cy - R, cx + R, cy + R);
        g.addColorStop(0, ARC_LO); g.addColorStop(0.45, ARC_MID); g.addColorStop(1, ARC_HI);
        ctx.strokeStyle = g; ctx.lineWidth = LW;
        ctx.beginPath(); ctx.arc(cx, cy, R, a0, a1); ctx.stroke();
        // A specular line along the outer third of the band: one highlight, so it reads as a lit tube rather than
        // as a flat ribbon of colour.
        ctx.strokeStyle = 'rgba(255,255,255,0.42)'; ctx.lineWidth = LW * 0.26;
        ctx.beginPath(); ctx.arc(cx, cy, R + LW * 0.29, a0, a1); ctx.stroke();
        // THE HEAD, which is the whole reason this works small. A bloom at the leading edge survives being scaled
        // to nine pixels when the arc itself has become a smudge — at that size the player is reading WHERE the
        // bright spot sits on the dial, which is still the answer.
        const hx = cx + Math.cos(a1) * R, hy = cy + Math.sin(a1) * R;
        const hg = ctx.createRadialGradient(hx, hy, 0, hx, hy, LW * 1.7);
        hg.addColorStop(0, 'rgba(255,255,255,0.95)'); hg.addColorStop(0.35, 'rgba(255,255,255,0.30)');
        hg.addColorStop(1, 'rgba(255,255,255,0)');
        ctx.fillStyle = hg;
        ctx.beginPath(); ctx.arc(hx, hy, LW * 1.7, 0, Math.PI * 2); ctx.fill();
        ctx.fillStyle = ARC_HI;
        ctx.beginPath(); ctx.arc(hx, hy, LW * 0.30, 0, Math.PI * 2); ctx.fill();
    }

    // ---- the hub. Its wash carries the charge as a COLOUR as well as an angle, so the icon still says something
    // at the size where the arc is one pixel wide.
    ctx.fillStyle = WELL;
    ctx.beginPath(); ctx.arc(cx, cy, R - LW * 0.72, 0, Math.PI * 2); ctx.fill();
    if (f > 0.004) {
        const hub = ctx.createRadialGradient(cx, cy, 0, cx, cy, R - LW * 0.72);
        hub.addColorStop(0, ARC_MID); hub.addColorStop(1, 'rgba(0,0,0,0)');
        ctx.globalAlpha = 0.16 + 0.44 * f;
        ctx.fillStyle = hub;
        ctx.beginPath(); ctx.arc(cx, cy, R - LW * 0.72, 0, Math.PI * 2); ctx.fill();
        ctx.globalAlpha = 1;
    }
    ctx.strokeStyle = BEZEL; ctx.lineWidth = size * 0.010;
    ctx.beginPath(); ctx.arc(cx, cy, R - LW * 0.72, 0, Math.PI * 2); ctx.stroke();
    ctx.strokeStyle = 'rgba(255,255,255,0.10)'; ctx.lineWidth = size * 0.005;
    ctx.beginPath(); ctx.arc(cx, cy, R - LW * 0.72 - size * 0.008, 0, Math.PI * 2); ctx.stroke();

    // ---- terminals, so it reads as a CELL and not as a dial: a raised positive bar and a recessed negative one.
    ctx.fillStyle = EDGE;  rrect(size * 0.44, size * 0.017, size * 0.12, size * 0.030, size * 0.010); ctx.fill();
    ctx.fillStyle = H_DEEP; rrect(size * 0.44, size * 0.953, size * 0.12, size * 0.030, size * 0.010); ctx.fill();

    // ---- corner bolts, shared with the rest of the manufactured family.
    for (const [u, v] of [[0.145, 0.145], [0.855, 0.145], [0.145, 0.855], [0.855, 0.855]]) {
        ctx.fillStyle = BOLT;    ctx.beginPath(); ctx.arc(size * u, size * v, size * 0.023, 0, Math.PI * 2); ctx.fill();
        ctx.fillStyle = BOLT_LO; ctx.beginPath(); ctx.arc(size * u, size * v, size * 0.011, 0, Math.PI * 2); ctx.fill();
    }

    const tex = new THREE.CanvasTexture(canvas);
    tex.wrapS = tex.wrapT = THREE.RepeatWrapping;
    return tex;
}


// Welded armour plate — the reference is the glacis of a tank or a submarine's sail: rolled homogeneous steel
// with a directional mill grain, joined by a TIG "stack of dimes" weld bead with a discoloured heat-affected
// zone either side, chamfered at the joint, and held down by a row of hex-socket fasteners. The stack-of-dimes
// bead is what carries the recognition — a smooth line reads as a drawn stripe, a row of overlapping crescents
// reads as something a welder laid down.
//
// The tile edge IS the plate joint: the weld is drawn straddling the seam (both halves, at 0 and at size), the
// chamfer is inside it, and the fastener row runs parallel at a pitch that divides 1024. So a wall of these does
// not merely tile without a seam — the seam is the point, and reads as plating.
//
// NEUTRAL. #4A4E54 is very nearly grey, so a white/grey canvas keeps the hue and only sets value; the metal's
// whole appearance is the drawn light, because the on-world voxel material is a MeshBasicMaterial with a custom
// light term (no specular at all), so the highlights have to be in the map or they do not exist.
export function createArmorPlateTexture(baseHex) {
    const size = 1024;
    const canvas = document.createElement('canvas');
    canvas.width = canvas.height = size;
    const ctx = canvas.getContext('2d');
    const seed = hexSeed(baseHex);

    const BASE  = 'rgb(214, 216, 220)';
    const LIT   = 'rgb(250, 251, 253)';
    const MID   = 'rgb(186, 188, 193)';
    const SHADE = 'rgb(146, 148, 154)';
    const DEEP  = 'rgb(92, 94, 100)';
    const BLACK = 'rgb(44, 46, 50)';
    const HAZ   = 'rgb(160, 156, 150)';      // heat tint either side of the bead — a touch warm of the plate

    ctx.fillStyle = BASE;
    ctx.fillRect(0, 0, size, size);

    // Mill grain: the plate came off a roll, so the grain is strongly directional and very low contrast.
    // Whole-cycle wobble only, so a streak leaves the right edge at the height it entered the left.
    for (let i = 0; i < 460; i++) {
        const y = (i / 460) * size, cyc = 1 + Math.floor(hash01(i + seed, 1) * 3);
        ctx.globalAlpha = 0.06 + hash01(i, seed + 2) * 0.13;
        ctx.strokeStyle = hash01(i, seed + 3) > 0.5 ? LIT : DEEP;
        ctx.lineWidth = 0.6 + hash01(i, seed + 4) * 2.2;
        ctx.beginPath();
        for (let x = 0; x <= size; x += 32) {
            const yy = y + Math.sin((x / size) * Math.PI * 2 * cyc + hash01(i + seed, 5) * 6.283) * (0.5 + hash01(i + seed, 6) * 1.8);
            if (x === 0) ctx.moveTo(x, yy); else ctx.lineTo(x, yy);
        }
        ctx.stroke();
    }
    ctx.globalAlpha = 1;

    // Chamfer at the joint. Light comes from up-left throughout this file, so the facet at the top and left of
    // the plate faces into it and is LIT, while the bottom and right facets fall away and are SHADEd.
    const CH = 30, HZ = 62;
    const bandV = (y0, h, from, to) => {                                     // a soft heat-tint falloff off the bead
        const g = ctx.createLinearGradient(0, y0, 0, y0 + h);
        g.addColorStop(0, from); g.addColorStop(1, to);
        ctx.fillStyle = g; ctx.fillRect(0, y0, size, h);
    };
    const bandH = (x0, w, from, to) => {
        const g = ctx.createLinearGradient(x0, 0, x0 + w, 0);
        g.addColorStop(0, from); g.addColorStop(1, to);
        ctx.fillStyle = g; ctx.fillRect(x0, 0, w, size);
    };
    ctx.globalAlpha = 0.55;
    bandV(0, HZ, HAZ, BASE); bandV(size - HZ, HZ, BASE, HAZ);
    bandH(0, HZ, HAZ, BASE); bandH(size - HZ, HZ, BASE, HAZ);
    ctx.globalAlpha = 1;
    ctx.fillStyle = LIT;   ctx.globalAlpha = 0.5;
    ctx.fillRect(0, CH, size, 13); ctx.fillRect(CH, 0, 13, size);
    ctx.fillStyle = SHADE; ctx.globalAlpha = 0.55;
    ctx.fillRect(0, size - CH - 13, size, 13); ctx.fillRect(size - CH - 13, 0, 13, size);
    ctx.globalAlpha = 1;

    // The bead. 64 dimes at a pitch of 16 — divides 1024, so the crescents step evenly straight across the join
    // instead of colliding at it. Each is a body ellipse, a lit crescent on the up-left of the ripple, and a
    // shadow on the down-right: the same light, applied 64 times, is what makes it read as molten-then-frozen.
    const dime = (x, y, horiz, k) => {
        const rl = 12 + hash01(k + seed, 11) * 2.5, rs = 9.5 + hash01(k + seed, 12) * 2;
        const rx = horiz ? rl : rs, ry = horiz ? rs : rl;
        ctx.fillStyle = MID;   ctx.beginPath(); ctx.ellipse(x, y, rx, ry, 0, 0, Math.PI * 2); ctx.fill();
        ctx.fillStyle = DEEP;  ctx.globalAlpha = 0.55;
        ctx.beginPath(); ctx.ellipse(x + 2.4, y + 2.6, rx * 0.94, ry * 0.94, 0, 0, Math.PI * 2); ctx.fill();
        ctx.globalAlpha = 1;
        ctx.fillStyle = MID;   ctx.beginPath(); ctx.ellipse(x, y, rx * 0.9, ry * 0.9, 0, 0, Math.PI * 2); ctx.fill();
        ctx.fillStyle = LIT;   ctx.globalAlpha = 0.75;
        ctx.beginPath(); ctx.ellipse(x - rx * 0.24, y - ry * 0.26, rx * 0.52, ry * 0.5, 0, 0, Math.PI * 2); ctx.fill();
        ctx.globalAlpha = 1;
    };
    const weld = (horiz) => {
        ctx.fillStyle = DEEP; ctx.globalAlpha = 0.45;                        // the groove the bead sits in
        if (horiz) { ctx.fillRect(0, 0, size, 15); ctx.fillRect(0, size - 15, size, 15); }
        else       { ctx.fillRect(0, 0, 15, size); ctx.fillRect(size - 15, 0, 15, size); }
        ctx.globalAlpha = 1;
        for (let i = 0; i < 64; i++) {
            const p = i * 16 + 8 + (hash01(i + seed, horiz ? 13 : 14) - 0.5) * 3;
            if (horiz) { dime(p, 0, true, i); dime(p, size, true, i); }
            else       { dime(0, p, false, i + 100); dime(size, p, false, i + 100); }
        }
    };
    weld(true); weld(false);

    // Hex-socket fasteners, pitch 128, parallel to each joint.
    const hexPath = (x, y, r, rot) => {
        ctx.beginPath();
        for (let i = 0; i < 6; i++) {
            const a = rot + i * Math.PI / 3;
            const px = x + Math.cos(a) * r, py = y + Math.sin(a) * r;
            if (i === 0) ctx.moveTo(px, py); else ctx.lineTo(px, py);
        }
        ctx.closePath();
    };
    const fastener = (fx, fy, r) => drawWrapped(size, fx, fy, r + 8, (x, y) => {
        ctx.fillStyle = DEEP; ctx.globalAlpha = 0.5;
        hexPath(x + r * 0.16, y + r * 0.2, r * 1.08, 0.2); ctx.fill();
        ctx.globalAlpha = 1;
        const g = ctx.createRadialGradient(x - r * 0.42, y - r * 0.46, r * 0.08, x, y, r * 1.15);
        g.addColorStop(0, LIT); g.addColorStop(0.5, BASE); g.addColorStop(1, SHADE);
        ctx.fillStyle = g; hexPath(x, y, r, 0.2); ctx.fill();
        ctx.strokeStyle = DEEP; ctx.lineWidth = 2; hexPath(x, y, r, 0.2); ctx.stroke();
        ctx.fillStyle = BLACK; hexPath(x, y, r * 0.46, 0.2 + Math.PI / 6); ctx.fill();
        ctx.strokeStyle = LIT; ctx.globalAlpha = 0.4; ctx.lineWidth = 1.6;
        hexPath(x, y, r * 0.46, 0.2 + Math.PI / 6); ctx.stroke();
        ctx.globalAlpha = 1;
    });
    for (let i = 0; i < 8; i++) {
        const p = i * 128 + 64;
        fastener(p, 96, 21); fastener(p, size - 96, 21);
        fastener(96, p, 21); fastener(size - 96, p, 21);
    }

    // Battle damage, over the fasteners so a dent can catch one: a pressed hollow (soft dark pool, bright rim on
    // the up-left where the metal is turned into the light) plus gouges and scuffs.
    for (let d = 0; d < 16; d++) {
        const dx = hash01(d + seed, 21) * size, dy = hash01(d + seed, 22) * size;
        const r = 15 + hash01(d + seed, 23) * 26;
        drawWrapped(size, dx, dy, r + 8, (x, y) => {
            const g = ctx.createRadialGradient(x + r * 0.34, y + r * 0.36, 0, x, y, r);
            g.addColorStop(0, 'rgba(66,68,74,0.50)'); g.addColorStop(0.65, 'rgba(66,68,74,0.22)'); g.addColorStop(1, 'rgba(66,68,74,0)');
            ctx.fillStyle = g; ctx.beginPath(); ctx.arc(x, y, r, 0, Math.PI * 2); ctx.fill();
            ctx.strokeStyle = 'rgba(255,255,255,0.5)'; ctx.lineWidth = 3;     // the lip turned into the light
            ctx.beginPath(); ctx.arc(x, y, r * 0.86, Math.PI * 0.92, Math.PI * 1.72); ctx.stroke();
            ctx.strokeStyle = 'rgba(60,62,68,0.4)'; ctx.lineWidth = 2.5;
            ctx.beginPath(); ctx.arc(x, y, r * 0.86, Math.PI * 0.05, Math.PI * 0.78); ctx.stroke();
        });
    }
    ctx.lineCap = 'round';
    for (let s = 0; s < 90; s++) {
        const sx = hash01(s + seed, 31) * size, sy = hash01(s + seed, 32) * size;
        const a = (hash01(s + seed, 33) - 0.5) * 0.7 + (hash01(s, seed + 34) > 0.5 ? 0 : Math.PI / 2);
        const len = 20 + hash01(s + seed, 35) * 150;
        drawWrapped(size, sx, sy, len, (x, y) => {
            ctx.globalAlpha = 0.05 + hash01(s, seed + 36) * 0.16;
            ctx.strokeStyle = hash01(s, seed + 37) > 0.5 ? LIT : DEEP;
            ctx.lineWidth = 0.8 + hash01(s, seed + 38) * 2.2;
            ctx.beginPath(); ctx.moveTo(x, y); ctx.lineTo(x + Math.cos(a) * len, y + Math.sin(a) * len); ctx.stroke();
        });
    }
    ctx.globalAlpha = 1;

    const texture = new THREE.CanvasTexture(canvas);
    texture.wrapS = texture.wrapT = THREE.RepeatWrapping;
    return texture;
}


// Dry quartz sand — the reference is builder's silica sand tipped out and raked flat, lit from one side. It is
// the first thing a new player picks up off a rock, so it has to look like something.
//
// What makes sand read as sand is that the surface is PACKED: there is no smooth ground between the grains,
// every square millimetre IS a grain, each with a shoulder turned into the light and a contact shadow on the
// other side, and the dark network between them is what the eye actually reads. Sprinkling a few thousand pale
// discs on a smooth field reads as foam or lichen instead — which is why the bed here is built as ONE
// createImageData pass on a 4px lattice (1024/4 = 256 cells, so it wraps exactly) rather than as draw calls:
// covering a million pixels with 4px grains would need a quarter of a million fills, and there is no cheap way
// to do that with paths. Every cell gets a hashed base brightness plus a diagonal facet ramp toward the up-left
// light, so a grain is lit on one shoulder and shadowed on the other, and every row/column is shoved by its own
// hashed amount so the cell edges stagger instead of lining up into a mosaic. Only the coarse grains and the
// glints on top of that bed are drawn, and there are few enough of them to be cheap. Measured at 18-20ms on an
// M-series laptop, which is the price of the one pass; every other generator in this file is under 4ms.
//
// NEUTRAL, and it barely needs to be anything else: #8B8781 is already a warm mid-grey, so a bright canvas keeps
// the hue and the drawn light supplies the rest. Grain bodies pull the blue channel down slightly, landing them
// warm of the hex — the faint cream of quartz — while the shadows stay neutral.
export function createSilicaSandTexture(baseHex) {
    const size = 1024;
    const canvas = document.createElement('canvas');
    canvas.width = canvas.height = size;
    const ctx = canvas.getContext('2d');
    const seed = hexSeed(baseHex);

    const LIGHT = 'rgb(248, 243, 228)';
    const GLINT = 'rgb(255, 254, 246)';
    const FIELD = 'rgb(206, 202, 192)';
    const DARK  = 'rgb(126, 124, 120)';

    // ── the grain bed: one pass, every table pre-rolled so the inner loop is array reads and multiplies ─────
    const D = 4, C = size / D;                       // 4px grains, 256 cells across — divides 1024, so it wraps
    const base = new Float32Array(C * C);            // how bright this grain is
    const face = new Float32Array(C * C);            // how hard its facet turns into the light
    for (let j = 0; j < C; j++) {
        for (let i = 0; i < C; i++) {
            base[j * C + i] = 0.76 + hash01(i + seed, j) * 0.56;
            face[j * C + i] = 0.35 + hash01(i, j + seed) * 0.95;
        }
    }
    const dith = new Float32Array(64 * 64);                       // 64 divides 1024, so this repeats seamlessly and
    for (let i = 0; i < 64 * 64; i++) dith[i] = (hash01(i, seed + 5) - 0.5) * 0.15;   // at 1px nobody sees the repeat
    // A straight 4px lattice reads as a MOSAIC of little squares, not as sand: the cell edges all line up. So
    // every row is shoved sideways and every column is shoved down by its own hashed amount, which turns the
    // shared cell boundaries into a stagger. Both shoves are constant along the axis they wrap on and the cell
    // index is masked to 256, so the lattice still repeats exactly at the tile edge.
    const jx = new Int32Array(size), jy = new Int32Array(size);
    for (let i = 0; i < size; i++) {
        jx[i] = Math.floor(hash01(i + seed, 91) * 12);
        jy[i] = Math.floor(hash01(i + seed, 92) * 12);
    }

    const img = ctx.createImageData(size, size);
    const dat = img.data;
    let p = 0;
    for (let y = 0; y < size; y++) {
        const drow = (y & 63) << 6, sx = jx[y];
        for (let x = 0; x < size; x++) {
            const wx = x + sx, wy = y + jy[x];
            const c = (((wy >> 2) & 255) * C) + ((wx >> 2) & 255);
            const s = base[c] + (-((wx & 3) * 0.3333 - 0.5) - ((wy & 3) * 0.3333 - 0.5)) * face[c] * 0.42
                    + dith[drow + (x & 63)];
            const v = s < 0.40 ? 0.40 : s > 1.30 ? 1.30 : s;
            dat[p]     = v * 208;
            dat[p + 1] = v * 204;
            dat[p + 2] = v * 191;                    // blue held back a touch: quartz, not concrete
            dat[p + 3] = 255;
            p += 4;
        }
    }
    ctx.putImageData(img, 0, 0);

    // Drift: broad, soft variation in packing density, so a wall of this does not read as uniform static.
    for (let m = 0; m < 16; m++) {
        const mx = hash01(m + seed, 1) * size, my = hash01(m + seed, 2) * size;
        const r = 190 + hash01(m + seed, 3) * 210, up = hash01(m + seed, 4) > 0.5;
        drawWrapped(size, mx, my, r, (x, y) => {
            const g = ctx.createRadialGradient(x, y, 0, x, y, r);
            g.addColorStop(0, up ? 'rgba(255,250,236,0.11)' : 'rgba(104,102,98,0.11)');
            g.addColorStop(1, up ? 'rgba(255,250,236,0)' : 'rgba(104,102,98,0)');
            ctx.fillStyle = g; ctx.beginPath(); ctx.arc(x, y, r, 0, Math.PI * 2); ctx.fill();
        });
    }

    // Coarse grains riding on the bed: angular, because a disc with a rim light reads as a bubble and the whole
    // point of quartz sand is that the grains are broken crystal with flat faces.
    for (let i = 0; i < 2200; i++) {
        const x = hash01(i + seed, 31) * size, y = hash01(i + seed, 32) * size;
        const r = 2.4 + hash01(i + seed, 33) * 2.6, h = hash01(i + seed, 34);
        drawWrapped(size, x, y, r + 3, (px, py) => {
            const facet = (fx, fy, rr, k) => {
                ctx.beginPath();
                for (let v = 0; v < 5; v++) {
                    const a = (v / 5 + hash01(i + seed, 35)) * Math.PI * 2;
                    const rv = rr * (0.70 + hash01(i * 5 + v + seed, 36 + k) * 0.56);
                    const vx = fx + Math.cos(a) * rv, vy = fy + Math.sin(a) * rv;
                    if (v === 0) ctx.moveTo(vx, vy); else ctx.lineTo(vx, vy);
                }
                ctx.closePath(); ctx.fill();
            };
            ctx.globalAlpha = 0.30;
            ctx.fillStyle = DARK;  facet(px + r * 0.55, py + r * 0.6, r * 1.06, 0);
            ctx.globalAlpha = 0.62;
            ctx.fillStyle = h > 0.42 ? LIGHT : FIELD; facet(px, py, r, 0);
        });
    }
    ctx.globalAlpha = 1;

    // Glints: a hard little square of near-white on the up-left shoulder of a grain. This is the whole reason
    // sand sparkles, and it is deliberately the LAST light-coloured thing laid down so nothing dulls it.
    for (let i = 0; i < 4200; i++) {
        const x = hash01(i + seed, 41) * size, y = hash01(i + seed, 42) * size, h = hash01(i + seed, 43);
        ctx.globalAlpha = 0.40 + h * 0.55;
        ctx.fillStyle = GLINT;
        ctx.fillRect(x, y, 1 + h * 1.6, 1 + h * 1.6);      // too small to straddle the seam visibly
    }
    ctx.globalAlpha = 1;

    // A scatter of larger granules with a real cast shadow, so the surface has a sense of scale.
    for (let i = 0; i < 42; i++) {
        const x = hash01(i + seed, 51) * size, y = hash01(i + seed, 52) * size;
        const r = 5 + hash01(i + seed, 53) * 4.5, h = hash01(i + seed, 54);
        drawWrapped(size, x, y, r + 8, (px, py) => {
            const sg = ctx.createRadialGradient(px + r * 0.55, py + r * 0.6, 0, px + r * 0.55, py + r * 0.6, r * 1.5);
            sg.addColorStop(0, 'rgba(88,87,85,0.42)'); sg.addColorStop(1, 'rgba(88,87,85,0)');
            ctx.fillStyle = sg; ctx.beginPath(); ctx.arc(px + r * 0.55, py + r * 0.6, r * 1.5, 0, Math.PI * 2); ctx.fill();
            const g = ctx.createRadialGradient(px - r * 0.4, py - r * 0.44, r * 0.06, px, py, r * 1.1);
            g.addColorStop(0, GLINT); g.addColorStop(0.4, LIGHT); g.addColorStop(1, h > 0.5 ? FIELD : DARK);
            ctx.fillStyle = g;
            ctx.beginPath();
            for (let v = 0; v < 7; v++) {
                const a = v / 7 * Math.PI * 2, rr = r * (0.74 + hash01(i * 9 + v + seed, 55) * 0.46);
                const vx = px + Math.cos(a) * rr, vy = py + Math.sin(a) * rr;
                if (v === 0) ctx.moveTo(vx, vy); else ctx.lineTo(vx, vy);
            }
            ctx.closePath(); ctx.fill();
        });
    }

    const texture = new THREE.CanvasTexture(canvas);
    texture.wrapS = texture.wrapT = THREE.RepeatWrapping;
    return texture;
}


// A CUBE OF GAS, read as the sight-glass of a pressure vessel. Reference: a Schlieren photograph of a moving gas, or
// a sealed jar of chlorine — a volume with no surface anywhere in it. Every other generator in this file is made of
// EDGES (cleaved facets, folded bands, brushed streaks, a conduit grid) because every other kube is a solid and has
// a surface. This one is the only texture in the game made of nothing but soft, edgeless, unevenly dense billows,
// and that contrast is what carries the recognition. Two things stop it reading as a smudge: a whisper of HEXAGONAL
// CONTAINMENT SCREEN — the perforated mesh over a discharge chamber's viewport, the cue that you are looking THROUGH
// something AT the gas — and a scatter of CONDENSATION BEADS on the inside of the glass.
//
// NEUTRAL, BECAUSE ALL ELEVEN GASES SHARE THIS ONE CACHE SLOT. applyMaterial does material.color.set(hex) AND
// material.map = tex, so the canvas is multiplied by the kube's hex on the GPU: built out of white and cool greys,
// the identical canvas comes out blue-lilac on Oxygen and yellow on Chlorine. Baking a hex in would square it for
// whichever gas rendered first and be simply wrong for the other ten. Mean luminance is 0.86, so a gas kube keeps
// essentially the brightness it has today — the texture modulates light and dark, it does not supply colour.
//
// AND THE SEED IS A CONSTANT, NOT hexSeed(baseHex). That is the half of sharing a slot that is easy to miss: the
// canvas is baked by whichever gas the player happens to meet first, so seeding it off THAT gas's hex would make the
// billows depend on the order of play — the exact cross-client divergence hash01 exists to rule out. hexSeed is
// still the helper doing the work, just fed a fixed string. baseHex is accepted only so the generator signature
// matches its siblings; it is deliberately unread.
export function createGasVesselTexture(baseHex) {
    const size = 1024;
    const canvas = document.createElement('canvas');
    canvas.width = canvas.height = size;
    const ctx = canvas.getContext('2d');
    const TAU = Math.PI * 2;

    const LIT = '255, 255, 255';
    const COOL = '68, 78, 96';                                   // a COOL shadow, so the dark side of a billow tints
    const seed = hexSeed('gas-vessel');                          // very slightly toward glass rather than toward dirt

    ctx.fillStyle = 'rgb(232, 232, 232)';                        // near-white ground: the hex is what colours this
    ctx.fillRect(0, 0, size, size);

    // One primitive, a soft ELLIPSE: a radial gradient drawn under a rotated non-uniform scale. At eccentricity 1 it
    // is a billow, and stretched it is a striation — which is how a gas gets directional structure without a single
    // hard edge anywhere. (A walked, stroked filament was tried first and is worse twice over: a round line cap at
    // the tile boundary is a visible seam, and any constant turn rate curls the walk into rings.)
    const puff = (cx, cy, r, ecc, ang, rgb, a) => {
        ctx.save();
        ctx.translate(cx, cy);
        ctx.rotate(ang);
        ctx.scale(ecc, 1 / ecc);
        const g = ctx.createRadialGradient(0, 0, 0, 0, 0, r);
        g.addColorStop(0, `rgba(${rgb}, ${a})`);
        g.addColorStop(0.42, `rgba(${rgb}, ${a * 0.5})`);
        g.addColorStop(1, `rgba(${rgb}, 0)`);
        ctx.fillStyle = g;
        ctx.fillRect(-r, -r, r * 2, r * 2);
        ctx.restore();
    };
    // Three scales of it, because the texture is read at three distances. On-world a kube face carries ONE whole
    // tile, so the MACRO band is all that survives on a distant face and in a 48px hotbar icon; the MESO band is the
    // long striation a moving gas is made of; the MICRO band is the fine turbulent curl that keeps a face from going
    // bare when you stand against it. Every one goes through drawWrapped, so all three tile.
    for (let i = 0; i < 1090; i++) {
        const t = hash01(i + seed, 1), e = hash01(i + seed, 7);
        const band = i < 44 ? 0 : (i < 190 ? 1 : 2);
        const r   = band === 0 ? 85 + t * t * 195 : band === 1 ? 24 + t * t * 72 : 8 + t * t * 26;
        const ecc = band === 0 ? 1 + e * 0.5 : band === 1 ? 1.8 + e * e * 2.8 : 1.8 + e * e * 2.7;
        const ang = hash01(i + seed, 8) * Math.PI;
        const x = hash01(i + seed, 2) * size, y = hash01(i + seed, 3) * size;
        const lit = hash01(i + seed, 4) > 0.46;
        const rgb = lit ? LIT : COOL;
        const base = band === 0 ? 0.06 : band === 1 ? 0.08 : 0.045;
        const a = base + hash01(i + seed, 5) * (band === 2 ? 0.065 : 0.16) * (lit ? 1 : 0.8);
        drawWrapped(size, x, y, r * ecc, (cx, cy) => puff(cx, cy, r, ecc, ang, rgb, a));
    }

    // THE CONTAINMENT SCREEN — the only hard-edged thing here, and the reason the billows read as gas held behind
    // something rather than as a stain on the block. A hex wire mesh, stroked twice about a pixel apart (lit up-left,
    // shadowed down-right, the same one light direction createCraftSurfaceTexture uses) so it reads as wire standing
    // off the glass. Kept to a WHISPER on purpose: at full strength it stops being a screen over a volume and becomes
    // a quilted honeycomb panel, which is a different material entirely.
    // It TILES because 1024 divides into a whole number of columns AND AN EVEN NUMBER OF ROWS — odd rows are offset
    // half a cell, so an odd row count would put an offset row against a non-offset row across the horizontal seam
    // and the lattice would step sideways there. (j & 1) is correct for j = -1 too: (-1 & 1) === 1.
    const cols = 14, rows = 16;
    const W = size / cols, H = size / rows;
    const rh = W * 0.5, rv = H * (2 / 3);                        // pointy-top hex: rows step 1.5 * rv, so rv = 2H/3
    const hexPath = (cx, cy) => {
        ctx.beginPath();
        ctx.moveTo(cx, cy - rv);
        ctx.lineTo(cx + rh, cy - rv * 0.5);
        ctx.lineTo(cx + rh, cy + rv * 0.5);
        ctx.lineTo(cx, cy + rv);
        ctx.lineTo(cx - rh, cy + rv * 0.5);
        ctx.lineTo(cx - rh, cy - rv * 0.5);
        ctx.closePath();
    };
    ctx.lineWidth = 1.4;
    ctx.lineJoin = 'round';
    for (let j = -1; j <= rows; j++) {                           // one ring beyond the tile: the overhang a neighbour
        const cy = j * H, dx = (j & 1) ? rh : 0;                 // tile would have contributed, drawn here instead
        for (let i = -1; i <= cols; i++) {
            const cx = i * W + dx;
            ctx.strokeStyle = `rgba(${LIT}, ${0.042 + hash01(i + seed, j) * 0.036})`;
            hexPath(cx - 1.1, cy - 1.1); ctx.stroke();
            ctx.strokeStyle = `rgba(${COOL}, ${0.034 + hash01(j, i + seed) * 0.028})`;
            hexPath(cx, cy); ctx.stroke();
        }
    }

    // Condensation on the inside of the glass: a bright bead with a soft contact shadow down-right of it. Small and
    // many rather than large and few — large ones start reading as upholstery buttons pinning the hex quilt.
    for (let i = 0; i < 320; i++) {
        const r = 1.1 + hash01(i + seed, 21) * 2.9;
        const x = hash01(i + seed, 22) * size, y = hash01(i + seed, 23) * size;
        const a = 0.22 + hash01(i + seed, 24) * 0.32;
        drawWrapped(size, x, y, r * 1.7, (cx, cy) => {
            ctx.fillStyle = `rgba(${COOL}, ${a * 0.32})`;
            ctx.beginPath(); ctx.arc(cx + r * 0.40, cy + r * 0.44, r, 0, TAU); ctx.fill();
            ctx.fillStyle = `rgba(${LIT}, ${a})`;
            ctx.beginPath(); ctx.arc(cx, cy, r, 0, TAU); ctx.fill();
        });
    }

    const texture = new THREE.CanvasTexture(canvas);
    texture.wrapS = texture.wrapT = THREE.RepeatWrapping;
    return texture;
}


// A CRYOGEN AT A ROLLING BOIL. Reference: liquid nitrogen or LOX in a clear dewar. The defining fact about all four
// of these — Liquid Oxygen (-183C), Liquid Nitrogen (-196C), Liquid Methane (-161C), Liquid Chlorine (-34C) — is
// that they are permanently AT their boiling point, so what you actually see is never a still surface: it is a
// churn of bubbles of every size rising through a cold, faintly foggy fluid. The bubbles are what carries the
// recognition, and they are the reason this is not createFluidFlowTexture: that generator draws long curling
// streamlines, which is a stirred or reacting liquid (Sulfuric Acid, Heavy Water, Liquid Argon — and Water, which
// joins it below). A cryogen does not flow, it FIZZES, and those two silhouettes are not interchangeable.
//
// A bubble is drawn the way a macro photograph shows one: a full DARK ring for the wall, a BRIGHT crescent on the
// far side where light entering the top is refracted round and concentrated, and a specular pip up-left. The full
// dark ring matters — an earlier version split the ring into a dark top arc and a pale bottom arc, and on the two
// near-white cryogens the pale arc vanished into the ground and every bubble read as an open crescent, like scales.
// With a full ring, at least one of the two always has contrast, whatever the kube's hex is.
//
// BAKED PER KUBE — each of the four gets its own cache slot, exactly as siliconcarbide/graphene do with one shared
// generator, so a texture is always baked from that kube's own hex and never wears whichever sibling rendered
// first. That accepts the double tint (baked hex, then multiplied by material.color) that the crafted generators
// already accept, and it is the right call here: the four hexes run from #E0FFFF to #D4AF37, so one shared canvas
// could not serve them, and the squaring is what turns Liquid Chlorine's #D4AF37 into the deep fuming amber the
// real thing is. It also means the four get four DIFFERENT bubble fields, since hexSeed(baseHex) differs.
export function createCryoBoilTexture(baseHex) {
    const size = 1024;
    const canvas = document.createElement('canvas');
    canvas.width = canvas.height = size;
    const ctx = canvas.getContext('2d');
    const TAU = Math.PI * 2;

    // Same construction order the shipped generators use: lighten/darken return "rgb(...)" and blendColors takes
    // (rgb-string, #hex) IN THAT ORDER — reversing them yields "rgb(NaN,...)", which canvas silently ignores.
    const sheenColor = '#FFFFFF';
    const chillColor = '#0D1B2A';
    const paleRim = blendColors(lightenColor(baseHex, 0.32), sheenColor, 0.55);
    const deepRim = blendColors(darkenColor(baseHex, 0.28), chillColor, 0.50);
    const vapour  = blendColors(lightenColor(baseHex, 0.20), sheenColor, 0.35);
    const rgba = (rgbStr, a) => rgbStr.replace('rgb(', 'rgba(').replace(')', ', ' + a + ')');   // gradient stops need alpha
    const seed = hexSeed(baseHex);

    ctx.fillStyle = baseHex;
    ctx.fillRect(0, 0, size, size);

    // Vapour pockets and cold shadow, under the bubbles: the boil is not evenly lit, and without this the field
    // reads as a flat colour with dots on it.
    const cloud = (cx, cy, r, col, a) => {
        const g = ctx.createRadialGradient(cx, cy, 0, cx, cy, r);
        g.addColorStop(0, rgba(col, a));
        g.addColorStop(0.5, rgba(col, a * 0.5));
        g.addColorStop(1, rgba(col, 0));
        ctx.fillStyle = g;
        ctx.fillRect(cx - r, cy - r, r * 2, r * 2);
    };
    for (let i = 0; i < 54; i++) {
        const t = hash01(i + seed, 41);
        const r = 60 + t * t * 210;
        const x = hash01(i + seed, 42) * size, y = hash01(i + seed, 43) * size;
        const cold = hash01(i + seed, 44) > 0.62;
        const col = cold ? deepRim : vapour;
        const a = cold ? 0.06 + hash01(i + seed, 45) * 0.10 : 0.08 + hash01(i + seed, 46) * 0.16;
        drawWrapped(size, x, y, r, (cx, cy) => cloud(cx, cy, r, col, a));
    }

    // The boil. r is cubic in the hash on purpose: a rolling boil is overwhelmingly fine fizz with a few large
    // bubbles through it, not an even spread of medium ones.
    ctx.lineCap = 'butt';
    for (let i = 0; i < 470; i++) {
        const t = hash01(i + seed, 11);
        const r = 3 + t * t * t * 46;
        const x = hash01(i + seed, 12) * size, y = hash01(i + seed, 13) * size;
        const a = 0.20 + hash01(i + seed, 14) * 0.5;
        const lw = Math.max(1, r * 0.17);
        drawWrapped(size, x, y, r + lw, (cx, cy) => {
            ctx.globalAlpha = a * 0.38;                          // gas inside reads paler than the liquid around it
            ctx.fillStyle = vapour;
            ctx.beginPath(); ctx.arc(cx, cy, r, 0, TAU); ctx.fill();
            ctx.lineWidth = lw;
            ctx.globalAlpha = a * 0.6;                           // the wall, all the way round
            ctx.strokeStyle = deepRim;
            ctx.beginPath(); ctx.arc(cx, cy, r, 0, TAU); ctx.stroke();
            ctx.globalAlpha = a;                                 // light refracted round to the far side. Canvas y is
            ctx.strokeStyle = paleRim;                           // DOWN, so PI/2 is the bottom — the side away from a
            const span = 0.55 + hash01(i + seed, 15) * 0.42;     // light up-left, which is where this crescent belongs
            ctx.beginPath(); ctx.arc(cx, cy, r, Math.PI * (0.5 - span / 2), Math.PI * (0.5 + span / 2)); ctx.stroke();
            if (r > 9) {                                         // direct specular, only where there is room for it
                ctx.globalAlpha = a * 0.85;
                ctx.fillStyle = paleRim;
                ctx.beginPath(); ctx.arc(cx - r * 0.36, cy - r * 0.36, Math.max(1, r * 0.15), 0, TAU); ctx.fill();
            }
        });
    }
    ctx.globalAlpha = 1;

    const texture = new THREE.CanvasTexture(canvas);
    texture.wrapS = texture.wrapT = THREE.RepeatWrapping;
    return texture;
}


// OPTIONAL BUT RECOMMENDED — this is the SHIPPED generator with one defect fixed; the only change is the four lines
// at the end of the step loop, marked below. It matters for this work because Water is about to wear it, and it
// fixes the same defect for the three liquids already on it.
//
// THE DEFECT: the old wrap wrote `if (jumped) ctx.moveTo(nx, ny); else ctx.lineTo(nx, ny);` — so the step that
// actually crosses the tile edge was never drawn at all, leaving a ~9px hole in every streamline at both seams,
// plus a round line cap on each side of it. Measured on the shipped version: |col1023-col0| = 11.05 and
// |row1023-row0| = 11.23 against an interior neighbour-pair median of 2.76/2.83 and an interior MAXIMUM of
// 6.25/6.83 — nearly double the worst interior transition, i.e. a real seam. With the fix: 5.14 / 5.16, back
// inside the interior range.
export function createFluidFlowTexture(baseHex) {
    const size = 1024;
    const canvas = document.createElement('canvas');
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext('2d');

    const sheenColor = '#FFFFFF';
    const depthColor = '#0B1626';
    const paleFlow = blendColors(lightenColor(baseHex, 0.35), sheenColor, 0.45);
    const deepFlow = blendColors(darkenColor(baseHex, 0.3), depthColor, 0.5);
    const seed = hexSeed(baseHex);

    ctx.fillStyle = baseHex;
    ctx.fillRect(0, 0, size, size);

    // Streamlines through a field of six vortices: a line is walked step by step and its heading is bent by every
    // vortex within range (harder the closer to the eye), which gives the long curling ribbons a liquid has. Vortex
    // placement/strength and every line's start, heading, width and shade are hash01 reads seeded by the colour.
    const vortices = [];
    for (let v = 0; v < 6; v++) {
        vortices.push({
            x: hash01(v + seed, 11) * size,
            y: hash01(v + seed, 12) * size,
            spin: (hash01(v + seed, 13) - 0.5) * 2.2,
            reach: 140 + hash01(v + seed, 14) * 220
        });
    }

    ctx.lineCap = 'round';
    for (let i = 0; i < 260; i++) {
        let x = hash01(i + seed, 21) * size;
        let y = hash01(i + seed, 22) * size;
        let heading = hash01(i + seed, 23) * Math.PI * 2;
        ctx.globalAlpha = 0.08 + hash01(i, seed + 4) * 0.22;
        ctx.strokeStyle = hash01(i, seed + 5) > 0.45 ? paleFlow : deepFlow;
        ctx.lineWidth = 2 + hash01(i, seed + 6) * 7;
        ctx.beginPath();
        ctx.moveTo(x, y);
        for (let step = 0; step < 70; step++) {
            for (const v of vortices) {
                const dx = x - v.x, dy = y - v.y;
                const d = Math.hypot(dx, dy) + 1;
                if (d > v.reach) continue;
                heading += v.spin * (1 - d / v.reach) * 0.35;
            }
            // THE FIX (four lines). The step is drawn UNWRAPPED first — running off the tile edge, where the canvas
            // clips it — and then, if it left the tile, drawn a SECOND time shifted a whole tile back, entering from
            // the far side. Both caps then fall outside the canvas and the ink is continuous across the seam. A step
            // is only 9px, so it can cross at most one boundary per axis and one shift per axis is exact.
            const ux = x + Math.cos(heading) * 9, uy = y + Math.sin(heading) * 9;
            const wx = ux < 0 ? size : (ux >= size ? -size : 0);
            const wy = uy < 0 ? size : (uy >= size ? -size : 0);
            ctx.lineTo(ux, uy);
            if (wx || wy) { ctx.moveTo(x + wx, y + wy); ctx.lineTo(ux + wx, uy + wy); }
            x = ux + wx; y = uy + wy;
        }
        ctx.stroke();
    }
    ctx.globalAlpha = 1;

    const texture = new THREE.CanvasTexture(canvas);
    texture.wrapS = texture.wrapT = THREE.RepeatWrapping;
    return texture;
}
